import { z } from 'zod';
import { CanvasTextListPrefixSchema } from './textListPrefix.js';
import { estUrlWebSure } from '../webUrl.js';
import { CanvasConversationMountIdentityShape } from './mountIdentity.js';
import { CANVAS_IMAGE_MIME_TYPES, CANVAS_IMAGE_SOURCE_MAX_BYTES } from './imageBytes.js';
import {
  CanvasFormatSettingOutcomeSchema,
  CanvasPageNumberFieldSchema,
  CanvasPageOrientationSchema,
  CanvasPaperSizeSchema,
  CANVAS_BORDER_WIDTH_PT_MAX,
  CANVAS_BORDER_WIDTH_PT_MIN,
  CANVAS_FORMAT_ANY,
  CANVAS_FORMAT_REMOVE,
  CANVAS_PARAGRAPH_SETTING_KEYS,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  CANVAS_UNDO_HISTORY_DEPTH,
  CanvasFormatReadSchema,
  CanvasFormatReadingRequestSchema,
  CanvasFormatReadingSchema,
  CanvasFormatSettingReceiptSchema,
  CanvasFormatWriteReceiptSchema,
  CanvasParagraphAlignmentSchema,
  CanvasCellBorderSchema,
  CanvasParagraphBorderSchema,
  CanvasShadingSchema,
} from './canvasFormat.js';
import { CanvasCopyFormatTargetSchema, CanvasPageZoneSchema, CanvasParagraphTargetSchema, CanvasTargetSchema, type CanvasTarget } from './canvasTarget.js';
import { CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX, CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX, CanvasReplaceEverywhereReceiptSchema, plierLaTypographie } from './canvasReplace.js';

export const CanvasActionNameSchema = z.enum([
  'rewrite_block',
  'replace_text',
  'apply_edit_plan',
  'insert_paragraphs',
  'delete_block_range',
  'apply_style',
  'write_markdown',
  'format_text',
  'format_paragraphs',
  'copy_format',
  'insert_heading',
  'insert_page_break',
  'add_hyperlink',
  'create_table',
  'convert_list',
  'add_list_items',
  'attach_numbering',
  'edit_table',
  'add_comments',
  'reply_to_comment',
  'resolve_comments',
  'move_range',
  'redo_changes',
  'accept_tracked_changes',
  'reject_tracked_changes',
  'undo_changes',
  'insert_footnote',
  'edit_footnote',
  'delete_footnote',
  'set_letterhead',
  'set_header_footer',
  'set_page_layout',
  'insert_image',
  'edit_image',
  'set_review_mode',
]);
export type CanvasActionName = z.infer<typeof CanvasActionNameSchema>;

/** Ce qu'un verbe retiré du catalogue laisse derrière lui. */
interface CanvasRetiredAction {
  /** Le libellé qu'il portait : la frise d'une conversation enregistrée nomme encore l'action passée. */
  readonly libelle: string;
  /** Pourquoi il n'existe plus, dit au modèle qui l'appelle encore. */
  readonly raison: string;
  /**
   * Quoi faire à sa place, dans l'ordre où le dire : changer le document à
   * l'écran d'abord (le cas par défaut), écrire un autre document ensuite.
   */
  readonly aLaPlace: { readonly enPlace: string; readonly documentNeuf: string };
}

/**
 * Verbes retirés du catalogue.
 *
 * Deux lecteurs en ont besoin après le retrait : la frise d'une conversation
 * enregistrée, qui doit continuer à nommer une action passée, et le refus d'un
 * appel périmé, qui doit dire que le verbe n'existe plus et quoi faire à sa
 * place. Un nom présent ici n'est jamais accepté comme action.
 *
 * Le conseil a UNE source, ici : le refus du backend (`SuperDocCanvasTools`),
 * celui du navigateur pendant un déploiement (`canvasAgentRequestGuard`) et le
 * refus d'un `write_markdown mode:"replace"` sur un document rempli
 * (`documentWriteExecutor`, même intention : écraser le document) le lisent.
 * Écrit trois fois, il avait déjà trois ordres et deux listes de verbes.
 *
 * `replace_document` — retiré le 2026-10-09 : il reconstruisait tout le corps
 * depuis du Markdown et rendait un reçu `ok` en détruisant la mise en forme
 * d'un Word d'avocat (styles, filets, fonds, images du corps).
 */
export const CANVAS_RETIRED_ACTIONS = {
  replace_document: {
    libelle: 'Remplacer le document',
    raison: 'it rebuilt the whole body from Markdown and destroyed the formatting of the lawyer\'s document.',
    aLaPlace: {
      enPlace: 'To change the document on screen, edit its text in place, its formatting stays: replace_text or '
        + 'apply_edit_plan for the passages that change, rewrite_block for a whole paragraph.',
      documentNeuf: 'To write another document, open a new page (open_canvas_document {source:"vierge"}), read it '
        + '(canvas_inspect({})), then ONE write_markdown: the previous document stays in the drafts of this conversation.',
    },
  },
} as const satisfies Record<string, CanvasRetiredAction>;
export type CanvasRetiredActionName = keyof typeof CANVAS_RETIRED_ACTIONS;

/** Le refus d'un appel à un verbe retiré : pourquoi il n'existe plus, puis ses deux issues. */
export function conseilDuVerbeCanvasRetire(verbe: CanvasRetiredActionName): string {
  const { raison, aLaPlace } = CANVAS_RETIRED_ACTIONS[verbe];
  return `${verbe} no longer exists — ${raison} ${aLaPlace.enPlace} ${aLaPlace.documentNeuf}`;
}

/** `true` quand ce nom est celui d'un verbe retiré du catalogue. */
export function estUnVerbeCanvasRetire(nom: unknown): nom is CanvasRetiredActionName {
  return typeof nom === 'string' && Object.prototype.hasOwnProperty.call(CANVAS_RETIRED_ACTIONS, nom);
}

/**
 * Liste de verbes ANNONCÉE par un navigateur (capacités du montage, reçu
 * d'inspection).
 *
 * Un onglet ouvert avant le retrait d'un verbe l'annonce encore. Le nom retiré
 * est écarté de la liste au lieu de faire refuser l'annonce entière : sans
 * cela le Canvas de cet onglet resterait sans montage qualifié, donc sans
 * aucune écriture, jusqu'à son rechargement. Un nom qui n'a jamais été un
 * verbe reste refusé.
 */
export const CanvasAnnouncedActionNamesSchema = z.preprocess(
  (valeur) => (Array.isArray(valeur) ? valeur.filter((nom) => !estUnVerbeCanvasRetire(nom)) : valeur),
  z.array(CanvasActionNameSchema),
);

/**
 * Qualification de la surface d'écriture réellement montée dans le Canvas.
 *
 * Une liste de verbes vide et une lecture de capacités impossible ne portent
 * pas le même fait : la première décrit un runtime connu qui n'expose aucune
 * mutation utilisable ; la seconde interdit d'en annoncer une, sans rien dire
 * de ce que le document saurait peut-être faire. Les deux doivent donc rester
 * des états discriminés sur le fil, jamais un `undefined` ambigu.
 */
export const CanvasCapabilityUnqualifiedReasonSchema = z.enum([
  'capabilities_unreadable',
  'capabilities_timeout',
  'qualification_missing',
]);
export type CanvasCapabilityUnqualifiedReason = z.infer<typeof CanvasCapabilityUnqualifiedReasonSchema>;

export const CanvasCapabilityQualificationSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('qualified'),
    executableActions: CanvasAnnouncedActionNamesSchema,
  }).strict(),
  z.object({
    status: z.literal('unqualified'),
    reason: CanvasCapabilityUnqualifiedReasonSchema,
  }).strict(),
]);
export type CanvasCapabilityQualification = z.infer<typeof CanvasCapabilityQualificationSchema>;

/** Readiness of an application resource required by one Canvas action. */
export const CanvasActionResourceReadinessSchema = z.object({
  action: CanvasActionNameSchema,
  resource: z.literal('named_letterhead_resolver'),
  status: z.enum(['available', 'unavailable']),
}).strict();
export type CanvasActionResourceReadiness = z.infer<typeof CanvasActionResourceReadinessSchema>;

/**
 * Versioned proof that the current browser mount has completed editor setup.
 * SuperDoc's supported operations and Hector's action-specific resources are
 * kept separate so one missing resource cannot disable inspection or unrelated
 * edits. The epoch binds the proof to exactly one mount.
 */
export const CanvasMountReadinessSchema = z.object({
  version: z.literal(1),
  mountEpoch: z.number().int().nonnegative(),
  editor: z.literal('ready'),
  capabilities: CanvasCapabilityQualificationSchema,
  actionResources: z.array(CanvasActionResourceReadinessSchema).max(32),
}).strict().superRefine((readiness, ctx) => {
  const seen = new Set<string>();
  for (const resource of readiness.actionResources) {
    const key = `${resource.action}:${resource.resource}`;
    if (seen.has(key)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['actionResources'], message: `duplicate action resource ${key}` });
    }
    seen.add(key);
  }
  if (
    readiness.capabilities.status === 'qualified'
    && readiness.capabilities.executableActions.includes('set_letterhead')
    && !seen.has('set_letterhead:named_letterhead_resolver')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['actionResources'],
      message: 'set_letterhead requires an explicit named_letterhead_resolver readiness entry',
    });
  }
});
export type CanvasMountReadiness = z.infer<typeof CanvasMountReadinessSchema>;

export const CanvasAgentActionBlockReasonSchema = z.enum([
  'no_canvas_session',
  'transport_unavailable',
  'mount_readiness_missing',
  'runtime_unqualified',
  'unsupported',
  'action_resource_unavailable',
  'inspection_required',
  'mutation_outcome_unknown',
]);
export type CanvasAgentActionBlockReason = z.infer<typeof CanvasAgentActionBlockReasonSchema>;

/** Backend-owned, factual state attached to every Canvas tool result. */
export const CanvasAgentSessionStateSchema = z.object({
  version: z.literal(1),
  mount: z.object({
    status: z.enum(['announced', 'not_announced']),
    mountEpoch: z.number().int().nonnegative().nullable(),
    revision: z.string().min(1).nullable(),
  }).strict(),
  document: z.object({ attached: z.boolean() }).strict().nullable(),
  readiness: z.object({
    status: z.enum(['qualified', 'unqualified', 'missing']),
    actionResources: z.array(CanvasActionResourceReadinessSchema),
  }).strict(),
  // `connection_interrupted` (#1829) : socket perdue sans écriture en vol. Lu
  // seulement par le backend et le modèle ; aucun front ne parse ce schéma.
  transport: z.enum(['no_session', 'ready_to_try', 'recovery_inspect_required', 'connection_interrupted', 'unresponsive']),
  tools: z.object({
    canvas_inspect: z.enum(['usable', 'blocked']),
    canvas_perform_action: z.enum(['usable', 'blocked']),
  }).strict(),
  actions: z.object({
    supported: z.array(CanvasActionNameSchema),
    allowed: z.array(CanvasActionNameSchema),
    blocked: z.array(z.object({
      action: CanvasActionNameSchema,
      reason: CanvasAgentActionBlockReasonSchema,
    }).strict()),
  }).strict(),
  targetScope: z.object({
    status: z.enum(['none', 'available']),
    revision: z.string().min(1).nullable(),
    publicTargetCount: z.number().int().nonnegative(),
  }).strict(),
}).strict();
export type CanvasAgentSessionState = z.infer<typeof CanvasAgentSessionStateSchema>;

/**
 * This only rejects terms that the shared search normalizer reduces to empty.
 *
 * `@hector/shared-types` is intentionally lower-level than
 * `@hector/agent-text-normalizer`; importing the latter would invert the
 * workspace dependency direction. Keep this negative check aligned with the
 * normalizer's whitespace and zero-width removal, without duplicating its
 * matching behavior here.
 */
// eslint-disable-next-line no-misleading-character-class -- each code point is matched alone; U+200D is listed as a character, not used as a joiner
const EMPTY_AFTER_SEARCH_NORMALIZATION = /^[\s\u200B\u200C\u200D\uFEFF]*$/u;
const CanvasTextSearchTermSchema = z
  .string()
  .min(1)
  .refine((value) => !EMPTY_AFTER_SEARCH_NORMALIZATION.test(value), 'textSearch terms must contain a visible character after French typography normalization');

const CanvasSelectorSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('nodeId').describe('Cible par identifiant stable de bloc.'),
      nodeId: z.string().min(1).describe('Identifiant nodeId publié par un canvas_inspect frais.'),
    })
    .strict()
    .describe('Sélecteur nodeId : cible exactement le bloc portant cet identifiant.'),
  z
    .object({
      kind: z.literal('ordinal').describe('Cible par rang absolu 1-based.'),
      ordinalKind: z.enum(['block', 'paragraph', 'heading']).describe('Nature du rang absolu : bloc, paragraphe ou titre.'),
      value: z.number().int().positive().describe('Obsolète : l’éditeur ne publie plus de rang, tout ordinal est refusé.'),
    })
    .strict()
      .describe('Obsolète : l’éditeur ne publie plus de rang, tout ordinal est refusé ; cible par nodeId ou textSearch.'),
  z
    .object({
      kind: z.literal('textSearch').describe('Cible par recherche de termes normalisée.'),
      terms: z.array(CanvasTextSearchTermSchema).min(1).describe('Termes à chercher dans un même bloc ; match décide si un ou tous doivent être présents. Un terme vide après normalisation typographique française est refusé.'),
      match: z.enum(['all', 'any']).default('all').describe('all exige tous les termes dans le même bloc ; any exige au moins un terme. Ce champ ne veut jamais dire toutes les occurrences.'),
      occurrence: z.number().int().positive().optional().describe('Occurrence 1-based dans l’ordre du document : une action textSearch l’utilise pour choisir un candidat ; canvas_inspect rend toujours tous les candidats et leur compte.'),
    })
    .strict()
    .describe('Sélecteur textSearch : recherche sur le texte complet normalisé des blocs.'),
]).describe('Cible facultative : nodeId ou textSearch.');
export type CanvasSelector = z.infer<typeof CanvasSelectorSchema>;

/**
 * Ordinaux absolus ciblés par l'action, où qu'ils se trouvent dans son payload.
 *
 * WHY cette question se pose : des trois sélecteurs, l'ordinal est le SEUL qui
 * se périme quand le document bouge. `nodeId` survit aux mutations et
 * `textSearch` ne dépend d'aucun rang, mais un ordinal est recalculé à chaque
 * lecture (`offset + index + 1` dans `readCanvasSnapshot`) : toute insertion ou
 * suppression décale tous les rangs en aval. Un ordinal issu d'une inspection
 * ANTÉRIEURE à une mutation ne désigne donc plus le bloc visé — et rien, dans
 * le document, ne signale l'erreur : l'écriture réussit, au mauvais endroit.
 *
 * WHY un parcours en profondeur plutôt qu'une liste de champs : un sélecteur
 * vit sous `selector`, `scope`, `from`/`to`, `placement.selector` ou
 * `styleSource.selector` selon le verbe, et le catalogue en gagne. Une liste
 * de champs se périmerait en silence au prochain verbe ajouté — or ici le
 * silence vaut une mutation appliquée au mauvais bloc. Le parcours reste exact
 * quand le catalogue bouge, sans que personne ait à y penser.
 */
export function ordinauxCiblesParRangAbsolu(action: unknown): readonly number[] {
  const ordinaux = new Set<number>();

  const parcourir = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(parcourir);
      return;
    }
    if (typeof value !== 'object' || value === null) return;
    const champs = value as Record<string, unknown>;
    if (
      champs.kind === 'ordinal'
      && typeof champs.value === 'number'
      && Number.isInteger(champs.value)
      && champs.value > 0
    ) {
      ordinaux.add(champs.value);
      return;
    }
    Object.values(champs).forEach(parcourir);
  };

  parcourir(action);
  return [...ordinaux];
}

/** Vrai si l'action cible au moins un rang absolu. */
export function actionCibleParRangAbsolu(action: unknown): boolean {
  return ordinauxCiblesParRangAbsolu(action).length > 0;
}

const CanvasPlacementSchema = z.discriminatedUnion('at', [
  z.object({ at: z.literal('document_start') }).strict(),
  z.object({ at: z.literal('document_end') }).strict(),
  z.object({ at: z.literal('before'), selector: CanvasSelectorSchema }).strict(),
  z.object({ at: z.literal('after'), selector: CanvasSelectorSchema }).strict(),
]);

const CanvasStyleSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('like'), selector: CanvasSelectorSchema }).strict(),
  z.object({ kind: z.literal('styleId'), styleId: z.string().min(1) }).strict(),
]);

/**
 * Paragraphes réécrits par appel de `rewrite_block`, au plus.
 *
 * WHY 50, et pas les 200 étapes qu'un plan de l'éditeur accepte : la liste est
 * écrite par le MODÈLE, dans la sortie de son tour, et cette sortie est bornée.
 * Coupée (`finishReason: length`), l'appel entier est perdu, après que le modèle
 * a tout rédigé. La plus petite borne de sortie du catalogue des modèles est
 * 16 384 tokens (`MODEL_CATALOG`, `apps/backend/lib/llm/catalog.ts`). Mesuré au
 * compteur du backend (`countTokens`, 2026-10-09) : un paragraphe d'acte coûte
 * 12 tokens d'enveloppe et 0,30 token par caractère, soit pour une liste pleine
 * de paragraphes de 400 caractères 6 557 tokens à 50 (40 % de la borne), 7 868
 * à 60, 26 296 à 200 ; à 600 caractères, 9 560 à 50 (58 %). 50 laisse au moins
 * la moitié de la borne au raisonnement du modèle pour des paragraphes de quatre
 * à cinq lignes ; la description du champ borne aussi le texte total, pour des
 * paragraphes plus longs. Garde : `CanvasToolDefinitions.test.ts` (backend).
 *
 * ⚠️ Le compteur est celui du backend, pas le tokeniseur de chaque fournisseur :
 * la marge couvre cet écart, elle ne le mesure pas.
 */
export const CANVAS_REWRITE_BLOCKS_MAX = 50;

/** Paragraphes par appel d'`insert_paragraphs` ; l'exécuteur ne dépasse jamais ce nombre en aérant un lot. */
export const CANVAS_INSERT_PARAGRAPHS_MAX = 80;

/**
 * Le code de l'erreur qui rend `partial` un reçu de `rewrite_block` dont tous les textes sont écrits et relus, mais
 * dont des paragraphes portent encore un passage de leur ancien texte sans la forme de caractère que ce passage
 * portait (`rewrite.formattingNotCarried`). Une forme partie avec un passage que le texte neuf ne contient plus ne
 * pousse pas cette erreur (`rewrite.formattingDropped`).
 *
 * WHY partagé : le navigateur l'émet (`recuDeReecriture`) et le courtier backend le lit (`observeActionReceipt`).
 * Quand ce code est la SEULE erreur du reçu, le courtier garde la portée de lecture, pour que `format_text` repose
 * la forme sans `canvas_inspect` ; le message de l'erreur le promet à l'agent. Deux chaînes recopiées feraient mentir
 * ce message au premier renommage.
 */
export const CANVAS_REWRITE_FORMATTING_NOT_CARRIED = 'REWRITE_FORMATTING_NOT_CARRIED';

/**
 * Code d'erreur du reçu `partial` de `rewrite_block` quand des paragraphes de la liste n'ont pas été réécrits
 * (`rewrite.skipped`) et que les autres le sont.
 *
 * WHY partagé : même contrat que `CANVAS_REWRITE_FORMATTING_NOT_CARRIED`. Le courtier garde la portée de lecture
 * quand le reçu ne porte que ces deux codes, et le message de l'erreur promet à l'agent une suite sans `canvas_inspect`.
 */
export const CANVAS_REWRITE_BLOCKS_SKIPPED = 'REWRITE_BLOCKS_SKIPPED';

/** Texte total, en caractères, qu'un appel de `rewrite_block` devrait porter au plus : 6 600 tokens au compteur du backend, 40 % de la plus petite borne de sortie. Dit au modèle, non imposé : un appel complet plus long reste valide. */
export const CANVAS_REWRITE_TEXT_ADVISED_MAX = 20_000;

/**
 * Réécrire le texte de paragraphes EN PLACE : chacun garde son identifiant et
 * sa forme de paragraphe (retraits, alignement, espacements, bordure, style).
 *
 * Une seule forme, la liste : un paragraphe seul est une liste d'un élément.
 * La forme d'avant (`selector` + `text`) coûtait un appel par paragraphe — 34
 * appels et 13 minutes sur une lettre de 57 paragraphes (mesure du 2026-10-09).
 * La cible est un identifiant, jamais une recherche de texte : un texte cherché
 * peut désigner un autre bloc que celui que l'agent a lu.
 */
const RewriteBlockInputSchema = z
  .object({
    action: z.literal('rewrite_block'),
    blocks: z
      .array(
        z
          .object({
            nodeId: z.string().min(1),
            text: z
              .string()
              .refine((value) => value.trim().length > 0, 'text must not be blank')
              // Une description d'outil est du prompt : c'est ICI que le modèle lit ce que le texte peut porter, au
              // moment d'écrire le champ. La règle de forme a sa mesure au §17 de `superdoc-document-api.md`.
              .describe(
                'The paragraph\'s new text, plain, with three optional marks: **bold**, *italic*, __underline__. A mark opens at the start of a word and '
                + 'closes at the end of one, inside this text; marks nest. They are removed from the text and set as formatting; write a literal * or _ '
                + 'that could be read as a mark as \\* or \\_. A tab is \\t; a line break is refused. '
                // Mesuré dans la vraie application (2026-10-11) : la lecture rend le gras en **…** ou <strong>, l'italique
                // en *…* ou <em>, le souligné en <u>…</u> ; sans cette phrase l'agent réécrivait un paragraphe pour un autre
                // dossier sans aucune marque, et le souligné des dates de l'avocat disparaissait.
                + 'Emphasis you read is the lawyer\'s: put it on the equivalent element of the new text, drop it when there is none '
                + '(<strong> → **, <em> → *, <u> → __: read <u>20 décembre 2021</u>, write __3 mars 2024__). '
                // Conversation de production du 2026-10-11 : l'agent récrivait « 1. … » dans des éléments de liste numérotés par
                // Word, l'acte affichait « 1. 1. ». La lecture rend ce numéro en repère (`canvasReadMarkers.ts`, backend).
                + 'A list item keeps the number or bullet Word gives it (read as ⟦numérotation Word : 1.⟧): the text starts after it. '
                + 'When the read exposes textListPrefix, keep its exact prefix (literal marker and whitespace) at the start of the new text unless asked to change that presentation. '
                + 'Unmarked text keeps the character formatting most of the paragraph had; a text identical to the paragraph leaves it untouched. '
                // Le reçu et cette phrase disent la même règle (`recuDeReecriture`, navigateur) : seul un passage GARDÉ sans
                // sa forme est à reposer. Conversation de production : un montant en gras retiré du paragraphe rendait le
                // reçu partial, et l'agent enchaînait des format_text sur un texte qui n'existait plus.
                + 'A passage you keep from the old text without the bold, italic, underline, size, colour, highlight or font it alone carried: '
                + 'the receipt is partial and names it (rewrite.formattingNotCarried), set it again with format_text. '
                + 'A passage you leave out takes its formatting with it; what you write in its place does not have it unless you mark or format it. '
                + 'NOT rewritten — a paragraph that carries a note call, a field, a link, a bookmark, a manual line break or a pending tracked change '
                // Mesuré le 2026-10-13 (tests/ws-mocked/mesure-recus-vrais.spec.ts) : l'éditeur fond une seconde
                // réécriture dans la révision de la première, et la rejeter rend le texte d'origine. La condition dite
                // est la garde de l'exécuteur (`remplacementEntierEnAttente`, navigateur), qui est STRUCTURELLE : rien
                // dans une révision ne dit qui l'a posée (l'éditeur signe celles de l'agent du nom de l'avocat). « ta
                // propre réécriture » promettait une provenance que rien ne vérifie (revue de la PR #2257).
                + '(with tracking on, a paragraph whose only pending change replaces its whole text — whoever proposed it — IS rewritten again, inside that change): '
                + 'the receipt names it (rewrite.skipped); change its passages instead (replace_text, apply_edit_plan), which keeps them.',
              ),
          })
          .strict(),
      )
      .min(1)
      .max(CANVAS_REWRITE_BLOCKS_MAX)
      // Le doublon est NOMMÉ : sur une longue liste, « un identifiant est en double » ne dit pas quelle entrée corriger.
      .superRefine((blocks, ctx) => {
        // Mesuré (§17 de `superdoc-document-api.md`) : l'éditeur accepte l'aperçu d'un texte à saut de ligne et
        // jette à l'application. Un paragraphe est une entrée de la liste. Une tabulation (`\t`), elle, est
        // acceptée et écrite comme une tabulation (§17.6). Le refus compte les paragraphes reçus et nomme l'entrée :
        // l'agent y envoie plusieurs paragraphes d'un coup, et « un saut de ligne est refusé » ne lui disait pas où
        // mettre les suivants.
        blocks.forEach(({ nodeId, text }, rang) => {
          if (!/[\n\r]/.test(text)) return;
          const paragraphes = text.split(/[\n\r]+/).filter((ligne) => ligne.trim().length > 0).length;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rang, 'text'],
            message: `rewrite_block blocks[${rang}].text (nodeId ${nodeId}) carries `
              + (paragraphes > 1
                ? `${paragraphes} paragraphs, and an entry is ONE paragraph: send one entry per existing paragraph, each under its own nodeId, `
                  + `then insert_paragraphs after nodeId ${nodeId} for the ${paragraphes - 1} extra paragraph(s) that have no nodeId yet. `
                : 'a line break, and an entry is ONE paragraph: remove it (one entry per existing paragraph). ')
              + 'A paragraph that has manual line breaks is not rewritten whole: change its passages (replace_text), which keeps them',
          });
        });
        const comptes = new Map<string, number>();
        for (const { nodeId } of blocks) comptes.set(nodeId, (comptes.get(nodeId) ?? 0) + 1);
        for (const [nodeId, fois] of comptes) {
          if (fois > 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `nodeId ${nodeId} appears ${fois} times: one entry per paragraph — merge them into the one text this paragraph must carry` });
        }
      })
      .describe(
        `At most ${CANVAS_REWRITE_BLOCKS_MAX} paragraphs and about ${CANVAS_REWRITE_TEXT_ADVISED_MAX} characters of text per call — your output is bounded, a longer call is cut and lost. `
        + 'Split a longer document into several calls, in document order.',
      ),
  })
  .strict();

/**
 * `replace_text` a deux modes, que le seul drapeau `everywhere` distingue.
 *
 * - Sans lui : UN passage isolé dans un bloc. Plusieurs `edits` y restent
 *   refusés (#1610) — un lot de remplacements indépendants n'est pas atomique ;
 *   les retouches liées dans des blocs choisis passent par `apply_edit_plan`.
 * - Avec `everywhere:true` : une liste de couples ancien / nouveau, chacun
 *   remplacé à CHAQUE occurrence en mot entier du corps, par UN plan atomique
 *   du navigateur (une occurrence en cellule de tableau est comptée mais fait
 *   refuser l'appel tant qu'elle n'est pas mesurée). `expectedCount` y est le garde-fou :
 *   l'appel n'écrit que si chaque texte se trouve exactement ce nombre de
 *   fois, sinon rien n'est écrit et le reçu situe chaque texte.
 *
 * Les règles qui lient ces champs (un seul edit sans le drapeau, `expectedCount`
 * exigé avec lui…) vivent dans `appliquerLesReglesInterChamps` : une branche
 * d'union discriminée doit rester un `ZodObject` nu. Le schéma livré au modèle
 * annonce donc `maxItems` {@link CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX} pour les
 * deux modes ; le refus inter-champs rattrape le mode à un passage avant l'envoi.
 */
const ReplaceTextInputSchema = z
  .object({
    action: z.literal('replace_text'),
    edits: z
      .array(
        z
          .object({
            // La lecture du canvas est rendue en Markdown : en production (2026-10-13) l'agent recopiait
            // « ### **Titre** » ici, et le texte n'était pas trouvé. Même formulation que `apply_edit_plan`.
            find: z.string().min(1).describe('Plain text as the document shows it (no #, ** or * from the read).'),
            replace: z.string(),
            expectedCount: z
              .number()
              .int()
              .min(1)
              .max(CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX)
              .optional()
              .describe('With everywhere: how many times find occurs in the document.'),
          })
          .strict(),
      )
      .min(1)
      .max(CANVAS_REPLACE_EVERYWHERE_PAIRS_MAX),
    scope: CanvasSelectorSchema.optional(),
    caseSensitive: z.boolean().optional(),
    everywhere: z
      .boolean()
      .optional()
      .describe('Every whole-word occurrence of each find in the document body, in one atomic write; each edit needs expectedCount. Always case-sensitive.'),
  })
  .strict();

const InsertParagraphsInputSchema = z
  .object({
    action: z.literal('insert_paragraphs'),
    texts: z
      .array(z.string().refine(
        // "" = vrai paragraphe vide : beaucoup d'actes espacent leurs paragraphes ainsi, et
        // l'interdire faisait supprimer ces séparateurs à l'agent (« tout est collé », 2026-09-30).
        // Les espaces seuls restent refusés : ce n'est ni un texte ni un séparateur voulu.
        (value) => value === '' || value.trim().length > 0,
        'paragraph text must be "" (an empty spacing paragraph) or contain visible characters; whitespace-only text is refused',
      ))
      .min(1)
      .max(CANVAS_INSERT_PARAGRAPHS_MAX),
    placement: CanvasPlacementSchema,
    styleSource: CanvasStyleSourceSchema.optional(),
  })
  .strict();

const DeleteBlockRangeInputSchema = z
  .object({
    action: z.literal('delete_block_range'),
    from: CanvasSelectorSchema,
    to: CanvasSelectorSchema.optional(),
  })
  .strict();

/**
 * Poser un style nommé sur UN bloc, par `styles.paragraph.setStyle`, qui efface
 * la forme de texte posée à la main dans ce bloc (`mesure-copy-format`, S1).
 * Poser un style sur plusieurs paragraphes en gardant cette forme est le
 * réglage `styleId` de `format_paragraphs` ; donner à des paragraphes la forme
 * d'un autre paragraphe, `copy_format` (#1869).
 */
const ApplyStyleInputSchema = z
  .object({
    action: z.literal('apply_style'),
    selector: CanvasSelectorSchema,
    styleId: z.string().min(1),
  })
  .strict();

/**
 * Poser un document ENTIER, écrit en markdown, en un seul appel (#993).
 *
 * ## Pourquoi `mode` vaut `replace` par défaut
 *
 * `replace` n'efface rien : le navigateur ne l'exécute que sur un document
 * prouvé vide et refuse tout le reste en nommant l'issue
 * (`documentWriteExecutor.ts`, `WRITE_MARKDOWN_REPLACE_NONEMPTY`). `append`
 * par défaut, lui, pourrait poser un doublon. Un appel sans `mode` était
 * refusé par « mode: Required », sans les valeurs admises.
 *
 * ⚠️ `markdown` n'est PAS dans `CANVAS_STRUCTURAL_KEYS` et ne doit jamais y
 * entrer : la réparation des tool-calls sérialisés parse toute valeur de clé
 * structurelle, et un acte qui commencerait par `{` deviendrait un objet.
 */
const WriteMarkdownInputSchema = z
  .object({
    action: z.literal('write_markdown'),
    markdown: z.string().refine((value) => value.trim().length > 0, 'markdown must not be blank'),
    mode: z.enum(['replace', 'append']).default('replace'),
  })
  .strict();

/**
 * Le vocabulaire de surlignage est FERMÉ, et c'est une contrainte de OOXML, pas
 * un confort de typage (#992).
 *
 * `w:highlight` n'accepte qu'une énumération de noms. Mesuré le 2026-08-16 sur
 * `superdoc@2.6.0` : `yellow`, `green` et `magenta` sont acceptés et **peints**
 * (`rgb(255,255,0)`, `rgb(0,255,0)`, `rgb(255,0,255)`), tandis qu'un nom hors
 * énumération — `chartreuse` — rend `NO_OP: format.apply produced no change` et
 * **rien n'est peint**.
 *
 * ⚠️ C'est ce `NO_OP`-là qui rend l'enum load-bearing : le même code est celui
 * que l'éditeur rend quand l'état demandé est déjà celui du document. Sans
 * vocabulaire fermé au contrat, une couleur inventée par le modèle serait
 * indiscernable d'un surlignage déjà posé.
 * Le rose de la palette produit s'écrit `magenta` : c'est le seul rose de
 * l'énumération.
 */
const CanvasHighlightSchema = z.enum(['yellow', 'green', 'magenta']);

/**
 * Taille de police maximale, en points. Valeur de jugement, non mesurée : une
 * borne contre une valeur aberrante du modèle (une taille donnée en
 * demi-points ou en pixels), très au-dessus de tout texte d'acte.
 */
const CANVAS_FONT_SIZE_PT_MAX = 400;
/** Longueur maximale d'un nom de police. Valeur de jugement, non mesurée : une borne contre un texte passé pour un nom. */
const CANVAS_FONT_FAMILY_MAX_LENGTH = 80;

/**
 * Le filtre `onlyWhere` de `format_text` (#1865) : n'agir que sur le texte qui
 * a DÉJÀ cette forme, d'où qu'elle vienne (posée à la main ou portée par un
 * style nommé). Mêmes clés que les réglages ; plusieurs clés = toutes vraies.
 *
 * Pour le surlignage et la couleur, {@link CANVAS_FORMAT_ANY} dit « en a un,
 * quel qu'il soit » et {@link CANVAS_FORMAT_REMOVE} « n'en a pas » : « mets en
 * rouge ce qui est surligné » ne nomme pas la couleur du surlignage. Le noir
 * (`#000000`) filtre comme `none` : c'est la couleur d'un texte sans couleur,
 * la même définition que pour un retrait. Une police et une taille ne peuvent
 * pas être absentes : elles se filtrent par leur valeur seulement. Comment
 * chaque valeur se confronte au texte lu est dit dans `reglagesDeTexte.ts`
 * (navigateur).
 */
export const CanvasTextFormatFilterSchema = z
  .object({
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    highlight: z.enum([...CanvasHighlightSchema.options, CANVAS_FORMAT_REMOVE, CANVAS_FORMAT_ANY]).optional(),
    color: z
      .string()
      .regex(
        new RegExp(`^(#[0-9A-Fa-f]{6}|${CANVAS_FORMAT_REMOVE}|${CANVAS_FORMAT_ANY})$`),
        `onlyWhere.color must be a #RRGGBB hex value, "${CANVAS_FORMAT_ANY}" (any colour) or "${CANVAS_FORMAT_REMOVE}" (no colour)`,
      )
      .optional(),
    fontFamily: z
      .string()
      .trim()
      .min(1)
      .max(CANVAS_FONT_FAMILY_MAX_LENGTH)
      .refine((police) => ![CANVAS_FORMAT_REMOVE, CANVAS_FORMAT_ANY].includes(police.toLowerCase()), {
        message: `onlyWhere.fontFamily must name a font: every text has one, so "${CANVAS_FORMAT_ANY}" and "${CANVAS_FORMAT_REMOVE}" filter nothing`,
      })
      .optional(),
    fontSizePt: z.number().min(1).max(CANVAS_FONT_SIZE_PT_MAX).multipleOf(0.5).optional(),
  })
  .strict()
  .refine((filtre) => CANVAS_TEXT_FORMAT_SETTING_KEYS.some((reglage) => filtre[reglage] !== undefined), {
    message: `onlyWhere requires at least one setting among ${CANVAS_TEXT_FORMAT_SETTING_KEYS.join(', ')}`,
  });
export type CanvasTextFormatFilter = z.infer<typeof CanvasTextFormatFilterSchema>;

/**
 * Mettre en forme du texte existant (#992, cible unique depuis #1863, police,
 * taille, couleur et « retirer » depuis #1864).
 *
 * La cible est la forme unique des outils de forme (`canvasTarget.ts`) : un
 * passage dans un paragraphe, une liste, une plage, tout l'acte. Les réglages
 * sont à plat, à côté de la cible : plusieurs tiennent en un appel.
 *
 * Chaque réglage se retire : un booléen par `false`, les autres par le mot
 * {@link CANVAS_FORMAT_REMOVE}. Unités d'avocat : la taille est en points
 * (demi-points admis), la couleur en `#RRGGBB`. Ce que chaque valeur devient
 * pour l'éditeur est dit dans `reglagesDeTexte.ts` (navigateur).
 *
 * Le mot qui retire est enseigné UNE fois, dans la description du verbe : les
 * champs n'en portent pas de seconde (`.describe`), et l'unité de la taille
 * est dans le nom du champ. Le navigateur reconnaît ce mot quelle que soit sa
 * casse dans `fontFamily`, seul champ où toute chaîne est admise.
 *
 * `onlyWhere` (#1865) restreint l'écriture au texte qui a déjà une forme : voir
 * {@link CanvasTextFormatFilterSchema}. Ce que chaque cible désigne est dit par
 * la description de CE verbe, pour tous les outils de forme (`canvasTarget.ts`).
 */
export const FormatTextInputSchema = z
  .object({
    action: z.literal('format_text'),
    target: CanvasTargetSchema,
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    highlight: z.enum([...CanvasHighlightSchema.options, CANVAS_FORMAT_REMOVE]).optional(),
    color: z
      .string()
      .regex(new RegExp(`^(#[0-9A-Fa-f]{6}|${CANVAS_FORMAT_REMOVE})$`), `color must be a #RRGGBB hex value, or "${CANVAS_FORMAT_REMOVE}" to remove the colour`)
      .optional(),
    fontFamily: z
      .string()
      .trim()
      .min(1)
      .max(CANVAS_FONT_FAMILY_MAX_LENGTH)
      .optional(),
    fontSizePt: z
      .union([z.number().min(1).max(CANVAS_FONT_SIZE_PT_MAX).multipleOf(0.5), z.literal(CANVAS_FORMAT_REMOVE)])
      .optional(),
    onlyWhere: CanvasTextFormatFilterSchema.optional(),
  })
  .strict();

/**
 * Retrait maximal, en centimètres. Valeur de jugement, non mesurée : la
 * largeur d'une page A4 (21 cm), au-delà de laquelle un retrait n'est plus un
 * retrait mais une valeur donnée dans une autre unité.
 */
const CANVAS_INDENT_CM_MAX = 21;
/** Espacement maximal avant ou après un paragraphe, en points. Valeur de jugement, non mesurée : dix lignes d'acte. */
const CANVAS_SPACE_PT_MAX = 200;
/** Bornes de l'interligne, en multiple de l'interligne simple. Valeurs de jugement, non mesurées : hors d'elles, la valeur est donnée en points ou en pourcent. */
const CANVAS_LINE_SPACING_MIN = 0.5;
const CANVAS_LINE_SPACING_MAX = 5;

const retraitEnCm = z.number().min(0).max(CANVAS_INDENT_CM_MAX);
const espacementEnPt = z.number().min(0).max(CANVAS_SPACE_PT_MAX);

/**
 * Mettre en forme des paragraphes entiers (#1866, #1867, spec #1858) : style
 * nommé, alignement, retraits, espacement, interligne, bordure, remise à zéro.
 *
 * La cible est celle des outils de paragraphe (`canvasTarget.ts`) : une liste,
 * une plage, tout l'acte — jamais un passage. Les réglages portent les clés et
 * les unités que rend la lecture de forme ({@link CANVAS_PARAGRAPH_FORMAT_KEYS}) :
 * centimètres pour les retraits, points pour l'espacement, multiple de
 * l'interligne simple pour l'interligne. Ce que chaque valeur devient pour
 * l'éditeur est dit dans `reglagesDeParagraphe.ts` (navigateur).
 *
 * `indentFirstLineCm` négatif : retrait suspendu, comme à la lecture.
 *
 * `styleId` pose un style de paragraphe que le fichier de l'acte définit, sans
 * toucher à la forme posée à la main ; donné avec d'autres réglages, ceux-ci
 * sont posés à la main par-dessus lui (`formatParagraphsExecutor.ts`).
 *
 * Partage des textes lus par le modèle (#1870) : la description du verbe dit
 * ce qu'il règle et ce que cela coûte ; ce qu'une VALEUR veut dire est dit au
 * champ, là où le modèle le remplit (`border`, `borderWidthPt`, `reset`, le
 * signe d'`indentFirstLineCm`, l'unité de `lineSpacing`). `styleId` n'a pas de
 * description : la ligne du verbe dit d'où vient un identifiant de style.
 *
 * `border` dit tout l'état de la bordure, dans les mots de la lecture (`bottom` :
 * le seul trait du bas ; `top+bottom` ; `left` : le trait de marge ; `box` : les
 * quatre côtés ; `none` : aucune) ; `borderWidthPt` en fixe l'épaisseur, et n'a
 * de sens qu'avec un trait à poser. `shading` pose ou retire le fond du
 * paragraphe, `cellShading` celui de la cellule de tableau qui le contient
 * (2026-10-11 : ce que l'agent lit d'un modèle, « bordure bas », « fond », il
 * peut l'écrire). `cellBorder` dit tout le cadre de cette même cellule, dans
 * les mots de `border` (2026-10-12 : le titre qu'Hector écrit est une case
 * encadrée, et rien ne retirait ce cadre). Il n'a pas de description au
 * schéma : son nom et la ligne du verbe le rangent à côté de `border`, et le
 * schéma des verbes de forme est à son plafond de poids. `reset` supprime la
 * forme posée à la main — de paragraphe et de texte — et rend celle du style
 * nommé, qui reste : il ne se combine avec aucun autre réglage, parce que
 * l'éditeur efface l'alignement en remettant à zéro et qu'un plan d'alignement
 * ne peut pas suivre une écriture par paragraphe (`ecritureDeForme.ts`).
 */
export const FormatParagraphsInputSchema = z
  .object({
    action: z.literal('format_paragraphs'),
    target: CanvasParagraphTargetSchema,
    styleId: z.string().min(1).optional(),
    alignment: CanvasParagraphAlignmentSchema.optional(),
    indentLeftCm: retraitEnCm.optional(),
    indentRightCm: retraitEnCm.optional(),
    indentFirstLineCm: z.number().min(-CANVAS_INDENT_CM_MAX).max(CANVAS_INDENT_CM_MAX).describe('Négatif : retrait suspendu.').optional(),
    spaceBeforePt: espacementEnPt.optional(),
    spaceAfterPt: espacementEnPt.optional(),
    lineSpacing: z.number().min(CANVAS_LINE_SPACING_MIN).max(CANVAS_LINE_SPACING_MAX).describe('Multiple de l\'interligne simple : 1, 1.5, 2.').optional(),
    border: CanvasParagraphBorderSchema
      .describe('Côtés tracés, joints par "+" ("bottom", "top+bottom"), "box", "none", ou "left" seul : trait de marge. Les autres sont retirés. Deux voisins de même bordure : un seul cadre.')
      .optional(),
    borderWidthPt: z
      .number()
      .min(CANVAS_BORDER_WIDTH_PT_MIN)
      .max(CANVAS_BORDER_WIDTH_PT_MAX)
      .describe('Épaisseur. Absente : un trait en place garde la sienne ; sinon 2,25 ("left" seul) ou 0,5.')
      .optional(),
    shading: CanvasShadingSchema.describe('Fond, comme cellShading : "554E94" ou "none".').optional(),
    cellShading: CanvasShadingSchema.optional(),
    cellBorder: CanvasCellBorderSchema.optional(),
    reset: z
      .literal(true)
      .describe('Seul, sans autre réglage : rend au paragraphe et à son texte la forme de leur style nommé. Partent aussi : bordures et barres de changement. Restent : liste, saut de page, niveau de plan, texte masqué.')
      .optional(),
  })
  .strict();

/**
 * Copier la forme d'un paragraphe modèle sur d'autres paragraphes (#1869, spec
 * #1858) : son style nommé, sa forme de paragraphe (alignement, retraits,
 * espacement, interligne, bordure) et sa forme de texte (les sept réglages de
 * `format_text`), telles que l'écran les montre — ce que le style donne
 * compris.
 *
 * `from` est l'identifiant du paragraphe modèle, dans le même acte ; la cible
 * est une liste ou une plage (`canvasTarget.ts`). Ce qui est écrit, ce qui ne
 * l'est pas et pourquoi est dit dans `copieDeForme.ts` (navigateur).
 */
const CopyFormatInputSchema = z
  .object({
    action: z.literal('copy_format'),
    from: z.string().min(1),
    target: CanvasCopyFormatTargetSchema,
  })
  .strict();

/**
 * Une étape d'un plan d'édition natif (#1610).
 *
 * Le modèle nomme un effet juridique visible et sa cible Canvas ; il ne reçoit
 * jamais les `where`, `ref` ou `nodeType` de l'API SuperDoc. Le navigateur
 * résout à nouveau chaque sélecteur sous la même révision, puis compile le
 * plan vers les opérations natives atomiques. Cela garde les identifiants
 * fournisseur hors de la conversation et interdit de rejouer un locator
 * internalisé par le modèle après une mutation.
 */
const CanvasEditPlanStepIdSchema = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_-]{0,79}$/, 'edit-plan step ids must start with a letter and contain only letters, digits, "_" or "-"');

const CanvasEditPlanOperationSchema = z.enum([
  'assert_text',
  'text_rewrite',
  'text_insert',
  'text_delete',
  'format_apply',
]);

const CanvasEditPlanFormatMarkSchema = z.union([
  z.object({ bold: z.boolean() }).strict(),
  z.object({ italic: z.boolean() }).strict(),
  z.object({ underline: z.boolean() }).strict(),
  z.object({ highlight: CanvasHighlightSchema }).strict(),
  z.object({ color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'color must be a #RRGGBB hex value') }).strict(),
]);

const CanvasEditPlanStepSchema = z.discriminatedUnion('op', [
  z.object({
    id: CanvasEditPlanStepIdSchema,
    op: z.literal('assert_text'),
    selector: CanvasSelectorSchema,
    text: CanvasTextSearchTermSchema,
    expectedCount: z.number().int().min(0).max(100),
  }).strict(),
  z.object({
    id: CanvasEditPlanStepIdSchema,
    op: z.literal('text_rewrite'),
    selector: CanvasSelectorSchema,
    find: CanvasTextSearchTermSchema,
    replace: z.string().min(1),
  }).strict(),
  z.object({
    id: CanvasEditPlanStepIdSchema,
    op: z.literal('text_insert'),
    selector: CanvasSelectorSchema,
    position: z.enum(['before', 'after']),
    text: z.string().refine((value) => value.trim().length > 0, 'inserted text must not be blank'),
  }).strict(),
  z.object({
    id: CanvasEditPlanStepIdSchema,
    op: z.literal('text_delete'),
    selector: CanvasSelectorSchema,
    text: CanvasTextSearchTermSchema,
  }).strict(),
  z.object({
    id: CanvasEditPlanStepIdSchema,
    op: z.literal('format_apply'),
    selector: CanvasSelectorSchema,
    text: CanvasTextSearchTermSchema,
    mark: CanvasEditPlanFormatMarkSchema,
  }).strict(),
]);

const ApplyEditPlanInputSchema = z
  .object({
    action: z.literal('apply_edit_plan'),
    // min(1), pas min(2) : un plan d'UNE retouche est un usage légitime (une
    // seule correction atomique relue avant/après). Le plancher à 2 rendait en
    // prod « Array must contain at least 2 element(s) » et poussait l'agent à
    // fabriquer une assertion de remplissage (2026-09-30).
    // Avec min(1), un plan fait SEULEMENT d'assert_text devenait valide : il ne
    // modifie rien mais rendait un receipt de mutation (« TRACKING_NOT_APPLIED …
    // changed every requested target » en suivi, faux succès en direct). Un assert
    // ne sert qu'à garder une étape qui modifie, donc il en faut au moins une.
    // Le raffinement vit sur le TABLEAU, pas sur l'objet : un ZodEffects au niveau
    // de l'objet sortirait ce verbe de l'union discriminée par `action`.
    steps: z.array(CanvasEditPlanStepSchema).min(1).max(50).superRefine((steps, ctx) => {
      // steps.length > 0 : un tableau vide est déjà refusé par min(1) ; sans ce test,
      // [].every(...) ajoutait un second message faux « every step is assert_text ».
      if (steps.length > 0 && steps.every((step) => step.op === 'assert_text')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `apply_edit_plan needs at least one step that changes the document; every step is assert_text (ids: ${steps.map((step) => step.id).join(', ')}). assert_text only guards a mutating step — add the rewrite/insert/delete/format step, or use canvas_inspect to merely check a text.`,
        });
      }
    }),
  })
  .strict();

/**
 * Insérer un TITRE de niveau 1 à 6 (#992).
 *
 * Le niveau est borné 1–6 parce que le vocabulaire de styles Hector s'arrête
 * là (`Hector-H1` … `Hector-H6`) : un niveau 7 nommerait un style qui n'existe
 * pas dans le gabarit, et la pose échouerait après que le paragraphe a déjà été
 * écrit — un demi-effet, le pire des résultats.
 */
const InsertHeadingInputSchema = z
  .object({
    action: z.literal('insert_heading'),
    text: z.string().refine((value) => value.trim().length > 0, 'heading text must not be blank'),
    level: z.number().int().min(1).max(6),
    placement: CanvasPlacementSchema,
  })
  .strict();

/**
 * Poser un SAUT DE PAGE avant un bloc (#992).
 *
 * Le saut se pose AVANT le bloc désigné, jamais après : c'est la sémantique
 * OOXML (`w:pageBreakBefore` est une propriété du paragraphe qui ouvre la page),
 * et la description du verbe l'énonce pour que le modèle ne vise pas le bloc
 * précédent.
 */
const InsertPageBreakInputSchema = z
  .object({
    action: z.literal('insert_page_break'),
    selector: CanvasSelectorSchema,
  })
  .strict();

/**
 * Transformer une portion de texte EXISTANTE en hyperlien (#992).
 *
 * `href` est fermé à HTTP(S) : `z.string().url()` accepte notamment
 * `javascript:` et `data:`, que le fournisseur transformerait en contenu
 * cliquable. Un refus de validation est moins coûteux qu'un lien actif injecté
 * dans un acte communiqué.
 */
const AddHyperlinkInputSchema = z
  .object({
    action: z.literal('add_hyperlink'),
    selector: CanvasSelectorSchema,
    text: z.string().min(1),
    href: z.string().refine(estUrlWebSure, 'href must be an absolute http:// or https:// URL'),
  })
  .strict();

/**
 * Créer un TABLEAU et le remplir (#992).
 *
 * `rows` est une matrice de textes de cellule, ligne par ligne : une seule
 * entrée porte donc à la fois la géométrie et le contenu, et il n'existe pas
 * d'état où l'une contredit l'autre. La rectangularité est vérifiée par
 * `appliquerLesReglesInterChamps` — une ligne plus courte que les autres
 * écrirait le contenu aux mauvaises places.
 *
 * ⚠️ `rows` est dans `CANVAS_STRUCTURAL_KEYS` : c'est une valeur imbriquée, que
 * certains modèles sérialisent en chaîne JSON.
 *
 * `borders` (#1861) : `"none"` pose un tableau INVISIBLE, qui ne sert qu'à
 * placer du texte côte à côte. Absent ou `"all"` : le tableau quadrillé, qui
 * reste le défaut. Mêmes deux valeurs que `TableBlock.borders` du parseur
 * d'acte — un seul vocabulaire pour les bordures de tableau.
 */
const CreateTableInputSchema = z
  .object({
    action: z.literal('create_table'),
    rows: z.array(z.array(z.string()).min(1).max(10)).min(1).max(50),
    borders: z.enum(['all', 'none']).optional(),
    placement: CanvasPlacementSchema,
  })
  .strict();

/**
 * Les trois verbes de LISTE (#994) — ils travaillent sur ce que l'avocat a
 * DÉJÀ écrit, là où `write_markdown` pose une liste neuve.
 *
 * ⚠️ Mesuré avant d'être écrit (`tests/ws-mocked/mesure-994-listes.spec.ts`) :
 * `lists.create({mode:'fromParagraphs'})` accepte des paragraphes existants et
 * leur `nodeId` SURVIT à la conversion, et `lists.attach` **poursuit** la
 * numérotation au lieu de repartir à 1 (marqueurs peints relevés `1.` à `6.`).
 * Cette continuité est la raison d'être d'`attach_numbering` : sans elle, le
 * verbe produirait deux listes côte à côte — indiscernables de la relecture, et
 * absurdes à l'écran.
 */
const ConvertListInputSchema = z
  .object({
    action: z.literal('convert_list'),
    from: CanvasSelectorSchema,
    to: CanvasSelectorSchema.optional(),
    kind: z.enum(['ordered', 'bullet']),
    startAt: z.number().int().positive().optional(),
  })
  .strict();

const AddListItemsInputSchema = z
  .object({
    action: z.literal('add_list_items'),
    selector: CanvasSelectorSchema,
    position: z.enum(['after', 'before']).default('after'),
    texts: z.array(z.string().min(1)).min(1).max(30),
  })
  .strict();

const AttachNumberingInputSchema = z
  .object({
    action: z.literal('attach_numbering'),
    from: CanvasSelectorSchema,
    to: CanvasSelectorSchema.optional(),
    attachTo: CanvasSelectorSchema,
  })
  .strict();

/**
 * Le verbe de STRUCTURE DE TABLE (#994) — un seul verbe pour les quatre gestes,
 * et non quatre verbes.
 *
 * Deux raisons, l'une mesurée, l'autre économique. Le fournisseur lui-même
 * modélise ces quatre opérations comme UN groupe d'intentions (`intentGroup:
 * "table"`, `intentAction: "insert_row" | "delete_row" | …` dans son registre) ;
 * et chaque verbe du catalogue coûte ~93 tokens de consigne à CHAQUE tour de
 * CHAQUE conversation (mesuré en #995) — quatre verbes qui partagent la même
 * cible, le même receipt et le même mode d'échec en coûteraient quatre fois plus
 * pour la même capacité.
 *
 * ⚠️ **`count` n'existe QUE pour l'insertion**, et c'est un fait mesuré, pas une
 * simplification : `deleteRow({rowIndex:1, count:2})` rend `success: true` et ne
 * supprime **qu'une seule ligne** (5×4 → 4×4 relevé le 2026-08-16). Exposer
 * `count` à la suppression ferait promettre à l'avocat une suppression qui n'a
 * pas lieu, sans le moindre signal. La règle est appliquée par
 * `appliquerLesReglesInterChamps`, pas seulement écrite ici.
 *
 * ⚠️ **`texts` ne remplit que la ligne INSÉRÉE.** Écrire dans une cellule
 * préexistante est refusé par le fournisseur sous le régime suivi — le régime
 * par défaut — avec `CAPABILITY_UNAVAILABLE: "Table mutations do not support
 * tracked mode on v2 yet."`, alors que remplir une cellule de la ligne qu'on
 * vient d'ajouter passe. La distinction porte sur la CELLULE, pas sur le
 * tableau : du contenu qui vit dans une insertion déjà suivie n'a pas besoin de
 * sa propre révision. C'est aussi pourquoi aucun verbe de ce lot ne propose de
 * modifier le texte d'une cellule existante : il serait refusé par défaut.
 */
const EditTableInputSchema = z
  .object({
    action: z.literal('edit_table'),
    selector: CanvasSelectorSchema,
    operation: z.enum(['insert_row', 'delete_row', 'insert_column', 'delete_column']),
    index: z.number().int().nonnegative().optional(),
    position: z.enum(['above', 'below', 'left', 'right']).optional(),
    count: z.number().int().positive().max(20).optional(),
    texts: z.array(z.string()).max(10).optional(),
  })
  .strict();

/**
 * Les cinq verbes de la TRANCHE 3 (#996) — commentaires, déplacement,
 * rétablissement.
 *
 * Le ticket en nommait huit. Trois sont tombés à la mesure, et leur absence est
 * un RÉSULTAT, pas un oubli :
 *
 * - **`insert_toc` est impossible** sur ce runtime : il n'existe aucun
 *   `toc.insert` (la famille `toc.*` a dix opérations, toutes pour manipuler un
 *   sommaire déjà là), et le chemin par champ brut fait planter le fournisseur
 *   en interne — « Cannot read properties of undefined (reading 'story') » sur
 *   les trois ancrages essayés.
 * - **`fill_placeholders` doublerait `replace_text`** : aucune surface
 *   `placeholder` n'existe, et `templates.apply` est hors chantier (spec #985).
 * - **`comment_paragraphs` est `add_comments`** — le ticket le nomme deux fois.
 *
 * ⚠️ **Un commentaire s'ancre sur du TEXTE, jamais sur un bloc.** Le
 * fournisseur refuse une adresse de bloc et n'accepte que le champ `target` que
 * `query.match` rend lui-même — pas son `handle.ref`, qui est pourtant l'adresse
 * écrivable qu'utilise `add_hyperlink`. Les deux vivent côte à côte dans le
 * même objet et rendent le même refus. Le sélecteur du catalogue
 * reste donc un `CanvasSelectorSchema` ordinaire : c'est l'exécuteur qui le
 * traduit en cible de commentaire.
 */
const AddCommentsInputSchema = z
  .object({
    action: z.literal('add_comments'),
    selector: CanvasSelectorSchema,
    text: z.string().min(1).max(2_000),
  })
  .strict();

/**
 * ⚠️ **Une réponse ne prend PAS de cible** — « Cannot combine parentCommentId
 * with target. Replies do not take a target. » Elle n'a que son parent, et
 * c'est pourquoi ce schéma n'a pas de `selector` : en offrir un ferait
 * construire au modèle un appel que le fournisseur refuse.
 */
const ReplyToCommentInputSchema = z
  .object({
    action: z.literal('reply_to_comment'),
    commentId: z.string().min(1),
    text: z.string().min(1).max(2_000),
  })
  .strict();

/**
 * ⚠️ **Résoudre la racine d'un fil résout TOUT le fil**, réponses comprises
 * (mesuré). Le verbe porte donc sur une conversation, pas sur un message — sa
 * description doit le dire, sinon le modèle croit clore une remarque isolée.
 *
 * L'arbitrage produit du 2026-08-16 autorise l'agent à clore **n'importe quel**
 * commentaire, y compris celui d'un confrère.
 */
const ResolveCommentsInputSchema = z
  .object({
    action: z.literal('resolve_comments'),
    commentIds: z.array(z.string().min(1)).min(1).max(50),
  })
  .strict();

/**
 * ⚠️ `blocks.move` figure dans `V1_RUNTIME_UNAVAILABLE_OPERATION_IDS` des
 * `.d.ts` — et fonctionne parfaitement (prouvé par les ordinaux relus, rang 3 →
 * rang 1). C'est la SECONDE opération de cette liste à démentir sa propre
 * déclaration, après `lists.remove` en #994 : **cette constante est périmée, et
 * `capabilities.get()` fait autorité.**
 */
const MoveRangeInputSchema = z
  .object({
    action: z.literal('move_range'),
    selector: CanvasSelectorSchema,
    destination: CanvasSelectorSchema,
    placement: z.enum(['before', 'after']),
  })
  .strict();

/**
 * ⚠️ Pendant exact d'`undo_changes`, qui a vécu toute sa mise en service sur une
 * erreur d'ADRESSE (`doc.undo`, inexistant). Le rétablissement vit sous
 * `doc.history.redo()`, sans entrée ni options — et `doc.history.get()` publie
 * `{undoDepth, redoDepth, canUndo, canRedo}`, ce qui permet de REFUSER avant
 * d'essayer plutôt que de faire dépenser un tour à l'agent pour apprendre qu'il
 * n'y a rien à rétablir.
 */
const RedoChangesInputSchema = z
  .object({ action: z.literal('redo_changes') })
  .strict();

/**
 * Cible d'une décision de revue : des révisions NOMMÉES, ou TOUTES.
 *
 * `kind:'all'` existe parce que « accepte tout » obligeait l'agent à relire le
 * document entier (50–90 k caractères en prod) pour recopier 59 à 107 ids
 * opaques. SuperDoc décide nativement `{kind:'all'}` en une unité d'historique
 * (typage `ReviewDecideTarget` de `superdoc/dist/document-api/src/track-changes/
 * track-changes.d.ts`, vérifié 2026-09-30). `kind:'all'` ne porte AUCUN id : un
 * mélange « tous sauf X » doit rester une décision `ids` explicite.
 */
const trackedChangesTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ids'), ids: z.array(z.string().min(1)).min(1) }).strict(),
  z.object({ kind: z.literal('all') }).strict(),
]);
const AcceptTrackedChangesInputSchema = z
  .object({ action: z.literal('accept_tracked_changes'), target: trackedChangesTargetSchema })
  .strict();
const RejectTrackedChangesInputSchema = z
  .object({ action: z.literal('reject_tracked_changes'), target: trackedChangesTargetSchema })
  .strict();

const UndoChangesInputSchema = z
  .object({
    action: z.literal('undo_changes'),
    // Le plafond est la profondeur de l'historique de l'éditeur : une mise en
    // forme qui a demandé une écriture par paragraphe se défait en un appel.
    steps: z.number().int().positive().max(CANVAS_UNDO_HISTORY_DEPTH).default(1),
  })
  .strict();

/**
 * Les trois verbes de NOTE DE BAS DE PAGE — relevé du 2026-08-26
 * (`tests/ws-mocked/mesure-footnotes.spec.ts`, §9 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`).
 *
 * ⚠️ **Plusieurs notes cohabitent dans un même paragraphe** — remesuré le
 * 2026-08-26 : trois insertions réussies dans un même paragraphe, dont une
 * COLLÉE à un renvoi existant. Une mesure antérieure avait conclu l'inverse ;
 * elle était fausse, sa plage d'ancrage étant fabriquée à la main plutôt que
 * dérivée de `query.match`. Aucune limite à annoncer au modèle, donc.
 *
 * Le refus `CAPABILITY_UNAVAILABLE — « footnotes.insert currently supports
 * plain paragraph text runs only »` existe bel et bien, mais il sanctionne une
 * plage MAL FORMÉE, pas l'état du document. L'exécuteur dérivant toujours son
 * ancre de `query.match`, il ne devrait pas survenir — il est néanmoins rendu
 * au modèle tel quel plutôt qu'avalé.
 *
 * `selector` est un `CanvasSelectorSchema` ordinaire — il désigne le BLOC
 * porteur, comme pour `add_comments` — et `anchor_text` le fragment DANS ce bloc
 * après lequel le renvoi se pose. Deux champs plutôt qu'un sélecteur de texte :
 * le fournisseur veut une plage `{blockId, range}` (forme `at` vérifiée), donc
 * un bloc résolu ET un décalage dedans.
 */
const InsertFootnoteInputSchema = z
  .object({
    action: z.literal('insert_footnote'),
    selector: CanvasSelectorSchema,
    // Borne haute absente, comme `add_hyperlink.text` :
    // ce champ recopie du texte DÉJÀ présent dans le document, il ne l'écrit pas.
    anchor_text: z.string().min(1),
    // Borné comme `add_comments.text` : c'est de la prose neuve rédigée par
    // l'agent, du même ordre de longueur qu'un commentaire.
    text: z.string().min(1).max(2_000),
  })
  .strict();

/**
 * ⚠️ **Modifier une note ne produit AUCUNE révision** — mesuré le 2026-08-26,
 * et *contrôlé* : la première mesure portait sur une note insérée sous suivi
 * (donc encore en attente de revue, où l'absence de seconde révision serait
 * légitime) ; refaite sur une note posée en `direct`, donc commitée, le résultat
 * est le même, témoin `doc.replace` toujours à 1. D'où `reviewPolicy: 'direct'`
 * au catalogue, malgré le `tracked: true` que `footnotes.update` DÉCLARE.
 *
 * Conséquence pour l'avocat, et c'est pourquoi la description du verbe le dit :
 * une note réécrite ne laisse aucune trace relisible en régime suivi.
 */
const EditFootnoteInputSchema = z
  .object({
    action: z.literal('edit_footnote'),
    // `note_id` vient de `canvas_inspect` avec le domaine `footnotes` — même
    // contrat que `commentId` : sans cette surface de lecture, le verbe serait
    // inappelable.
    note_id: z.string().min(1),
    text: z.string().min(1).max(2_000),
  })
  .strict();

/**
 * ⚠️ **Une suppression suivie ne retire pas la note de `footnotes.list()`** :
 * elle y reste, avec son contenu, marquée en attente de revue (mesuré
 * 2026-08-26). Compter les notes après un `delete_footnote` ne dit donc rien de
 * ce que l'avocat verra — la description du verbe l'énonce pour qu'un modèle qui
 * relit ne prenne pas la persistance pour un échec et ne rejoue pas l'appel.
 */
const DeleteFootnoteInputSchema = z
  .object({
    action: z.literal('delete_footnote'),
    note_id: z.string().min(1),
  })
  .strict();

/**
 * L'en-tête du cabinet, posé ou retiré par l'agent.
 *
 * ## Pourquoi un NOM et jamais un identifiant
 *
 * Le tool `list_letterheads` ne rend pas les `id` — sa projection s'arrête au
 * nom, à la note d'usage, au défaut, à l'actif et à la présence d'un fichier. Le
 * modèle ne dispose donc QUE du nom, et sa résolution
 * (`resolveLetterheadByName`, avec ses issues `found` / `not-found` /
 * `ambiguous`) s'exécute côté front, seul endroit qui détienne à la fois les
 * octets de l'image et l'API du document. `name` absent = l'en-tête par défaut
 * du cabinet, exactement comme le bouton de la barre d'outils.
 *
 * ## Les deux drapeaux sont `z.literal(true)`, pas `z.boolean()`
 *
 * `remove: false` et `replace_existing: false` ne veulent rien dire — ils
 * décrivent l'absence d'un geste, que l'omission de la clé dit déjà. Les
 * accepter multiplierait les façons d'écrire la même demande et ouvrirait la
 * porte à un `{action, remove: false}` sans `name`, c'est-à-dire à un appel qui
 * passe le schéma sans rien demander. Même parti que `isDefault` dans le
 * contrat des en-têtes nommés.
 *
 * ## `replace_existing` porte le consentement de l'avocat, pas une préférence
 *
 * La garde d'idempotence des en-têtes est structurellement ANONYME : le contrat
 * `HeaderFooterSlotEntry` ne porte aucune provenance, donc un logo importé avec
 * le `.docx` de l'avocat et un logo posé par Hector sont indiscernables. Le
 * premier appel est toujours ADDITIF et se refuse sur un document qui porte déjà
 * un en-tête ; l'agent doit alors nommer le coût à l'avocat — « s'il venait d'un
 * Word que vous avez importé, il sera perdu » — et ne repasser
 * `replace_existing` qu'après un oui. C'est la confirmation de
 * `LetterheadToolbarControl`, déplacée du dialogue modal vers la conversation,
 * seul endroit où l'agent peut la recueillir.
 */
const SetLetterheadInputSchema = z
  .object({
    action: z.literal('set_letterhead'),
    name: z.string().min(1).max(120).optional(),
    remove: z.literal(true).optional(),
    replace_existing: z.literal(true).optional(),
  })
  .strict();

/**
 * Borne d'un côté d'image, en pixels (96 par pouce) : quatre fois la hauteur
 * d'une page A4 (1 123 px). Au-delà, la valeur n'est plus une taille d'image
 * dans un acte mais une erreur d'unité.
 */
export const CANVAS_IMAGE_SIDE_PX_MAX = 4_500;

/**
 * Une image du corps telle que l'agent la lit : son identifiant, son placement
 * et sa taille en pixels entiers. Il n'en voit pas le contenu.
 */
export const CanvasInspectImageSchema = z
  .object({
    imageId: z.string().min(1),
    placement: z.enum(['inline', 'floating']),
    width: z.number().int().nonnegative(),
    height: z.number().int().nonnegative(),
  })
  .strict();
export type CanvasInspectImage = z.infer<typeof CanvasInspectImageSchema>;

/**
 * Ce que l'agent lit à la place d'une image dans le texte d'un bloc : assez
 * pour la nommer à l'avocat et la viser par `edit_image`.
 */
export function canvasImageReadLabel(image: CanvasInspectImage): string {
  return `[image ${image.imageId} · ${image.width}×${image.height} px · ${image.placement}]`;
}

/**
 * Reconnaît une étiquette d'image dans un texte que l'agent veut ÉCRIRE.
 *
 * L'étiquette n'existe que dans la lecture : recopiée dans une écriture, elle
 * s'afficherait telle quelle dans l'acte, à côté de l'image qu'elle désigne.
 */
export const CANVAS_IMAGE_READ_LABEL_PATTERN = /\[image \S+ · \d+×\d+ px · (?:inline|floating)\]/;

/** Images rendues par bloc inspecté : au-delà, le bloc est une planche d'images, pas un paragraphe d'acte. */
export const CANVAS_INSPECT_IMAGES_PER_BLOCK_MAX = 20;

/**
 * D'où vient l'image qu'un verbe pose ou substitue : exactement UNE des trois
 * clés (`appliquerLesReglesInterChamps`).
 *
 * - `documentId` : un fichier d'un dossier de la conversation, l'identifiant que rendent
 *   `find_case_documents` et `get_case_structure` ;
 * - `attachmentId` : un fichier joint à la conversation, l'identifiant du bloc
 *   « [Documents attachés : … (id: …)] » ;
 * - `imageId` : une image déjà dans le corps de l'acte, pour la copier.
 *
 * Un objet à trois clés facultatives plutôt qu'une union de trois objets : une
 * union non discriminée rend au modèle « Invalid input » sans nommer la clé
 * fautive, alors que la règle « une seule » se dit ici en une phrase.
 *
 * Les deux premières sont résolues par le BACKEND, qui tient les gardes
 * d'accès : les octets voyagent à côté de l'action (`imageBytes` de
 * l'enveloppe), jamais dans ces paramètres. La troisième est résolue par le
 * navigateur, dans le fichier de l'acte.
 */
export const CanvasImageSourceSchema = z
  .object({
    documentId: z.string().min(1).max(200).optional(),
    attachmentId: z.string().min(1).max(200).optional(),
    imageId: z.string().min(1).max(300).optional(),
  })
  .strict();
export type CanvasImageSource = z.infer<typeof CanvasImageSourceSchema>;

/**
 * Les octets d'une image résolue par le backend, tels qu'ils voyagent dans
 * l'enveloppe d'une action, à côté d'elle.
 *
 * Ce n'est PAS un argument du modèle : il ne lit ni n'écrit jamais ce champ,
 * et aucun reçu ne le renvoie. La borne de `base64` est celle du poids maximal
 * d'une image (`CANVAS_IMAGE_SOURCE_MAX_BYTES`), quatre caractères pour trois
 * octets.
 */
export const CanvasImageBytesSchema = z
  .object({
    mimeType: z.enum(CANVAS_IMAGE_MIME_TYPES),
    base64: z.string().min(1).max(Math.ceil(CANVAS_IMAGE_SOURCE_MAX_BYTES / 3) * 4),
  })
  .strict();
export type CanvasImageBytes = z.infer<typeof CanvasImageBytesSchema>;

/**
 * Poser une image dans le corps, seule dans un paragraphe neuf, avant ou après
 * le bloc `at.nodeId`.
 *
 * `at` nomme le bloc par son identifiant, sans sélecteur : la portée inspectée
 * le contrôle quand même (`lireNodeIdsCibles` du courtier, qui lit
 * `at.nodeId`). `width` est en pixels et garde les proportions de l'image ;
 * absent, l'exécuteur prend la taille de l'image, ramenée à la largeur du
 * texte quand elle la dépasse.
 */
const InsertImageInputSchema = z
  .object({
    action: z.literal('insert_image'),
    source: CanvasImageSourceSchema,
    at: z.object({ nodeId: z.string().min(1).max(200), placement: z.enum(['before', 'after']) }).strict(),
    width: z.number().positive().max(CANVAS_IMAGE_SIDE_PX_MAX).optional(),
  })
  .strict();

/**
 * Retirer UNE image du corps, la redimensionner ou en remplacer le contenu.
 * L'image est désignée par l'identifiant que `canvas_inspect` publie
 * (`images[].imageId`).
 *
 * `remove`, `size` et `source` sont trois gestes : exactement l'un des trois
 * (`appliquerLesReglesInterChamps`). `remove` est `z.literal(true)`, comme
 * celui de `set_letterhead` : `false` ne dit rien que l'absence de la clé ne
 * dise. `size` est en pixels, l'unité dans laquelle la lecture rend la taille ;
 * avec un seul côté, l'exécuteur garde les proportions lues. `source` remplace
 * le contenu et laisse l'image à sa place : le cadre garde sa largeur.
 */
const EditImageInputSchema = z
  .object({
    action: z.literal('edit_image'),
    imageId: z.string().min(1).max(300),
    remove: z.literal(true).optional(),
    size: z
      .object({
        width: z.number().positive().max(CANVAS_IMAGE_SIDE_PX_MAX).optional(),
        height: z.number().positive().max(CANVAS_IMAGE_SIDE_PX_MAX).optional(),
      })
      .strict()
      .optional(),
    source: CanvasImageSourceSchema.optional(),
  })
  .strict();

/**
 * Les trois zones d'un en-tête ou d'un pied de page, **nommées comme dans
 * l'add-in Word** (`primary` / `first_page` / `even_pages`).
 *
 * ⚠️ **Ce n'est PAS le vocabulaire de SuperDoc**, qui dit `default` / `first` /
 * `even` (`HeaderFooterVariant`). La traduction vit dans l'exécuteur, et
 * l'alignement sur Word est délibéré : le même avocat dicte le même geste à
 * Hector depuis son Word et depuis le canvas web, et deux vocabulaires pour la
 * même chose lui vaudraient deux formulations à retenir. `default` est en outre
 * un mot que le modèle lit comme « la valeur par défaut de l'argument » plutôt
 * que comme « toutes les pages », ce qui est exactement le contresens à éviter.
 */
const CanvasHeaderFooterTypeSchema = CanvasPageZoneSchema;
export type CanvasHeaderFooterType = z.infer<typeof CanvasHeaderFooterTypeSchema>;

/**
 * Écrire dans l'en-tête ou le pied de page d'une section (mesuré 2026-09-05,
 * `tests/ws-mocked/mesure-en-tetes-et-pieds.spec.ts`).
 *
 * ## Le numéro de page est du CONTENU de la zone (2026-10-07)
 *
 * Il se pose par le texte d'un paragraphe : `{page}` devient le champ `PAGE`,
 * `{pages}` le champ `NUMPAGES` (`CANVAS_PAGE_NUMBER_TOKENS`), le reste du texte
 * est écrit tel quel autour — « Page {page} / {pages} ». Pas de second champ
 * d'entrée ni de verbe à part : c'est un paragraphe comme un autre, avec son
 * alignement, et « X » comme « X / Y » ne sont que deux textes.
 *
 * Il se retire par `remove_page_number`, qui prend la place de `paragraphs` :
 * l'un ou l'autre (`appliquerLesReglesInterChamps`). `z.literal(true)` et non
 * un booléen, comme `remove` de `set_letterhead` : `false` ne dit rien que
 * l'absence de la clé ne dise.
 *
 * `paragraphs` plutôt qu'un `text` unique : un en-tête de cabinet tient
 * couramment sur trois lignes (raison sociale, adresse, barreau), et les
 * concaténer avec des retours à la ligne produirait UN paragraphe Word au lieu
 * de trois — donc un interligne faux et un alignement qu'on ne peut plus régler
 * ligne par ligne. La borne haute de 20 est celle d'un en-tête plausible : au
 * delà, l'agent est en train d'écrire le corps de l'acte au mauvais endroit.
 */
const SetHeaderFooterInputSchema = z
  .object({
    action: z.literal('set_header_footer'),
    target: z.enum(['header', 'footer']),
    type: CanvasHeaderFooterTypeSchema.default('primary'),
    /**
     * Rang 0-based de la section, tel que `canvas_inspect` le publie sous
     * `section_index`. La plupart des actes n'ont qu'une section — d'où le
     * défaut — mais un acte importé qui change d'orientation en compte
     * plusieurs, et écrire dans la mauvaise reste invisible à l'écran tant que
     * la page correspondante n'est pas atteinte.
     */
    section_index: z.number().int().nonnegative().default(0),
    /**
     * `replace` remplace le TEXTE en place, `append` ajoute à la suite.
     *
     * ⚠️ `replace` ne supprime JAMAIS une image de l'en-tête — voir
     * `previous_had_image` dans le receipt et la description du verbe.
     */
    mode: z.enum(['replace', 'append']).default('replace'),
    paragraphs: z
      .array(
        z
          .object({
            text: z.string().min(1).max(500),
            alignment: z.enum(['left', 'center', 'right']).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(20)
      .optional(),
    /**
     * Retire de la zone les paragraphes qui portent un numéro de page, LIGNE ENTIÈRE : un texte qui partage la ligne
     * du numéro part avec lui, et le reçu le cite (`page_numbers.removed_paragraphs`). `mode` n'y joue pas.
     */
    remove_page_number: z.literal(true).optional(),
  })
  .strict();

/**
 * Ce qu'un texte de `set_header_footer` écrit pour un numéro de page : le jeton,
 * par champ. Entre accolades : une suite que le texte d'un en-tête d'acte ne
 * porte pas, et que le modèle lit comme une place à remplir.
 */
export const CANVAS_PAGE_NUMBER_TOKENS = { page: '{page}', pages: '{pages}' } as const;

/**
 * Plafond de saisie d'UNE marge de page, en centimètres. Il n'établit pas qu'il reste de quoi écrire : deux marges de
 * 10 cm dépassent la largeur d'un A5 (14,8 cm). C'est l'exécuteur qui refuse, avant d'écrire, une page dont la zone
 * de texte tomberait sous {@link CANVAS_PAGE_TEXT_AREA_CM_MIN} (`zoneDeTexteTropPetite`, `miseEnPage.ts`).
 */
export const CANVAS_PAGE_MARGIN_CM_MAX = 10;

/**
 * Nombre maximal de blocs qu'une zone d'en-tête ou de pied rend à la relecture (`blocks.list`, `limit`), et de champs
 * de numéro de page que le reçu en cite (`page_numbers.fields_after`). Valeur de jugement : une zone porte quelques
 * lignes. Au-delà, le reçu coupe plutôt que d'être refusé entier.
 */
export const CANVAS_HEADER_FOOTER_BLOCKS_MAX = 60;

/**
 * Largeur et hauteur minimales de la zone de texte qu'une mise en page doit laisser, en centimètres : la page moins ses
 * deux marges. Valeur de jugement — en dessous, une ligne ne tient plus un mot long.
 */
export const CANVAS_PAGE_TEXT_AREA_CM_MIN = 1;

const CanvasPageMarginCmSchema = z.number().min(0).max(CANVAS_PAGE_MARGIN_CM_MAX);

/**
 * Régler la page du document : marges, orientation, format (mesuré le
 * 2026-10-06 et le 2026-10-07, `mesure-socle-page-et-defauts`,
 * `mesure-socle-numero-et-page`).
 *
 * Un réglage de SECTION, posé sur toutes les sections : « passe le document en
 * paysage » vaut pour tout le document. Il n'y a pas de section à viser — un
 * document dont une seule section change d'orientation se règle dans Word.
 *
 * Les marges sont en centimètres, l'unité de l'avocat et des retraits de
 * `format_paragraphs` ; l'éditeur prend des pouces, convertis par l'exécuteur.
 * Au moins un réglage : `appliquerLesReglesInterChamps`.
 */
export const SetPageLayoutInputSchema = z
  .object({
    action: z.literal('set_page_layout'),
    marginsCm: z
      .object({
        top: CanvasPageMarginCmSchema.optional(),
        bottom: CanvasPageMarginCmSchema.optional(),
        left: CanvasPageMarginCmSchema.optional(),
        right: CanvasPageMarginCmSchema.optional(),
      })
      .strict()
      .optional(),
    orientation: CanvasPageOrientationSchema.optional(),
    paperSize: CanvasPaperSizeSchema.optional(),
  })
  .strict();

/**
 * Régler le régime de suivi du document (#1838) — le réglage que porte le bouton
 * « Suivi des modifications » de la barre du canvas, et lui seul.
 *
 * Un booléen plutôt qu'un mode d'éditeur (`editing` / `suggesting`) : l'agent
 * règle ce que l'avocat voit sur son bouton, pas la mécanique du fournisseur.
 */
const SetReviewModeInputSchema = z
  .object({
    action: z.literal('set_review_mode'),
    enabled: z.boolean(),
  })
  .strict();

/**
 * Les branches du schéma d'action, indexées par nom de verbe.
 *
 * WHY ce record en plus du tuple de définitions : c'est lui qui rend le
 * catalogue **filtrable par document** (#987). Le backend construit le schéma
 * de `canvas_perform_action` à chaque tour depuis les seuls verbes que le
 * document sait exécuter — un verbe dont l'opération Document API est absente
 * n'apparaît alors pas dans le contrat que le modèle lit, au lieu d'être
 * découvert par un refus au tour suivant (`undo_changes` a vécu ainsi toute sa
 * mise en service).
 *
 * ⚠️ Un `ZodObject` **nu** est requis par `z.discriminatedUnion` : n'y mettre
 * jamais un `.refine()` local (il produirait un `ZodEffects`, refusé). Les
 * contraintes inter-champs vivent dans `appliquerLesReglesInterChamps` ; un
 * raffinement interne à UN champ (ex. `apply_edit_plan.steps`) reste admis sur ce champ.
 */
const CANVAS_ACTION_BRANCHES = {
  rewrite_block: RewriteBlockInputSchema,
  replace_text: ReplaceTextInputSchema,
  apply_edit_plan: ApplyEditPlanInputSchema,
  insert_paragraphs: InsertParagraphsInputSchema,
  delete_block_range: DeleteBlockRangeInputSchema,
  apply_style: ApplyStyleInputSchema,
  write_markdown: WriteMarkdownInputSchema,
  format_text: FormatTextInputSchema,
  format_paragraphs: FormatParagraphsInputSchema,
  copy_format: CopyFormatInputSchema,
  insert_heading: InsertHeadingInputSchema,
  insert_page_break: InsertPageBreakInputSchema,
  add_hyperlink: AddHyperlinkInputSchema,
  create_table: CreateTableInputSchema,
  convert_list: ConvertListInputSchema,
  add_list_items: AddListItemsInputSchema,
  attach_numbering: AttachNumberingInputSchema,
  edit_table: EditTableInputSchema,
  add_comments: AddCommentsInputSchema,
  reply_to_comment: ReplyToCommentInputSchema,
  resolve_comments: ResolveCommentsInputSchema,
  move_range: MoveRangeInputSchema,
  redo_changes: RedoChangesInputSchema,
  accept_tracked_changes: AcceptTrackedChangesInputSchema,
  reject_tracked_changes: RejectTrackedChangesInputSchema,
  undo_changes: UndoChangesInputSchema,
  insert_footnote: InsertFootnoteInputSchema,
  edit_footnote: EditFootnoteInputSchema,
  delete_footnote: DeleteFootnoteInputSchema,
  set_letterhead: SetLetterheadInputSchema,
  set_header_footer: SetHeaderFooterInputSchema,
  set_page_layout: SetPageLayoutInputSchema,
  insert_image: InsertImageInputSchema,
  edit_image: EditImageInputSchema,
  set_review_mode: SetReviewModeInputSchema,
} as const satisfies Record<CanvasActionName, z.AnyZodObject>;

type CanvasActionBranch = (typeof CANVAS_ACTION_BRANCHES)[CanvasActionName];

/**
 * L'ordre canonique des branches — celui qui produit le JSON Schema figé par
 * `CanvasToolDefinitions.test.ts`. Le respecter est ce qui garantit qu'un
 * catalogue complet reconstruit par `buildCanvasActionInputSchema` est
 * byte-identique à celui d'avant #987.
 */
const CANVAS_ACTION_ORDER = Object.keys(CANVAS_ACTION_BRANCHES) as CanvasActionName[];

/** Un texte cité dans un refus de schéma : entre guillemets, borné pour rester lisible. */
function citerPourUnRefus(texte: unknown): string {
  const cite = String(texte);
  return JSON.stringify(cite.length > 60 ? `${cite.slice(0, 60)}…` : cite);
}

/**
 * Les règles de `replace_text` qui lient `edits` à `everywhere` : chaque refus
 * nomme l'entrée fautive et dit quoi renvoyer.
 *
 * Sans `everywhere:true`, un seul edit et un `expectedCount` absent ou égal à 1. Avec : pas de
 * `scope` ni de `caseSensitive:false`, un `expectedCount` par edit, un `replace`
 * non vide et différent de `find`, des `find` distincts à la typographie près,
 * et une somme des `expectedCount` qui tient en UN plan atomique.
 *
 * ⚠️ Ni `find` ni `replace` ne commence ou ne finit par une espace : la règle
 * du mot entier ne garde un bord que s'il porte une lettre ou un chiffre, donc
 * « 1 500 » suivi d'une espace serait trouvé dans « 1 500 000 € » et y écrirait
 * un montant faux.
 */
function verifierLeRemplacementDeTexte(value: Record<string, unknown>, ctx: z.RefinementCtx): void {
  const edits: Array<{ find?: unknown; replace?: unknown; expectedCount?: unknown }> = Array.isArray(value.edits)
    ? value.edits.filter((edit): edit is Record<string, unknown> => typeof edit === 'object' && edit !== null)
    : [];
  const refuser = (path: Array<string | number>, message: string): void => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
  if (value.everywhere !== true) {
    if (edits.length > 1) {
      refuser(['edits'], `replace_text without everywhere:true takes exactly one edit (got ${edits.length}). `
        + 'To replace the same texts in the whole document add everywhere:true and an expectedCount on each edit; for linked edits in chosen blocks use apply_edit_plan.');
    }
    edits.forEach((edit, rang) => {
      // Mesuré en production le 2026-10-13 : l'agent ajoute `expectedCount: 1` au remplacement d'un passage, et
      // l'appel était refusé alors que ce mode garantit déjà ce compte. Seul un compte supérieur demande l'autre mode.
      if (typeof edit.expectedCount === 'number' && edit.expectedCount > 1) {
        refuser(['edits', rang, 'expectedCount'], `replace_text without everywhere:true replaces one passage, so edits[${rang}].expectedCount (got ${edit.expectedCount}) cannot be above 1. `
          + `Add everywhere:true to replace all ${edit.expectedCount} occurrences of this text in the whole document, or remove expectedCount to replace one passage.`);
      }
    });
    return;
  }
  if (value.scope !== undefined) {
    refuser(['scope'], 'replace_text with everywhere:true searches the whole document (body and tables), so it takes no scope (got one). '
      + 'Remove scope, or remove everywhere and expectedCount to replace one passage inside that block.');
  }
  if (value.caseSensitive === false) {
    refuser(['caseSensitive'], 'replace_text with everywhere:true is always case-sensitive (got caseSensitive:false). '
      + 'Remove caseSensitive and give each find with the exact capitals the document reads; one edit per spelling.');
  }
  let somme = 0;
  const plies = edits.map((edit) => (typeof edit.find === 'string' ? plierLaTypographie(edit.find) : undefined));
  edits.forEach((edit, rang) => {
    for (const champ of ['find', 'replace'] as const) {
      const texte = edit[champ];
      // `trim` retire aussi les espaces insécables et fines : c'est la comparaison voulue.
      if (typeof texte === 'string' && texte !== '' && texte !== texte.trim()) {
        refuser(['edits', rang, champ], `replace_text with everywhere:true refuses a ${champ} that starts or ends with a space, or is only spaces: edits[${rang}].${champ} is ${citerPourUnRefus(texte)}. `
          + 'A space at the edge defeats the whole-word guard ("1 500 " would match inside "1 500 000 €"). Remove the leading and trailing spaces (non-breaking and thin spaces included) and resend.');
      }
    }
    if (typeof edit.expectedCount === 'number') somme += edit.expectedCount;
    else {
      refuser(['edits', rang, 'expectedCount'], `replace_text with everywhere:true requires expectedCount on every edit; edits[${rang}] (find ${citerPourUnRefus(edit.find)}) has none. `
        + 'Give the number of times you read this text in the document: the call writes only if every count matches.');
    }
    if (edit.replace === '') {
      refuser(['edits', rang, 'replace'], `replace_text with everywhere:true cannot delete a text everywhere: edits[${rang}] (find ${citerPourUnRefus(edit.find)}) has an empty replace. `
        + 'Give the new text, or delete the passage in each block with apply_edit_plan (text_delete steps).');
    } else if (edit.replace === edit.find) {
      refuser(['edits', rang, 'replace'], `replace_text with everywhere:true would change nothing: edits[${rang}] has the same find and replace (${citerPourUnRefus(edit.find)}). Remove this edit or correct its replace.`);
    }
    const premier = plies.findIndex((plie) => plie !== undefined && plie === plies[rang]);
    if (premier !== rang && premier >= 0) {
      refuser(['edits', rang, 'find'], edits[premier]!.find === edit.find
        ? `replace_text with everywhere:true requires distinct find texts; edits[${premier}] and edits[${rang}] both look for ${citerPourUnRefus(edit.find)}. Keep one edit per text.`
        : `replace_text with everywhere:true requires distinct find texts; edits[${premier}] (${citerPourUnRefus(edits[premier]!.find)}) and edits[${rang}] (${citerPourUnRefus(edit.find)}) differ only by the kind of their spaces or apostrophes, which the search ignores: both would match the same occurrences. Keep one edit for this text.`);
    }
  });
  if (somme > CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX) {
    refuser(['edits'], `replace_text with everywhere:true writes at most ${CANVAS_REPLACE_EVERYWHERE_OCCURRENCES_MAX} occurrences per call (the expectedCount of these ${edits.length} edits add up to ${somme}). `
      + 'Split the edits into several calls, each atomic on its own.');
  }
}

/**
 * Les contraintes entre champs d'une action. Elles ne peuvent pas vivre dans
 * les branches (Zod veut un ZodObject nu dans une union discriminée) ; elles
 * s'appliquent après le choix de branche, donc l'erreur reste attachée au bon
 * chemin.
 *
 * `value` est un enregistrement nu : le schéma filtré peut ne pas contenir une
 * branche, auquel cas TypeScript ne connaît pas son littéral dans l'union.
 */
function appliquerLesReglesInterChamps(value: Record<string, unknown>, ctx: z.RefinementCtx): void {
  if (value.action === 'apply_edit_plan') {
    const steps = value.steps;
    if (Array.isArray(steps)) {
      const ids = steps.map((step) => (typeof step === 'object' && step !== null ? (step as { id?: unknown }).id : undefined));
      const duplicate = ids.find((id, index) => typeof id === 'string' && ids.indexOf(id) !== index);
      if (typeof duplicate === 'string') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps'],
          message: `apply_edit_plan requires distinct stable step ids; duplicate "${duplicate}".`,
        });
      }
    }
    return;
  }
  if (value.action === 'replace_text') {
    verifierLeRemplacementDeTexte(value, ctx);
    return;
  }
  // Écrire un texte ou retirer le numéro de page : deux demandes, une par appel.
  if (value.action === 'set_header_footer') {
    const ecrit = value.paragraphs !== undefined;
    const retire = value.remove_page_number !== undefined;
    if (ecrit === retire) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: ecrit
          ? 'set_header_footer takes either paragraphs (text to write) or remove_page_number:true, not both (got both). Send two calls: the removal first, then the paragraphs with mode:"append".'
          : 'set_header_footer requires paragraphs (at least one, text to write) or remove_page_number:true (got neither).',
      });
    }
    return;
  }
  // Tous les réglages sont optionnels dans la branche : un appel qui n'en porte aucun n'écrirait rien.
  if (value.action === 'set_page_layout') {
    const marges = typeof value.marginsCm === 'object' && value.marginsCm !== null ? Object.values(value.marginsCm).filter((marge) => marge !== undefined) : [];
    if (marges.length === 0 && value.orientation === undefined && value.paperSize === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: 'set_page_layout requires at least one setting among marginsCm (with at least one side), orientation, paperSize (got none).',
      });
    }
    return;
  }
  // Une ligne plus courte que les autres écrirait le contenu aux mauvaises
  // places : la table est créée à la largeur de la PREMIÈRE ligne, et les
  // cellules suivantes seraient décalées sans qu'aucun refus ne le signale.
  if (value.action === 'create_table') {
    const rows = value.rows;
    if (Array.isArray(rows) && rows.length > 0) {
      const largeur = Array.isArray(rows[0]) ? (rows[0] as unknown[]).length : 0;
      const irregulière = rows.some((ligne) => !Array.isArray(ligne) || ligne.length !== largeur);
      if (irregulière) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rows'],
          message: 'create_table requires every row to have the same number of cells',
        });
      }
    }
    return;
  }
  // Tous les réglages sont optionnels dans la branche : un appel qui n'en porte
  // aucun n'écrirait RIEN tout en rendant un reçu. Le refus est ici, après le
  // choix de branche, parce qu'une branche d'union discriminée doit rester un
  // ZodObject nu.
  if (value.action === 'format_text') {
    if (CANVAS_TEXT_FORMAT_SETTING_KEYS.every((reglage) => value[reglage] === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: `format_text requires at least one setting among ${CANVAS_TEXT_FORMAT_SETTING_KEYS.join(', ')}`,
      });
    }
    // #1947 — une liste de textes désigne des occurrences dans tout l'acte : un paragraphe n'y a pas de sens.
    const cible = value.target as CanvasTarget;
    if (cible.kind === 'text' && Array.isArray(cible.text) && cible.nodeId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target', 'text'],
        message: `format_text target {kind:"text"}: a list of texts (got ${cible.text.length}) designates their occurrences in the whole act and takes no nodeId (got ${JSON.stringify(cible.nodeId)}). `
          + 'Remove nodeId, or send one text with this nodeId.',
      });
    }
    if (cible.kind === 'text' && cible.nodeId !== undefined && cible.in !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target', 'in'],
        message: `format_text target {kind:"text"}: nodeId names a body paragraph (got ${JSON.stringify(cible.nodeId)}), so it cannot be combined with a place (in: ${JSON.stringify(cible.in)}). `
          + 'Remove nodeId to format every occurrence of the text in that place, or remove "in" to format the body paragraph.',
      });
    }
    return;
  }
  // Même règle, même raison que `format_text`.
  if (value.action === 'format_paragraphs') {
    const demandes = CANVAS_PARAGRAPH_SETTING_KEYS.filter((reglage) => value[reglage] !== undefined);
    if (demandes.length === 0 && value.reset === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: `format_paragraphs requires at least one setting among ${CANVAS_PARAGRAPH_SETTING_KEYS.join(', ')}, or reset:true`,
      });
    }
    if (value.reset !== undefined && demandes.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reset'],
        message: `format_paragraphs reset:true removes every setting made by hand, so it cannot be combined with ${demandes.join(', ')}: `
          + 'send reset:true alone, then a second format_paragraphs call with these settings.',
      });
    }
    if (value.borderWidthPt !== undefined && (value.border === undefined || value.border === 'none')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['borderWidthPt'],
        message: `format_paragraphs borderWidthPt is the thickness of a border to draw: it requires a border that draws a side — "bottom", "top+bottom", "left", "box"… (got border ${JSON.stringify(value.border ?? null)}).`,
      });
    }
    return;
  }
  /**
   * `insert_image`, `edit_image` — la source d'une image tient en UNE clé :
   * deux désigneraient deux images, aucune n'en désigne pas.
   */
  if (value.action === 'insert_image' || value.action === 'edit_image') {
    const source = typeof value.source === 'object' && value.source !== null ? (value.source as Record<string, unknown>) : undefined;
    const cles = source === undefined ? [] : Object.keys(source).filter((cle) => source[cle] !== undefined);
    if (source !== undefined && cles.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source'],
        message: `${value.action} source takes exactly one of documentId (a file of a case of this conversation), attachmentId (a file attached to the conversation) or imageId (an image of the document body), `
          + `got ${cles.length === 0 ? 'none' : cles.join(' and ')}. Send the one key that names the image.`,
      });
    }
  }
  /**
   * `edit_image` — retirer, redimensionner et remplacer sont trois gestes : un
   * appel en porte exactement un. Deux ensemble demanderaient de régler ou de
   * remplacer une image qu'on retire ; aucun, l'appel ne demande rien.
   */
  if (value.action === 'edit_image') {
    const taille = typeof value.size === 'object' && value.size !== null ? (value.size as Record<string, unknown>) : undefined;
    const gestes = [value.remove === true ? 'remove' : undefined, taille !== undefined ? 'size' : undefined, value.source !== undefined ? 'source' : undefined]
      .filter((geste): geste is string => geste !== undefined);
    if (gestes.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [gestes[1] ?? 'remove'],
        message: 'edit_image takes exactly one of remove:true (delete the image), size (resize it) or source (replace its picture), '
          + `got ${gestes.length === 0 ? 'none' : gestes.join(' and ')}. Send one call per change.`,
      });
    } else if (taille !== undefined && taille.width === undefined && taille.height === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['size'],
        message: 'edit_image size requires width, height, or both, in pixels (got none). With only one of them the image keeps its proportions.',
      });
    }
    return;
  }
  if (value.action === 'insert_image') return;
  /**
   * `set_letterhead` — retirer et poser sont deux gestes opposés sous un verbe.
   *
   * `remove` avec `name` demande littéralement « retire l'en-tête, et pose
   * celui-ci » : les deux moitiés s'annulent, et rien dans l'exécuteur ne peut
   * deviner laquelle l'avocat voulait. `remove` avec `replace_existing` est
   * pire — le consentement à un écrasement n'a aucun sens quand on ne pose rien,
   * et l'accepter laisserait croire au modèle qu'il a couvert le cas coûteux.
   * Les deux se ferment AVANT l'appel : après, le document a déjà bougé.
   */
  if (value.action === 'set_letterhead' && value.remove === true) {
    if (value.name !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'set_letterhead cannot take both remove and name — removing a letterhead and placing one are '
          + 'two separate calls',
      });
    }
    if (value.replace_existing !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replace_existing'],
        message: 'set_letterhead cannot take both remove and replace_existing — nothing is placed, so there is '
          + 'nothing to replace',
      });
    }
    return;
  }
  /**
   * `edit_table` — quatre gestes sous un verbe, donc quatre combinaisons
   * illégales à fermer AVANT l'appel plutôt qu'après l'écriture.
   *
   * ⚠️ La première règle est la seule qui protège d'un mensonge SILENCIEUX :
   * `deleteRow({rowIndex, count})` rend `success: true` en ne supprimant qu'une
   * ligne (mesuré). Sans ce refus, « supprime les trois dernières lignes »
   * renverrait un receipt en succès pour un document où deux lignes restent —
   * et rien, ni le provider ni la relecture de géométrie, ne dirait que la
   * demande n'a pas été honorée.
   */
  if (value.action === 'edit_table') {
    const insertion = value.operation === 'insert_row' || value.operation === 'insert_column';
    const ligne = value.operation === 'insert_row' || value.operation === 'delete_row';

    if (!insertion && value.count !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['count'],
        message: 'edit_table: count is only honoured when inserting. The provider silently ignores it on '
          + 'delete_row/delete_column and removes exactly one — issue one call per row or column to delete.',
      });
    }
    if (!insertion && value.index === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['index'],
        message: 'edit_table: delete_row and delete_column require index (0-based) — there is no implicit target.',
      });
    }
    if (value.position !== undefined) {
      const attendues = ligne ? ['above', 'below'] : ['left', 'right'];
      if (!attendues.includes(String(value.position))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['position'],
          message: `edit_table: position must be ${attendues.join(' or ')} for ${String(value.operation)}.`,
        });
      }
      if (!insertion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['position'],
          message: 'edit_table: position only applies to insert_row and insert_column.',
        });
      }
      if (value.index === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['index'],
          message: 'edit_table: position needs index — it says WHERE relative to which existing row or column. '
            + 'Omit both to append at the end.',
        });
      }
    }
    if (value.texts !== undefined && value.operation !== 'insert_row') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['texts'],
        message: 'edit_table: texts fills the row being inserted, so it only applies to insert_row. Cells of a '
          + 'pre-existing row cannot be rewritten while change tracking is on.',
      });
    }
    if (value.texts !== undefined && value.count !== undefined && value.count !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['texts'],
        message: 'edit_table: texts fills ONE row, so count must be 1 (or omitted) when texts is given.',
      });
    }
  }
}

/**
 * Construit le schéma d'action restreint aux verbes passés.
 *
 * Rend `null` sur une liste vide : un document qui ne sait exécuter aucun verbe
 * ne doit pas se voir proposer `canvas_perform_action` du tout — et
 * `z.discriminatedUnion` exige de toute façon au moins une branche. Renvoyer un
 * schéma qui n'accepte rien serait pire que ne rien exposer : le modèle
 * tenterait un appel et lirait une erreur de validation au lieu de constater
 * l'absence de l'outil.
 *
 * L'ordre des branches suit toujours `CANVAS_ACTION_ORDER`, quel que soit
 * l'ordre d'entrée : le JSON Schema d'un catalogue donné ne doit pas dépendre de
 * l'ordre dans lequel le front a énuméré ses capacités.
 */
export function buildCanvasActionInputSchema(
  names: readonly CanvasActionName[],
): z.ZodEffects<z.ZodDiscriminatedUnion<'action', CanvasActionBranch[]>> | null {
  const retenus = CANVAS_ACTION_ORDER.filter((name) => names.includes(name));
  if (retenus.length === 0) return null;

  const branches = retenus.map((name) => CANVAS_ACTION_BRANCHES[name]);
  return z
    .discriminatedUnion('action', branches as [CanvasActionBranch, ...CanvasActionBranch[]])
    .superRefine((value, ctx) => appliquerLesReglesInterChamps(value as Record<string, unknown>, ctx));
}

/**
 * Union **discriminée** sur `action` — et c'est load-bearing, pas cosmétique.
 *
 * WHY : en `z.union` simple, Zod essaie les 8 branches, les voit toutes échouer
 * et ne peut désigner ni la branche visée ni la clé fautive. Le modèle recevait
 * `(root): Invalid input` pour TOUTE malformation — `texts` en chaîne, `edits`
 * en objet, `text` en objet, une action inexistante : le même message opaque.
 * Il ne pouvait que retenter au hasard (incident canvas 2026-08-03).
 *
 * Discriminée, Zod sait quelle branche viser dès `action` et rend l'erreur
 * exacte : `texts: Expected array, received string`, `selector.terms: Expected
 * array, received string`, ou la liste des actions valides si `action` est
 * inconnue. Le JSON Schema produit pour la définition d'outil
 * (`zodToJsonSchema`) est **inchangé au caractère près** — vérifié — donc le
 * contrat vu par le modèle ne bouge pas ; seule la qualité du diagnostic change.
 *
 * Le `superRefine` porte les contraintes entre champs (`appliquerLesReglesInterChamps`),
 * qui ne peuvent pas vivre dans une branche : Zod exige des ZodObject nus dans
 * une union discriminée. Il s'applique après le choix de branche, donc l'erreur
 * reste attachée au bon chemin.
 */
export const CanvasActionInputSchema = z
  .discriminatedUnion(
    'action',
    CANVAS_ACTION_ORDER.map((name) => CANVAS_ACTION_BRANCHES[name]) as [CanvasActionBranch, ...CanvasActionBranch[]],
  )
  .superRefine((value, ctx) => appliquerLesReglesInterChamps(value as Record<string, unknown>, ctx));
export type CanvasActionInput = z.infer<typeof CanvasActionInputSchema>;

/**
 * Clés des schémas canvas dont la valeur attendue est un objet ou un tableau.
 *
 * WHY : certains modèles (constaté en staging le 2026-08-03 sur
 * `tensorix/z-ai/glm-5.2`, conversation `851d5560-29e1-41d9-9223-73b08707af67`)
 * sérialisent les valeurs IMBRIQUÉES d'un tool call en chaîne JSON, alors que le
 * JSON Schema annonce bien `array`/`object` :
 *   `{ texts: "[\"A\",\"B\"]", placement: "{\"at\":\"document_start\"}" }`
 * Zod rejetait alors le payload sur `CanvasActionInputSchema` — une `z.union` de
 * 8 variantes, donc une erreur `(root): Invalid input` sans clé fautive. L'agent
 * ne pouvait pas se corriger : il a retenté 5 fois avec des stratégies
 * différentes, toutes invalides pour la même raison invisible, puis a abandonné
 * la rédaction devant l'avocat.
 *
 * La donnée est présente et non ambiguë : on la répare au lieu de la rejeter
 * (même arbitrage que pour les autres sorties LLM — cf. `.strict()` vs `.strip()`
 * dans le CLAUDE.md de ce paquet).
 *
 * Liste FERMÉE et volontairement étroite : `text`, `styleId`, `nodeId` et
 * `find`/`replace` sont de vraies chaînes et ne doivent JAMAIS être parsées —
 * un paragraphe juridique qui commencerait par `{` deviendrait sinon un objet.
 */
const CANVAS_STRUCTURAL_KEYS = new Set([
  'texts',
  'placement',
  'selector',
  'scope',
  'edits',
  // #1610 — un plan est une liste d'étapes structurées ; les chaînes de texte
  // qu'elles portent restent du texte et ne sont jamais désérialisées.
  'steps',
  'from',
  'to',
  'styleSource',
  'target',
  // #1865 — le filtre de `format_text` : un objet de réglages, jamais un texte d'acte.
  'onlyWhere',
  'includeDomains',
  'terms',
  'ids',
  // #992 — la clé structurelle de `create_table`. `text`, `href` et `styleId`
  // restent volontairement dehors : ce sont de vraies chaînes, et un texte
  // d'acte qui commencerait par `{` deviendrait un objet.
  'rows',
  // La liste de `rewrite_block`. `nodeId` et `text` restent dehors : de vraies chaînes.
  'blocks',
  // #994 — `attachTo` est le seul champ structurel neuf des verbes de liste et
  // de table : `from`, `to`, `selector` et `texts` y figurent déjà. `operation`,
  // `position` et `kind` restent dehors — ce sont des énumérations, donc de
  // vraies chaînes, et les parser n'aurait aucun sens.
  'attachTo',
  // #996 — `destination` est un sélecteur, donc une valeur imbriquée.
  // `commentId` et `text` restent dehors : ce sont de vraies chaînes, et un
  // commentaire qui commencerait par `{` deviendrait un objet.
  'destination',
  'commentIds',
  // #1606 — continuation structurée d'un paragraphe long. Le texte lui-même
  // reste volontairement hors de cette liste : seule sa plage est un objet.
  'textWindow',
  // #1859 — la cible unique des outils de forme : l'objet `formatOf` et sa
  // liste `nodeIds`. Les `from`/`to` d'une plage sont des identifiants, donc
  // de vraies chaînes : déjà listés pour les sélecteurs, ils ne sont parsés
  // que s'ils commencent par `{` ou `[`.
  'formatOf',
  'nodeIds',
  // La lecture avec forme de l'acte ouvert : un objet, pour porter le rang de départ.
  'formatReading',
]);

/**
 * Répare, en profondeur, les valeurs structurelles d'un tool call canvas que le
 * modèle a sérialisées en chaîne JSON. Toute valeur déjà bien typée est rendue
 * inchangée, et une chaîne non parsable est laissée telle quelle pour que Zod
 * produise l'erreur de type habituelle.
 *
 * À appliquer AVANT `CanvasActionInputSchema` / `CanvasInspectInputSchema`.
 * Délibérément un helper séparé, et non un `z.preprocess` sur les schémas :
 * ceux-ci sont convertis en JSON Schema pour la définition des tools
 * (`openAiParameters`), et un `ZodEffects` en tête casserait cette conversion.
 */
export function coerceCanvasToolArgs(args: unknown): unknown {
  if (Array.isArray(args)) return args.map(coerceCanvasToolArgs);
  if (args === null || typeof args !== 'object') return args;

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(args as Record<string, unknown>)) {
    let next = value;
    if (CANVAS_STRUCTURAL_KEYS.has(key) && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          next = JSON.parse(trimmed) as unknown;
        } catch {
          // Chaîne qui ressemble à du JSON sans en être : on la laisse passer
          // pour que Zod signale le vrai type attendu plutôt qu'une erreur de
          // parsing opaque.
        }
      }
    }
    out[key] = coerceCanvasToolArgs(next);
  }
  return reparerLaListeDeTextes(out);
}

/**
 * La liste de textes d'une cible « texte » (`{kind:"text", text:[…]}`, #1947)
 * que le modèle a sérialisée en chaîne JSON, rendue en liste.
 *
 * WHY à part de {@link CANVAS_STRUCTURAL_KEYS} : `text` y reste interdit — c'est
 * partout ailleurs un texte d'acte. Ici la réparation est bornée à la cible
 * « texte » SANS paragraphe (la seule qui admet une liste), et à un tableau
 * JSON non vide de chaînes. « [Décision attaquée] » ou « [1] » restent le texte
 * qu'ils sont. Limite assumée : un texte d'acte qui serait lui-même un tableau
 * JSON de chaînes (`["Pièce 1"]`, guillemets droits compris) est lu comme une
 * liste ; pour le viser tel quel, l'agent nomme son paragraphe.
 */
function reparerLaListeDeTextes(objet: Record<string, unknown>): Record<string, unknown> {
  const { target } = objet;
  if (target === null || typeof target !== 'object' || Array.isArray(target)) return objet;
  const { kind, text, nodeId } = target as Record<string, unknown>;
  if (kind !== 'text' || nodeId !== undefined || typeof text !== 'string' || !text.trim().startsWith('[')) return objet;
  let liste: unknown;
  try {
    liste = JSON.parse(text);
  } catch {
    return objet;
  }
  if (!Array.isArray(liste) || liste.length === 0 || !liste.every((element) => typeof element === 'string')) return objet;
  return { ...objet, target: { ...target, text: liste } };
}

/**
 * Borne max de `blockLimit` pour `canvas_inspect` — exportée pour que les
 * appelants qui paginent manuellement (ex. sonde de référence #930,
 * `tests/ws-mocked/canvas-orchestrator-reference-live.spec.ts`) réutilisent
 * la même valeur que le schéma plutôt que de la recopier en dur.
 */
export const CANVAS_INSPECT_BLOCK_LIMIT_MAX = 250;

/** Default page size used only when an explicit bounded continuation is requested. */
export const CANVAS_INSPECT_BLOCK_LIMIT_DEFAULT = 80;

/**
 * Maximum native Markdown body exposed by one Canvas document read.
 *
 * This deliberately matches the established `read_document` body ceiling
 * (300k UTF-16 code units, roughly 75k tokens) instead of assuming a provider
 * context window. The receipt publishes this limit's actual coverage, so a
 * caller can never mistake a prefix for a complete live Canvas document.
 */
export const CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH = 300_000;

/** A compact range list must not recreate a second full source map in context. */
export const CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX = 250;

/**
 * Vue de relecture demandée au projecteur natif SuperDoc.
 *
 * Ce choix ne décide jamais une révision suivie et ne décrit pas si le Canvas
 * est inscriptible : il dit seulement quel côté des changements ouverts est
 * rendu à l'agent. Le mode documentaire reste publié séparément dans le
 * receipt d'inspection.
 */
export const CanvasInspectionProjectionSchema = z.enum(['final', 'original', 'redline']);
export type CanvasInspectionProjection = z.infer<typeof CanvasInspectionProjectionSchema>;

export const CanvasInspectTextWindowSchema = z
  .object({
    offset: z.number().int().nonnegative().describe('Décalage 0-based dans le texte logique du bloc public visé.'),
    limit: z.number().int().positive().max(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH).describe('Nombre maximal de caractères à lire dans cette continuation, au plus le budget déclaré de lecture Canvas.'),
  })
  .strict();
export type CanvasInspectTextWindow = z.infer<typeof CanvasInspectTextWindowSchema>;

export const CanvasInspectInputSchema = z
  .object({
    projection: CanvasInspectionProjectionSchema.default('final').describe(
      'Vue de relecture du document : final inclut les insertions et masque les suppressions ouvertes ; original fait l’inverse ; redline rend les deux côtés sans décider les modifications suivies. final est la valeur par défaut.',
    ),
    includeDomains: z
      .array(z.enum(['blocks', 'styles', 'trackedChanges', 'comments', 'sections', 'footnotes', 'headersFooters']))
      .min(1)
      .default(['blocks', 'styles'])
      .describe('Domaines à inclure dans la réponse. blocks est requis pour lire ou cibler le document.'),
    selector: CanvasSelectorSchema.optional().describe('Cible facultative à résoudre dans tout le document. Sans selector et sans fenêtre, la lecture porte sur le document entier ; avec selector, elle porte sur tous les appariements.'),
    blockOffset: z.number().int().nonnegative().optional().describe('Décalage 0-based d’une fenêtre de blocs explicite. Exige blockLimit ; vaut 0 quand seul blockLimit est fourni. Sert uniquement à poursuivre une lecture incomplète ou paginer des appariements.'),
    blockLimit: z.number().int().positive().max(CANVAS_INSPECT_BLOCK_LIMIT_MAX).optional().describe(`Nombre de blocs d’une fenêtre explicite, de 1 à ${CANVAS_INSPECT_BLOCK_LIMIT_MAX} ; blockOffset vaut 0 s’il est omis. ${CANVAS_INSPECT_BLOCK_LIMIT_DEFAULT} est la taille conseillée de continuation.`),
    textWindow: CanvasInspectTextWindowSchema.optional().describe('Continuation d’un paragraphe long. Disponible uniquement avec selector:{kind:"nodeId"}; elle lit la plage demandée du bloc public sans fabriquer une adresse de mutation.'),
    formatOf: CanvasParagraphTargetSchema.optional().describe('Cible de la lecture de forme.'),
    formatReading: CanvasFormatReadingRequestSchema.optional().describe('Lecture avec forme de l’acte ouvert.'),
  })
  .strict()
  .superRefine((value, context) => {
    // Une lecture de forme est une lecture à part (#1859) : sa cible est
    // `formatOf`, et la forme lue est celle du texte affiché. La combiner à une
    // autre adresse ou à une vue analytique obligerait à deviner laquelle
    // l'emporte — or aucun paramètre accepté ne peut être ignoré.
    // La lecture avec forme (`formatReading`) est l'autre lecture à part, sous
    // la même règle : elle rend tout le corps, page par page, dans la vue finale.
    const lectureAPart = value.formatOf !== undefined ? 'formatOf' : value.formatReading !== undefined ? 'formatReading' : undefined;
    if (lectureAPart !== undefined) {
      const enTrop = [
        ...(value.formatOf !== undefined && value.formatReading !== undefined ? ['formatReading'] : []),
        ...(value.selector === undefined ? [] : ['selector']),
        ...(value.blockOffset === undefined && value.blockLimit === undefined ? [] : ['blockOffset/blockLimit']),
        ...(value.textWindow === undefined ? [] : ['textWindow']),
        ...(value.projection === 'final' ? [] : [`projection:"${value.projection}"`]),
      ];
      if (enTrop.length > 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [lectureAPart],
          message: `${lectureAPart} cannot be combined with ${enTrop.join(', ')}. Remove ${enTrop.length > 1 ? 'them' : 'it'} to read the form, or remove ${lectureAPart} to run the other read.`,
        });
      }
    }
    const hasBlockOffset = value.blockOffset !== undefined;
    const hasBlockLimit = value.blockLimit !== undefined;
    // `blockLimit` seul est une fenêtre complète depuis le début : le navigateur
    // lit `blockOffset ?? 0`. Le refuser rendait en prod une erreur de schéma
    // pour `{blockLimit:50}` (2026-09-30). `blockOffset` seul reste refusé : un
    // décalage sans taille ne dit pas combien de blocs la continuation couvre.
    // Le backend injecte `blockOffset:0` explicite avant l'envoi, pour les
    // bundles navigateur antérieurs qui exigent encore la paire.
    if (hasBlockOffset && !hasBlockLimit) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blockLimit'],
        message: 'blockOffset requires blockLimit (1 to 250) to bound the inspection window; blockLimit alone starts at blockOffset 0.',
      });
    }
    if (value.textWindow !== undefined && value.selector?.kind !== 'nodeId') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['textWindow'],
        message: 'textWindow requires selector:{kind:"nodeId"} from a fresh Canvas inspection.',
      });
    }
    if (value.textWindow !== undefined && (hasBlockOffset || hasBlockLimit)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['textWindow'],
        message: 'textWindow is its own continuation and cannot be combined with blockOffset/blockLimit.',
      });
    }
  })
  .describe('Entrée de canvas_inspect : sans fenêtre, lecture du document vivant complet quand il tient dans le budget déclaré ; une fenêtre explicite sert uniquement de continuation ou de ciblage. Cette lecture ne mute jamais le document.');
export type CanvasInspectInput = z.infer<typeof CanvasInspectInputSchema>;

const CanvasActorSchema = z.object({ id: z.literal('agent-hector'), name: z.literal('Agent Hector') }).strict();

const CanvasInspectEnvelopeSchema = z
  .object({
    requestId: z.string().min(1),
    tool: z.literal('canvas_inspect'),
    documentSessionId: z.string().min(1),
    /** Monotonic physical-mount identity. Prevents an old async callback from targeting a remount. */
    mountEpoch: z.number().int().nonnegative().optional(),
    input: CanvasInspectInputSchema,
  })
  .strict();

const CanvasActionEnvelopeSchema = z
  .object({
    requestId: z.string().min(1),
    tool: z.literal('canvas_perform_action'),
    documentSessionId: z.string().min(1),
    /** See CanvasInspectEnvelopeSchema.mountEpoch. Optional only for rolling-deploy compatibility. */
    mountEpoch: z.number().int().nonnegative().optional(),
    expectedRevision: z.string().min(1),
    snapshotToken: z.string().min(1),
    actor: CanvasActorSchema,
    requestedReviewPolicy: z.literal('tracked_text'),
    /**
     * Identités publiques issues d'une inspection native `projection:"final"`.
     *
     * Ce champ n'est PAS un argument du modèle : le broker le forge après la
     * lecture du receipt, et le navigateur s'en sert pour empêcher qu'un texte
     * projeté sans identité devienne une cible d'écriture brute. Son absence
     * garde les clients antérieurs à #1605 **décodables pour un refus nommé**
     * pendant le rolling deploy ; elle n'autorise jamais une mutation. Un
     * tableau vide signifie au contraire qu'une projection native a eu lieu
     * mais n'a publié aucune cible mutable.
     */
    inspectedPublicNodeIds: z.array(z.string().min(1)).optional(),
    /**
     * One-use broker authority for the one physical empty paragraph required by
     * a valid Hector DOCX template. It is not model input and applies only to
     * `write_markdown` mode="replace" on that exact paragraph.
     */
    initialBlankTemplateParagraphId: z.string().min(1).optional(),
    /**
     * Les octets de l'image que l'action pose ou substitue, quand sa `source`
     * est une pièce du dossier ou un fichier joint (#2141).
     *
     * Ce champ n'est PAS un argument du modèle : le backend le remplit après
     * avoir appliqué les gardes d'accès et contrôlé le type et le poids des
     * octets. Il voyage à côté de l'action, jamais dedans, et aucun reçu ne le
     * renvoie. Absent pour toute autre action, et pour une source `imageId`,
     * que le navigateur lit dans le fichier de l'acte.
     */
    imageBytes: CanvasImageBytesSchema.optional(),
    action: CanvasActionInputSchema,
  })
  .strict();

export const CanvasAgentRequestEnvelopeSchema = z.union([
  CanvasInspectEnvelopeSchema,
  CanvasActionEnvelopeSchema,
]);
export type CanvasInspectEnvelope = z.infer<typeof CanvasInspectEnvelopeSchema>;
export type CanvasActionEnvelope = z.infer<typeof CanvasActionEnvelopeSchema>;
export type CanvasAgentRequestEnvelope = z.infer<typeof CanvasAgentRequestEnvelopeSchema>;

// ── Forme conversation/époque des mêmes enveloppes (#1335) ─────────────────
//
// Construites par `omit` + `extend` sur les schémas legacy, pour qu'un champ
// ajouté à l'un ne puisse pas manquer à l'autre. `.strict()` est porté par le
// `_def` d'origine et survit aux deux opérations : une enveloppe conversation
// qui porterait AUSSI `documentSessionId` est donc refusée, sans alias possible.
//
// ⚠️ **Exportées nommément** (#1373), et pas seulement enfouies dans l'union de
// transition : un producteur qui VISE la forme conversation doit pouvoir
// appliquer ce schéma-là. L'union `…Transition` accepte les deux formes — s'y
// valider prouverait seulement qu'on a émis l'une des deux, ce qu'un émetteur
// resté en legacy réussirait sans un bruit.
export const CanvasConversationInspectEnvelopeSchema = CanvasInspectEnvelopeSchema
  .omit({ documentSessionId: true, mountEpoch: true })
  .extend(CanvasConversationMountIdentityShape);
export type CanvasConversationInspectEnvelope = z.infer<typeof CanvasConversationInspectEnvelopeSchema>;

export const CanvasConversationActionEnvelopeSchema = CanvasActionEnvelopeSchema
  .omit({ documentSessionId: true, mountEpoch: true })
  .extend(CanvasConversationMountIdentityShape);
export type CanvasConversationActionEnvelope = z.infer<typeof CanvasConversationActionEnvelopeSchema>;

/**
 * Contrat de TRANSITION des commandes canvas : les deux formes d'identité de
 * montage, côte à côte (#1335).
 *
 * ⚠️ Distinct de `CanvasAgentRequestEnvelopeSchema`, qui reste volontairement
 * limité à la forme legacy tant que le navigateur déployé n'a pas basculé
 * (#1336, T7) : c'est LUI que la garde front applique, et un backend qui
 * émettrait la forme conversation vers un client non renouvelé se ferait
 * refuser sa commande. D'où l'ordre de déploiement — ce backend d'abord, le
 * frontend ensuite. Les deux noms fusionnent au retrait final (#1339, T10).
 */
export const CanvasAgentRequestEnvelopeTransitionSchema = z.union([
  CanvasInspectEnvelopeSchema,
  CanvasActionEnvelopeSchema,
  CanvasConversationInspectEnvelopeSchema,
  CanvasConversationActionEnvelopeSchema,
]);
export type CanvasAgentRequestEnvelopeTransition = z.infer<typeof CanvasAgentRequestEnvelopeTransitionSchema>;
export type CanvasInspectEnvelopeTransition = CanvasInspectEnvelope | CanvasConversationInspectEnvelope;
export type CanvasActionEnvelopeTransition = CanvasActionEnvelope | CanvasConversationActionEnvelope;

// ── Progression d'opération Canvas (#1773) ────────────────────────────────

/**
 * Une progression décrit uniquement l'activité du navigateur entre l'envoi
 * d'une commande et son reçu final. Elle ne constitue jamais une preuve que
 * l'écriture a eu lieu : seul `canvas_superdoc_result` peut le faire.
 *
 * Le fil est volontairement fermé et sans données métier : ni contenu,
 * sélecteur, bloc, ni reçu ne doit pouvoir transiter dans une trame affichée
 * dans la timeline de l'avocat.
 */
export const CanvasOperationProgressPhaseSchema = z.enum(['accepted', 'queued', 'running']);
export type CanvasOperationProgressPhase = z.infer<typeof CanvasOperationProgressPhaseSchema>;

const CanvasOperationProgressFields = {
  requestId: z.string().min(1),
  expectedRevision: z.string().min(1).optional(),
  sequence: z.number().int().positive(),
  phase: CanvasOperationProgressPhaseSchema,
  completedUnits: z.number().int().nonnegative().optional(),
  totalUnits: z.number().int().positive().optional(),
} as const;

function verifierProgressionCanvas(
  payload: {
    phase: CanvasOperationProgressPhase;
    completedUnits?: number;
    totalUnits?: number;
  },
  ctx: z.RefinementCtx,
): void {
  const completedUnits = payload.completedUnits;
  const totalUnits = payload.totalUnits;
  const hasCompletedUnits = completedUnits !== undefined;
  const hasTotalUnits = totalUnits !== undefined;
  if (hasCompletedUnits !== hasTotalUnits) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: hasCompletedUnits ? ['totalUnits'] : ['completedUnits'],
      message: 'completedUnits and totalUnits must be provided together',
    });
    return;
  }
  if (!hasCompletedUnits || !hasTotalUnits) return;
  if (payload.phase !== 'running') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['phase'],
      message: 'unit progress is only valid while the Canvas operation is running',
    });
  }
  if (completedUnits > totalUnits) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['completedUnits'],
      message: 'completedUnits must not exceed totalUnits',
    });
  }
}

/** Progression pour un montage legacy, encore accepté pendant la transition #1335. */
export const CanvasOperationProgressLegacySchema = z.object({
  ...CanvasOperationProgressFields,
  documentSessionId: z.string().min(1),
  mountEpoch: z.number().int().nonnegative().optional(),
}).strict().superRefine(verifierProgressionCanvas);
export type CanvasOperationProgressLegacy = z.infer<typeof CanvasOperationProgressLegacySchema>;

/** Progression pour un montage identifié par conversation et époque. */
export const CanvasOperationProgressConversationSchema = z.object({
  ...CanvasOperationProgressFields,
  ...CanvasConversationMountIdentityShape,
}).strict().superRefine(verifierProgressionCanvas);
export type CanvasOperationProgressConversation = z.infer<typeof CanvasOperationProgressConversationSchema>;

/** Les deux formes de montage de la progression, sans alias entre elles. */
export const CanvasOperationProgressPayloadSchema = z.union([
  CanvasOperationProgressLegacySchema,
  CanvasOperationProgressConversationSchema,
]);
export type CanvasOperationProgressPayload = z.infer<typeof CanvasOperationProgressPayloadSchema>;

// ── Sonde passive d'opération Canvas (#1774) ──────────────────────────────

/**
 * A passive probe asks only whether the exact browser-owned operation still
 * exists. It deliberately carries the same conversation/mount/revision fence
 * as the original current-generation command, but no tool input or document
 * data. A probe result is never a Canvas receipt.
 */
export const CanvasOperationProbeRequestPayloadSchema = z.object({
  probeId: z.string().min(1),
  requestId: z.string().min(1),
  ...CanvasConversationMountIdentityShape,
  expectedRevision: z.string().min(1).optional(),
}).strict();
export type CanvasOperationProbeRequestPayload = z.infer<typeof CanvasOperationProbeRequestPayloadSchema>;

const CanvasOperationProbeResultIdentityFields = {
  probeId: z.string().min(1),
  requestId: z.string().min(1),
  ...CanvasConversationMountIdentityShape,
  expectedRevision: z.string().min(1).optional(),
} as const;

/** The exact operation is still owned by the browser; keep waiting for its receipt. */
export const CanvasOperationProbeActivePayloadSchema = z.object({
  ...CanvasOperationProbeResultIdentityFields,
  status: z.literal('active'),
  phase: CanvasOperationProgressPhaseSchema,
  completedUnits: z.number().int().nonnegative().optional(),
  totalUnits: z.number().int().positive().optional(),
}).strict().superRefine(verifierProgressionCanvas);
export type CanvasOperationProbeActivePayload = z.infer<typeof CanvasOperationProbeActivePayloadSchema>;

/** The browser no longer owns this exact operation; its document effect is unknown. */
export const CanvasOperationProbeNotRunningPayloadSchema = z.object({
  ...CanvasOperationProbeResultIdentityFields,
  status: z.literal('not_running'),
}).strict();
export type CanvasOperationProbeNotRunningPayload = z.infer<typeof CanvasOperationProbeNotRunningPayloadSchema>;

/** The requested mount no longer exists on this browser connection. */
export const CanvasOperationProbeMountChangedPayloadSchema = z.object({
  ...CanvasOperationProbeResultIdentityFields,
  status: z.literal('mount_changed'),
}).strict();
export type CanvasOperationProbeMountChangedPayload = z.infer<typeof CanvasOperationProbeMountChangedPayloadSchema>;

/**
 * An intentionally closed, content-free passive observation. `active` is the
 * sole branch that can include technical liveness; none of these branches can
 * include a document receipt, selector, block identity, or action arguments.
 *
 * There is deliberately no `connection_lost` branch (removed 2026-09-29). The
 * backend accepts a probe result only on the connection that owns the
 * operation (`SessionState.resolveCanvasOperationProbe` requires
 * `pending.connectionId === input.connectionId`), so any answer that arrives
 * proves that connection is still alive: a `connection_lost` answer was
 * unreachable by construction, and no frontend emitter ever existed (the
 * registry answers `active`/`not_running`, `WebSocketPool` answers
 * `mount_changed`, a closed socket answers nothing). Real loss is observed by
 * the backend when the owning socket closes and surfaces as
 * `CanvasOperationConnectionLostError`; a silent probe times out as
 * `probe_unavailable`. Do not reintroduce the branch.
 */
export const CanvasOperationProbeResultPayloadSchema = z.union([
  CanvasOperationProbeActivePayloadSchema,
  CanvasOperationProbeNotRunningPayloadSchema,
  CanvasOperationProbeMountChangedPayloadSchema,
]);
export type CanvasOperationProbeResultPayload = z.infer<typeof CanvasOperationProbeResultPayloadSchema>;

/**
 * Browser acknowledgement for the optional probe protocol. A current frontend
 * emits it only after a current backend advertised support in
 * `connection_established`; this protects mixed Vercel/Render deployments.
 */
export const CanvasOperationProbeCapabilityPayloadSchema = z.object({
  protocolVersion: z.literal(1),
}).strict();
export type CanvasOperationProbeCapabilityPayload = z.infer<typeof CanvasOperationProbeCapabilityPayloadSchema>;

const CanvasSnapshotSummarySchema = z
  .object({
    revision: z.string().min(1),
    counts: z
      .object({ blocks: z.number().int().nonnegative(), trackedChanges: z.number().int().nonnegative(), comments: z.number().int().nonnegative() })
      .strict(),
  })
  .strict();

const ResolvedTargetSummarySchema = z
  .object({
    nodeId: z.string().min(1),
    ordinal: z.number().int().positive(),
    text: z.string().max(500),
    candidateCount: z.number().int().positive().optional(),
  })
  .strict();

const CanvasEffectSchema = z
  .object({
    kind: z.string().min(1),
    reviewability: z.enum(['tracked', 'direct_untracked', 'mixed', 'none']),
    description: z.string().min(1),
    nodeIds: z.array(z.string().min(1)).optional(),
  })
  .strict();

const VerificationCheckSchema = z
  .object({
    name: z.string().min(1),
    passed: z.boolean(),
    message: z.string().min(1).optional(),
  })
  .strict();

const CanvasInspectBlockSchema = z
  .object({
    /**
     * `public` vient de `projectMarkdown().blocks` : le `nodeId` fourni par
     * SuperDoc est alors une adresse réellement reconnue par les APIs de bloc.
     * `unavailable` est tout aussi important : le texte se lit, mais aucune
     * adresse stable ne peut être promise au modèle.
     *
     * Le champ reste optionnel pour les receipts pré-#1605. La super-refine
     * ci-dessous leur conserve le contrat historique nodeId + ordinal.
     */
    identity: z.enum(['public', 'unavailable']).optional(),
    identityUnavailableReason: z.literal('positionDerivedFallback').optional(),
    nodeId: z.string().min(1).optional(),
    ordinal: z.number().int().positive().optional(),
    nodeType: z.string().min(1),
    /**
     * Le texte du bloc, PAS un aperçu (#1008).
     *
     * Il portait auparavant le `textPreview` du fournisseur — un champ de liste
     * d'interface coupé à ~78 caractères — ce qui rendait l'agent incapable de
     * situer quoi que ce soit dans un acte. La borne de 2 000 caractères est la
     * seule troncature restante, et elle se DÉCLARE par `textTruncated`.
     */
    text: z.string().max(2000),
    /** Observed literal marker and separator; it remains part of text when rewriting the paragraph. */
    textListPrefix: CanvasTextListPrefixSchema.optional(),
    /**
     * Présent seulement quand le bloc dépasse 2 000 caractères et a été coupé.
     *
     * Son ABSENCE est un énoncé positif : « ce bloc est rendu en entier ». Sans
     * ce champ, un texte coupé se lirait comme un texte complet.
     * La continuation détaillée reste hors de `data.blocks`, dans le champ
     * top-level `inspectionTextContinuations` : les anciens backends
     * revalident ce receipt `.strict()` pendant un déploiement front-first.
     */
    textTruncated: z.literal(true).optional(),
    // Niveau de titre (H1=1, H2=2…) / de liste, émis par le snapshot frontend
    // (`canvasSnapshot.normalizeBlock`) sur les blocs de titre/liste. SANS EUX,
    // ce schéma `.strict()` rejetait tout receipt d'un plan détaillé — qui
    // contient TOUJOURS des titres — avec `unrecognized_keys: 'headingLevel'`,
    // rendant `canvas_inspect` inexploitable post-génération (conv 0bb8bfc9,
    // 2026-07-15 : l'agent a échoué 3× à lire le plan, la sonde live ne
    // couvrait pas les headings). `nonnegative` tolère un niveau 0 provider.
    headingLevel: z.number().int().nonnegative().optional(),
    listLevel: z.number().int().nonnegative().optional(),
    /**
     * Les images que ce bloc du corps porte, dans l'ordre du texte. Présent
     * seulement quand il y en a : son absence dit « aucune image lue ici ».
     * Chaque `imageId` est la cible d'`edit_image`.
     */
    images: z.array(CanvasInspectImageSchema).min(1).max(CANVAS_INSPECT_IMAGES_PER_BLOCK_MAX).optional(),
    /** Parent structure and annotations come from the same projection block map. */
    parentBlockId: z.string().min(1).optional(),
    changeIds: z.array(z.string().min(1)).max(64).optional(),
    commentIds: z.array(z.string().min(1)).max(64).optional(),
    /**
     * ⚠️ **Trois de ces quatre champs ne sont plus jamais renseignés** (relevé
     * du 2026-08-16, `tests/ws-mocked/mesure-lecture-par-bloc.spec.ts`).
     *
     * L'émetteur les lit sur l'entrée `blocks.list`, et `superdoc@2.6.0` n'y
     * remplit plus que `styleId` : `fontFamily`, `fontSize` et `alignment` sont
     * déclarés par `BlockListEntry`, PEINTS à l'écran, et jamais publiés. Un
     * agent qui lit ce receipt reçoit donc leur absence — et **un signal absent
     * n'est pas un signal négatif** : c'est « je ne sais plus lire la police »,
     * pas « ce bloc n'a pas de police ».
     *
     * Conservés et non retirés : deux sondes de référence les consomment
     * (`canvas-generation-reference-live.spec.ts` juge la taille de titre
     * dessus), et ils redeviennent vrais si le fournisseur les republie. Ce qui
     * lit vraiment ces facettes aujourd'hui, c'est le nœud SDM
     * (`doc.getNodeById`), voir `canvas-agent/effectiveStyle.ts`.
     */
    effectiveStyle: z
      .object({
        styleId: z.string().min(1).optional(),
        fontFamily: z.string().min(1).optional(),
        fontSize: z.number().positive().optional(),
        alignment: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
    // Instrument de lecture additif pour la sonde de référence pas 1 (migration
    // SuperDoc V2, spec #928, ticket #929) : les deux régressions réellement
    // survenues (largeur de table 2026-07-21, sauts de page 2026-07-21) n'ont
    // aucun signal dans `canvas_inspect` avant cette extension — seule
    // `canvas_state_request` existait, et elle SKIP les tables par construction
    // (`canvasBridge.walkParagraphs`, PR2). Lu depuis l'état ProseMirror de
    // l'éditeur (`node.attrs.grid` / `tableProperties.tableWidth` /
    // `paragraphProperties.pageBreakBefore`, cf. CLAUDE.md
    // `features/redaction/CLAUDE.md` "Largeur des colonnes de table" et "Sauts
    // de page"), jamais du DOM. La surface de lecture définitive du canvas V2
    // (table LLM-safe, allocations, etc.) est portée par #940 ; cette extension
    // se limite au strict nécessaire de la sonde.
    pageBreakBefore: z.boolean().optional(),
    table: z
      .object({
        columnWidthsTwips: z.array(z.number().int().positive()),
        widthPercent: z.number().positive().nullable(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((block, context) => {
    if (block.identity === 'public' && block.nodeId === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nodeId'],
        message: 'A projection block with identity="public" requires its public nodeId.',
      });
    }
    if (block.identity === 'unavailable') {
      if (block.identityUnavailableReason === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identityUnavailableReason'],
          message: 'A projection block without a public identity must state why it is unavailable.',
        });
      }
      if (block.nodeId !== undefined || block.ordinal !== undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['identity'],
          message: 'A projection block with identity="unavailable" must not carry a guessed nodeId or ordinal.',
        });
      }
    }
    if (block.identity === undefined && (block.nodeId === undefined || block.ordinal === undefined)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['identity'],
        message: 'A legacy inspection block requires nodeId and ordinal when projection identity is absent.',
      });
    }
  });

/**
 * Une révision suivie, telle que `canvas_inspect` la livre au modèle (#994).
 *
 * ## Ce que ce schéma répare
 *
 * `accept_tracked_changes` et `reject_tracked_changes` exigent des identifiants
 * de révision. **Aucune surface ne les publiait** : les deux verbes étaient
 * inatteignables depuis leur mise en service, pendant que le front lisait la
 * liste des révisions deux fois par action pour n'en garder qu'un compte. Le
 * domaine `trackedChanges` de `canvas_inspect` livre désormais de quoi décider.
 *
 * ## Les champs sont RELEVÉS, pas devinés
 *
 * Mesuré le 2026-08-16 sur `superdoc@2.6.0`
 * (`tests/ws-mocked/mesure-994-formes-de-revision.spec.ts`) :
 *
 * - `type` / `subtype` : `structural`/`paragraph-insertion`,
 *   `replacement`/`text-replacement`, `deletion`/`text-deletion`… Chaînes
 *   LIBRES et non un enum : l'énumération du fournisseur n'est pas fermée, et
 *   une valeur inconnue ferait rejeter le receipt ENTIER — c'est-à-dire perdre
 *   la lecture du document pour cause de vocabulaire.
 * - ⚠️ `excerpt` **n'est pas toujours un extrait du document** : sur une
 *   insertion de paragraphe il vaut le libellé `"paragraph insertion"`. C'est
 *   pour cela qu'`insertedText` et `deletedText` sont publiés à côté, et que le
 *   front n'en fabrique aucune phrase de synthèse : le modèle lit les trois.
 * - `blockId` vient de `navigationTarget.blockId` — l'adresse de bloc faisant
 *   autorité pour la navigation de revue. C'est elle qui relie une révision aux
 *   blocs que le modèle vient de lire. Optionnelle : son absence ne dit jamais
 *   « cette révision n'est nulle part ».
 */
const CanvasTrackedChangeSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1).optional(),
    subtype: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    author: z.string().min(1).optional(),
    date: z.string().min(1).optional(),
    excerpt: z.string().max(400).optional(),
    insertedText: z.string().max(400).optional(),
    deletedText: z.string().max(400).optional(),
    blockId: z.string().min(1).optional(),
  })
  .strict();

/**
 * Un commentaire tel que l'inspection le publie au modèle (#996).
 *
 * ⚠️ **`commentId` est le seul champ obligatoire, et c'est tout le sujet** :
 * c'est lui que `resolve_comments` et `reply_to_comment` réclament, et son
 * absence rendait les deux verbes inappelables. Tout le reste est optionnel —
 * une lecture partielle vaut mieux qu'un receipt rejeté.
 *
 * ⚠️ **`status` est fermé ici (`open` / `resolved`) alors que le fournisseur
 * ÉCRIT `resolved` / `active`.** Les deux vocabulaires ne coïncident pas
 * (mesuré) : relire `open` et le réécrire tel quel est refusé. Le modèle
 * n'apprend donc qu'un seul mot — celui qu'il lit — et la traduction vers le
 * vocabulaire d'écriture reste dans l'exécuteur.
 *
 * `anchoredText` est l'extrait du document sur lequel le commentaire est posé :
 * sans lui, le modèle sait qu'un commentaire existe sans savoir de QUOI il
 * parle, et devrait relire le bloc pour le deviner.
 */
const CanvasCommentSchema = z
  .object({
    commentId: z.string().min(1),
    text: z.string().max(1_000).optional(),
    status: z.enum(['open', 'resolved']).optional(),
    author: z.string().min(1).optional(),
    replyCount: z.number().int().nonnegative().optional(),
    /** L'extrait du document que ce commentaire vise. */
    anchoredText: z.string().max(400).optional(),
    /** Le bloc porteur, quand la cible tient dans un seul. */
    blockId: z.string().min(1).optional(),
    /** Présent sur une RÉPONSE, absent sur la racine d'un fil. */
    parentCommentId: z.string().min(1).optional(),
  })
  .strict();

/**
 * Une note de bas de page telle que l'inspection la publie au modèle
 * (relevé 2026-08-26, §9 de `superdoc-document-api.md`).
 *
 * ⚠️ **`noteId` est le seul champ obligatoire, et c'est tout le sujet** — même
 * raisonnement qu'`commentId` en #996 : c'est lui que `edit_footnote` et
 * `delete_footnote` réclament, et sans surface qui le publie, les deux verbes
 * seraient inappelables. Un identifiant n'atteint le modèle QUE par
 * `canvas_inspect`, jamais par le receipt d'une action. Tout le reste est
 * optionnel : une lecture partielle vaut mieux qu'un receipt rejeté.
 *
 * `displayNumber` est une CHAÎNE et non un nombre : c'est le renvoi tel qu'il
 * est peint (`1`, `i`, `*`), pas un rang — le fournisseur le rend déjà formaté
 * par le vocabulaire de numérotation du document.
 *
 * `type` porte la distinction note de bas de page / note de fin : le
 * fournisseur n'a pas d'opération `endnote` distincte, le type voyage en
 * paramètre.
 */
const CanvasFootnoteSchema = z
  .object({
    noteId: z.string().min(1),
    /** Le renvoi tel qu'il est peint, déjà formaté par le fournisseur. */
    displayNumber: z.string().min(1).optional(),
    text: z.string().max(1_000).optional(),
    type: z.enum(['footnote', 'endnote']).optional(),
  })
  .strict();

/**
 * Une zone d'en-tête ou de pied de page, telle que l'inspection la publie
 * (relevé 2026-09-05, `tests/ws-mocked/mesure-en-tetes-et-pieds.spec.ts`).
 *
 * ⚠️ **`status` distingue trois états que rien d'autre ne distingue** :
 * - `explicit` — cette section porte sa propre partie d'en-tête. C'est la seule
 *   qu'un `set_header_footer` sur cette section modifiera en place.
 * - `inherited` — la zone est PEINTE, mais son contenu appartient à une section
 *   antérieure. Y écrire matérialise une copie locale : la section précédente
 *   n'est PAS touchée.
 * - `none` — rien n'est peint dans cette zone.
 *
 * ⚠️ **`has_image` est la seule chose qui distingue un logo d'une ligne
 * blanche**, et c'est mesuré : un paragraphe qui ne porte QU'UNE image se relit
 * `text:''`. Le papier à en-tête du cabinet vit exactement là. Un
 * `has_image:true` avec `text:''` veut donc dire « il y a quelque chose de
 * visible ici », jamais « cette zone est vide ».
 *
 * ⚠️ **`is_empty` compte les BLOCS, pas les caractères** (corrigé 2026-09-06).
 * Un paragraphe qui ne porte qu'un CHAMP de numéro de page se relit lui aussi
 * `text:''`, et sans être ancré à aucune image : le juger sur le seul texte
 * faisait publier `is_empty:true` sur un pied de page qui affichait « Page / »
 * à l'écran. L'agent en concluait qu'il n'y avait rien à retirer, et l'annonçait
 * à l'avocat. Un bloc listé mais illisible reste un bloc.
 */
const CanvasHeaderFooterZoneSchema = z
  .object({
    status: z.enum(['explicit', 'inherited', 'none']),
    /** Les paragraphes concaténés, bornés — de quoi reconnaître la zone, pas la rejouer. */
    text: z.string().max(1_000),
    paragraphs: z
      .array(
        z
          .object({
            text: z.string().max(500),
            alignment: z.enum(['left', 'center', 'right', 'justify']).optional(),
          })
          .strict(),
      )
      .max(30),
    /** `true` seulement quand la lecture d'images a CONFIRMÉ une image ancrée dans la zone. */
    has_image: z.boolean(),
    /** Aucun paragraphe ET aucune image : la zone ne porte rien. Voir le ⚠️ ci-dessus. */
    is_empty: z.boolean(),
  })
  .strict();
export type CanvasHeaderFooterZone = z.infer<typeof CanvasHeaderFooterZoneSchema>;

const CanvasHeaderFooterZonesSchema = z
  .object({
    primary: CanvasHeaderFooterZoneSchema,
    first_page: CanvasHeaderFooterZoneSchema,
    even_pages: CanvasHeaderFooterZoneSchema,
  })
  .strict();

/**
 * Une section du document et ses six zones.
 *
 * `different_first_page` et `odd_even` sont les deux réglages OOXML sans
 * lesquels une zone écrite n'est **jamais peinte** : `w:titlePg` pour
 * `first_page`, `w:evenAndOddHeaders` pour `even_pages`. Ils valent `null`
 * quand la lecture ne les publie pas — « je ne sais pas », jamais « faux »,
 * même doctrine que `documentMode` et `executableActions`.
 */
const CanvasHeaderFooterSectionSchema = z
  .object({
    section_index: z.number().int().nonnegative(),
    different_first_page: z.boolean().nullable(),
    odd_even: z.boolean().nullable(),
    headers: CanvasHeaderFooterZonesSchema,
    footers: CanvasHeaderFooterZonesSchema,
  })
  .strict();
export type CanvasHeaderFooterSection = z.infer<typeof CanvasHeaderFooterSectionSchema>;

const CanvasHeadersFootersSchema = z
  .object({ sections: z.array(CanvasHeaderFooterSectionSchema).max(20) })
  .strict();
export type CanvasHeadersFooters = z.infer<typeof CanvasHeadersFootersSchema>;

/**
 * Une des quatre parties d'une section, relue après une écriture de
 * `set_header_footer` : son statut et un extrait borné de son texte.
 *
 * `text` vide ne dit pas « partie vide » : une image ou un champ (numéro de
 * page) ne se relisent pas en texte. `canvas_inspect` avec le domaine
 * `headersFooters` donne la zone entière.
 */
const CanvasHeaderFooterPartAfterSchema = z
  .object({
    status: z.enum(['explicit', 'inherited', 'none']),
    text: z.string().max(200),
  })
  .strict();

const CanvasHeaderFooterPartsAfterOfKindSchema = z
  .object({
    /** La zone `first_page` : ce que la page 1 montre quand `first_page_distinct` est vrai. */
    first_page: CanvasHeaderFooterPartAfterSchema,
    /** La zone `primary` : les pages qui n'ont pas de zone à elles. */
    other_pages: CanvasHeaderFooterPartAfterSchema,
  })
  .strict();

/**
 * Ce que l'écriture a fait de l'AUTRE partie (le pied quand on écrit l'en-tête,
 * et inversement). Activer la première page distincte ou les pages paires est
 * un réglage de SECTION : il vaut pour les deux parties.
 *
 * - `untouched` : l'autre partie montre ce qu'elle montrait.
 * - `kept_by_copy` : la zone que le réglage vient d'activer a reçu une copie de
 *   la zone `primary`, et cette copie, relue dans le fichier exporté, porte le
 *   texte, les champs et l'alignement de sa source.
 * - `copy_unverified` : la copie est faite, mais la relecture n'a pas pu la
 *   comparer à sa source ; `reason` dit pourquoi. Le reçu est alors `partial`.
 * - `changed` : les pages concernées ne montrent plus ce qu'elles montraient ;
 *   `reason` dit pourquoi. Le reçu est alors `partial`.
 */
const RAISONS_DE_L_AUTRE_PARTIE = {
  untouched: [],
  kept_by_copy: [],
  /**
   * `source_has_image` : la source porte une image, que la relecture ne compare pas.
   * `source_not_comparable` : la source porte un tableau, une zone de texte ou un contrôle de contenu.
   * `file_unreadable` : le fichier de l'acte n'a pas pu être exporté, ou une des deux parties n'y est pas.
   */
  copy_unverified: ['source_has_image', 'source_not_comparable', 'file_unreadable'],
  /**
   * `copy_unavailable` : l'éditeur n'expose pas la copie d'une partie.
   * `copy_refused` : l'éditeur a refusé la copie ou son rattachement.
   * `zone_already_present` : l'autre partie portait déjà sa propre zone, que
   * le réglage affiche désormais à la place de la zone `primary`.
   * `copy_differs` : la copie relue dans le fichier n'a pas le texte, les champs ou l'alignement de sa source.
   */
  changed: ['copy_unavailable', 'copy_refused', 'zone_already_present', 'copy_differs'],
} as const satisfies Record<string, readonly string[]>;

const CanvasHeaderFooterOtherPartSchema = z
  .object({
    part: z.enum(['header', 'footer']),
    outcome: z.enum(['untouched', 'kept_by_copy', 'copy_unverified', 'changed']),
    reason: z.enum([...RAISONS_DE_L_AUTRE_PARTIE.copy_unverified, ...RAISONS_DE_L_AUTRE_PARTIE.changed]).optional(),
  })
  .strict()
  .superRefine((valeur, ctx) => {
    const admises: readonly string[] = RAISONS_DE_L_AUTRE_PARTIE[valeur.outcome];
    const tenue = valeur.reason === undefined ? admises.length === 0 : admises.includes(valeur.reason);
    if (!tenue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: admises.length === 0
          ? `headerFooter.other_part.reason is forbidden when outcome is "${valeur.outcome}"`
          : `headerFooter.other_part.reason must be one of ${admises.join('|')} when outcome is "${valeur.outcome}" (got ${String(valeur.reason)})`,
      });
    }
  });
export type CanvasHeaderFooterOtherPart = z.infer<typeof CanvasHeaderFooterOtherPartSchema>;

/** Un réglage de page tel que le reçu le nomme : une marge par côté, l'orientation, le format. */
export const CANVAS_PAGE_LAYOUT_SETTING_KEYS = ['marginTopCm', 'marginBottomCm', 'marginLeftCm', 'marginRightCm', 'orientation', 'paperSize'] as const;
export type CanvasPageLayoutSettingKey = (typeof CANVAS_PAGE_LAYOUT_SETTING_KEYS)[number];

/** Valeurs lues rendues pour un réglage différent : au plus une par section distincte, bornées. */
export const CANVAS_PAGE_LAYOUT_READ_MAX = 5;

const CanvasPageLayoutSettingValueSchema = z.union([z.number(), z.string().min(1).max(40)]);

const CanvasPageLayoutReceiptSchema = z
  .object({
    /** Les sections du document : toutes reçoivent les réglages. */
    sections: z.number().int().nonnegative(),
    /** Les écritures faites, une entrée d'historique chacune : ce qu'`undo_changes` doit défaire. Un réglage déjà en place n'en fait pas. */
    writes: z.number().int().nonnegative(),
    settings: z
      .array(
        z
          .object({
            setting: z.enum(CANVAS_PAGE_LAYOUT_SETTING_KEYS),
            /** La valeur demandée : centimètres pour une marge, sinon le mot du schéma. */
            requested: CanvasPageLayoutSettingValueSchema,
            outcome: CanvasFormatSettingOutcomeSchema,
            /** Réglage différent : les valeurs lues dans les sections, sans doublon. Un format hors liste se lit « 21 x 29.7 cm ». */
            read: z.array(CanvasPageLayoutSettingValueSchema).max(CANVAS_PAGE_LAYOUT_READ_MAX).optional(),
          })
          .strict(),
      )
      .max(CANVAS_PAGE_LAYOUT_SETTING_KEYS.length),
  })
  .strict();
export type CanvasPageLayoutReceipt = z.infer<typeof CanvasPageLayoutReceiptSchema>;

/**
 * Ce que `rewrite_block` a fait, relu après l'écriture. Facultatif au contrat
 * (un onglet resté sur un bundle d'avant le 2026-10-09 ne l'émet pas) ;
 * l'exécuteur l'envoie toujours.
 */
const CanvasRewriteReceiptSchema = z
  .object({
    /** Les paragraphes demandés. */
    requested: z.number().int().nonnegative(),
    /** Ceux que l'appel a écrits et qui portent, relus, le texte demandé. */
    written: z.number().int().nonnegative(),
    /**
     * Ceux qui portaient déjà le texte demandé, avec les marques qu'il porte : rien n'y est écrit, ni texte ni forme —
     * réécrire un texte identique donnerait à tout le paragraphe la forme de son premier passage.
     */
    unchanged: z.number().int().positive().optional(),
    /** Les écritures faites, une entrée d'historique chacune : ce qu'`undo_changes` doit défaire. */
    writes: z.number().int().nonnegative(),
    /**
     * Celles de ces écritures qui ne se défont pas sûrement « par le compte » : toutes quand l'historique de l'éditeur
     * a gagné autre chose pendant l'appel (l'avocat a tapé), les premières quand il n'en garde plus la trace. Même
     * règle et même sens que `format.writesNotUndoable`.
     */
    writesNotUndoable: z.number().int().positive().optional(),
    /**
     * Les paragraphes qui ne sont pas réécrits, chacun avec sa cause et son issue — TOUS, la liste d'un appel étant
     * bornée : celui qui porte ce qu'une réécriture entière détruirait (appel de note, champ, lien, saut de ligne…),
     * refusé avant toute écriture, et celui que l'éditeur refuse.
     */
    skipped: z.array(z.object({ nodeId: z.string().min(1), reason: z.string().min(1).max(600) }).strict()).max(CANVAS_REWRITE_BLOCKS_MAX).optional(),
    /**
     * La forme de caractère posée sur le texte réécrit — les marques `**`, `*`, `__` du texte, et la forme que le
     * texte non marqué garde —, relue au fichier exporté : même règle et mêmes mots que le reçu d'une écriture de
     * forme. Absent : aucun paragraphe écrit n'en demandait.
     */
    textFormatting: z
      .object({
        /** Les paragraphes écrits dont la forme de caractère était à poser ou à garder. */
        paragraphs: z.number().int().positive(),
        outcome: CanvasFormatSettingOutcomeSchema,
        /** Les paragraphes où la forme relue n'est pas celle demandée, ou ne se lit pas : tous. */
        differentIn: z.array(z.string().min(1)).max(CANVAS_REWRITE_BLOCKS_MAX).optional(),
      })
      .strict()
      .optional(),
    /**
     * Ce que des paragraphes réécrits portaient sur un passage de leur ancien texte, lu au fichier d'avant l'écriture,
     * et que le texte neuf ne porte pas alors qu'il contient encore ce passage : une ligne par paragraphe, TOUS les
     * paragraphes concernés de l'appel et TOUS leurs réglages — `nodeId: réglage=valeur on "texte", "texte"; …`,
     * réglages nommés comme ceux de `format_text`, chaque texte cité tel que le texte neuf le porte (la cible exacte de
     * `format_text`) et coupé à 80 caractères. La forme hors de ces réglages (petites capitales, barré, exposant, style
     * de caractère) y est dite `now on ALL the text` ou `lost`. Présent, il rend le reçu `partial`, sous l'erreur
     * `CANVAS_REWRITE_FORMATTING_NOT_CARRIED` (décision du porteur, 2026-10-11 : nommé dans le seul effet d'un reçu
     * `ok`, il n'était ni repris ni dit à l'avocat) : l'agent repose chaque ligne par `format_text`.
     *
     * WHY tous : sur un modèle d'avocat, 51 paragraphes portaient une forme partielle. Une liste coupée à 20 laissait
     * l'agent sans moyen de reposer les autres — la décision du porteur (« le reçu le dit ») n'était pas tenue.
     */
    formattingNotCarried: z.array(z.string().min(1).max(6000)).max(CANVAS_REWRITE_BLOCKS_MAX).optional(),
    /**
     * Ce que des paragraphes réécrits portaient sur un passage que leur texte neuf ne contient plus : mêmes lignes que
     * `formattingNotCarried` (`nodeId: réglage=valeur on "texte"`), une par paragraphe. Pour information : la forme est
     * partie avec son texte, il n'y a rien à reposer, et ce champ ne rend pas le reçu `partial`.
     *
     * WHY un champ distinct : rangées avec les pertes, ces lignes faisaient enchaîner à l'agent des `format_text` sur
     * un texte qu'il venait de retirer (un montant en gras absent du paragraphe neuf).
     */
    formattingDropped: z.array(z.string().min(1).max(6000)).max(CANVAS_REWRITE_BLOCKS_MAX).optional(),
    /**
     * Les paragraphes réécrits qui portaient l'ancre d'un commentaire : le commentaire reste, son ancre est maintenant
     * en fin de paragraphe et n'entoure plus le texte commenté (relevé du 2026-10-09, §17.6 de `superdoc-document-api.md`).
     */
    commentAnchorsMoved: z.array(z.string().min(1)).min(1).max(CANVAS_REWRITE_BLOCKS_MAX).optional(),
  })
  .strict();
export type CanvasRewriteReceipt = z.infer<typeof CanvasRewriteReceiptSchema>;

/** Nombre maximal de paragraphes retirés avec leur numéro de page que le reçu de `set_header_footer` cite ; le reste est compté. */
export const CANVAS_REMOVED_PARAGRAPHS_MAX = 20;

/**
 * Ce que `set_header_footer` a fait des numéros de page d'une zone, jugé au
 * fichier exporté — même règle et mêmes mots que le reçu d'une écriture de
 * forme (`CanvasFormatSettingOutcomeSchema`) : `conforming` (pose : chaque
 * paragraphe écrit porte ses champs, dans l'ordre ; retrait : la zone n'en porte
 * plus et garde au moins un paragraphe), `different`, `unreadable`.
 */
const CanvasHeaderFooterPageNumbersSchema = z
  .object({
    requested: z.enum(['set', 'remove']),
    outcome: CanvasFormatSettingOutcomeSchema,
    /** Les champs de la zone après l'appel, dans l'ordre du fichier. `null` : le fichier n'a pas pu être relu. */
    fields_after: z.array(CanvasPageNumberFieldSchema).max(CANVAS_HEADER_FOOTER_BLOCKS_MAX).nullable(),
    /**
     * Retrait : pour chaque paragraphe retiré avec son numéro, le texte qu'il portait EN PLUS du numéro, borné — ce que
     * l'avocat perd avec la ligne, que l'agent lui redit. Chaîne vide : le paragraphe ne portait que le numéro.
     */
    removed_paragraphs: z.array(z.string().max(200)).max(CANVAS_REMOVED_PARAGRAPHS_MAX).optional(),
    /** Les paragraphes retirés avec leur numéro au-delà de ceux que `removed_paragraphs` cite. Absent : tous sont cités. */
    removed_paragraphs_not_listed: z.number().int().positive().optional(),
  })
  .strict();
export type CanvasHeaderFooterPageNumbers = z.infer<typeof CanvasHeaderFooterPageNumbersSchema>;

const CanvasHeaderFooterPartsAfterSchema = z
  .object({
    /** `null` : l'éditeur ne publie pas le réglage — « non lisible », jamais « faux ». */
    first_page_distinct: z.boolean().nullable(),
    header: CanvasHeaderFooterPartsAfterOfKindSchema,
    footer: CanvasHeaderFooterPartsAfterOfKindSchema,
  })
  .strict();
export type CanvasHeaderFooterPartsAfter = z.infer<typeof CanvasHeaderFooterPartsAfterSchema>;

const CanvasActionErrorInputSchema = z.union([
  CanvasActionInputSchema,
  CanvasSelectorSchema,
  trackedChangesTargetSchema,
  z.object({ find: z.string().min(1), replace: z.string() }).strict(),
]);

const CanvasActionErrorSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
    /** Stable plan step responsible for this error, when the action is a plan. */
    stepId: z.string().min(1).optional(),
    input: CanvasActionErrorInputSchema.optional(),
    nextAction: z.string().min(1).optional(),
  })
  .strict();

/**
 * La conduite à tenir après un receipt, dite en STRUCTURE et non en prose.
 *
 * Un agent lit d'abord les champs, la prose ensuite — et parfois pas du tout.
 * Une consigne qui n'existe que dans le texte du message (« NE RELANCE PAS »)
 * dépend donc de la bonne volonté du modèle ; portée par ce champ, elle se lit
 * sans interprétation.
 *
 * Les quatre issues sont exclusives et disent des choses très différentes :
 * `reinspect` (relis l'état, il a bougé), `retry` (la même demande peut
 * aboutir), `rollback` (défais ce que tu viens de faire), `stop` (rien ne se
 * dénouera de soi-même — arrête ce geste et parle à l'avocat).
 *
 * ⚠️ **Exporté depuis le 2026-08-29** : le contrat vivait en privé ici, au
 * service du seul `CanvasActionReceiptCommonSchema`. Les refus émis AVANT toute
 * exécution (`canvasResultFailureSchema`, `index.ts`) en ont le même besoin, et
 * une seconde notion de « recovery » posée à côté aurait laissé deux
 * vocabulaires diverger. Un seul mot pour une seule chose.
 */
export const RecoveryInstructionSchema = z
  .object({
    action: z.enum(['reinspect', 'retry', 'rollback', 'stop']),
    reason: z.string().min(1),
  })
  .strict();
export type RecoveryInstruction = z.infer<typeof RecoveryInstructionSchema>;

/**
 * Le mode d'édition du document monté, tel que SuperDoc le tient sur
 * `instance.config.documentMode`.
 *
 * ## Pourquoi ce vocabulaire existe (autopsie du 2026-08-27, conv CD3 `76a7101d`)
 *
 * L'écriture d'un acte a été refusée par le fournisseur avec
 * `PERMISSION_DENIED: insert is unavailable while the document is in read-only
 * review mode.` — et **rien, nulle part, ne publiait le mode du document** :
 * ni l'inspection, ni l'annonce de montage, ni la trace backend. L'agent ne
 * l'apprenait qu'en s'y cognant, donc il ne pouvait pas le dire à l'avocat, et
 * un enquêteur relisant la conversation ne pouvait pas davantage l'établir
 * après coup.
 *
 * ## `unknown` est une valeur, pas un trou
 *
 * Le champ qui la porte est OPTIONNEL au contrat — un navigateur plus ancien
 * n'en publie aucun. Il faut donc distinguer trois états, pas deux :
 * champ absent = « cet hôte ne sait pas encore l'annoncer » ; `unknown` = « cet
 * hôte a REGARDÉ et n'a pas su lire » ; `editing` = « le document est
 * inscriptible ». Replier le mode illisible sur `editing` ferait affirmer
 * l'inscriptibilité sur une lecture ratée — c'est exactement la doctrine
 * constante de ce module : un signal absent dit « je ne sais pas », jamais
 * « ça vaut zéro » (cf. `executableActions` et `effectiveStyle` ci-dessous).
 */
const CanvasDocumentModeSchema = z.enum(['editing', 'suggesting', 'viewing', 'unknown']);
export type CanvasDocumentMode = z.infer<typeof CanvasDocumentModeSchema>;

/**
 * Résumé LLM-safe de la projection détaillée native SuperDoc.
 *
 * Les messages libres de diagnostic ne sont pas reproduits : ils sont utiles
 * à l'intégrateur mais peuvent gonfler la lecture d'un acte sans rendre une
 * décision plus sûre. Les codes, sévérités et indicateurs de perte suffisent à
 * dire si l'agent doit demander une lecture ciblée avant de raisonner dessus.
 */
const CanvasProjectionDiagnosticSchema = z
  .object({
    code: z.string().min(1).max(120),
    severity: z.enum(['error', 'warning', 'info']),
    lossy: z.boolean(),
  })
  .strict();

export const CanvasInspectionProjectionReceiptSchema = z
  .object({
    mode: CanvasInspectionProjectionSchema,
    evaluatedRevision: z.string().min(1),
    status: z.enum(['success', 'warning']),
    outcome: z.enum(['preserved', 'preserved-with-warnings', 'simplified', 'no-op']),
    lossy: z.boolean(),
    diagnostics: z.array(CanvasProjectionDiagnosticSchema).max(32),
  })
  .strict();
export type CanvasInspectionProjectionReceipt = z.infer<typeof CanvasInspectionProjectionReceiptSchema>;

const CanvasInspectReceiptBaseSchema = z
  .object({
    requestId: z.string().min(1),
    documentSessionId: z.string().min(1),
    snapshotToken: z.string().min(1),
    revision: z.string().min(1),
    /**
     * Vue effectivement projetée par SuperDoc, indépendante du mode d'édition
     * de l'avocat. Sa révision est reliée à celle du receipt par le refine en
     * sortie du schéma : aucun texte projeté ne peut être associé à un jeton
     * de mutation d'une autre version du document.
     */
    /**
     * Optionnel uniquement pour pouvoir LIRE un receipt émis avant #1605.
     * Son absence ne peut jamais armer `canvas_perform_action` : le broker
     * exige une projection finale native avant de publier une portée de blocs
     * mutable. Ce contrat de parsing n'est pas une compatibilité d'écriture.
     */
    projection: CanvasInspectionProjectionReceiptSchema.optional(),
    /**
     * Le mode d'édition du document AFFICHÉ, au moment de l'inspection.
     *
     * ⚠️ **Optionnel au contrat, TOUJOURS émis par ce frontend.** L'option
     * n'existe que pour ne pas casser un navigateur plus ancien resté sur une
     * version antérieure du bundle : son absence dit « cet hôte ne publie pas
     * encore le mode », et n'autorise à conclure NI que le document est
     * inscriptible, NI qu'il ne l'est pas. Le broker conserve alors le reçu
     * pour lecture mais refuse d'armer toute mutation jusqu'à une inspection
     * complète. Le mode réellement illisible, lui,
     * s'écrit `unknown` — voir {@link CanvasDocumentModeSchema}.
     *
     * ⚠️ **Ce `.describe()` n'atteint PAS le modèle.** Seuls les schémas
     * d'ENTRÉE (`CanvasInspectInputSchema`, `CanvasActionInputSchema`) sont
     * convertis en JSON Schema pour le fournisseur ; un receipt, lui, part au
     * modèle en JSON brut, sans son contrat. Ce que le modèle doit COMPRENDRE
     * de ces quatre valeurs est donc écrit dans `INSPECT_DESCRIPTION`
     * (`apps/backend/services/hector-agent-case-discovery-v3/llm/CanvasToolDefinitions.ts`),
     * qui est le prompt qu'il lit au moment d'appeler. Les deux textes se
     * modifient ensemble.
     */
    documentMode: CanvasDocumentModeSchema.optional().describe(
      "Mode d'édition du document affiché au moment de l'inspection. "
      + "editing : le document est inscriptible, une action canvas écrit directement. "
      + "suggesting : le document est inscriptible, mais chaque écriture devient une modification suivie que l'avocat devra accepter ou refuser. "
      + "viewing : le document est en LECTURE SEULE — il n'est PAS inscriptible, et toute écriture sera refusée par le fournisseur (PERMISSION_DENIED, insert is unavailable while the document is in read-only review mode) ; il faut demander à l'avocat de quitter la lecture seule, jamais réessayer à l'identique. "
      + "unknown : le mode n'a pas pu être lu — ce n'est PAS editing, et rien ne doit être conclu de l'inscriptibilité du document. "
      + "Champ absent : ce navigateur ne publie pas encore le mode, ce qui ne dit rien non plus.",
    ),
    // Identité métier du document monté (uuid table documents + titre) — permet au modèle
    // de relier « le doc du canvas » au dossier même quand le load vient de l'UI, pas du
    // tool (incident 06eed659 2026-07-13 : sans elle, le modèle confabulait « c'est un PDF »).
    // Optionnel : un canvas généré (page blanche/SSE) n'a pas encore de document backend.
    document: z
      .object({ id: z.string().nullable(), title: z.string().nullable() })
      .strict()
      .optional(),
    counts: z.object({ blocks: z.number().int().nonnegative(), blocksTotalExact: z.boolean(), trackedChanges: z.number().int().nonnegative(), comments: z.number().int().nonnegative() }).strict(),
    blocks: z.array(CanvasInspectBlockSchema),
    /**
     * Ce qu'un `selector` d'inspection a rendu (#1009).
     *
     * Publié **seulement** quand un `selector` a été demandé. Le champ absent
     * dit « aucun sélecteur, `blocks` est la fenêtre brute du document », jamais
     * « le sélecteur n'a rien trouvé » — ce dernier cas s'écrit
     * `matchCount: 0`.
     *
     * `matchCount` porte sur le document **entier** (le balayage est intégral,
     * paginé par 250), `returnedCount` sur ce qui a survécu au fenêtrage. Les
     * deux sont nécessaires : sans `matchCount`, un appariement coupé par la
     * fenêtre serait indiscernable d'un appariement exhaustif, et une action
     * destructive partirait sur une cible que l'agent croit unique.
     */
    selectorResolution: z
      .object({
        matchCount: z.number().int().nonnegative(),
        returnedCount: z.number().int().nonnegative(),
        blockOffset: z.number().int().nonnegative(),
        blockLimit: z.number().int().positive(),
      })
      .strict()
      .optional(),
    /**
     * Verbes que CE document sait exécuter, d'après `capabilities.get()` (#987).
     *
     * ⚠️ **Un verbe absent de cette liste ne doit pas être tenté** : son
     * opération n'existe pas sur ce runtime, donc le retenter échouera à
     * l'identique. C'est ce qui remplace la découverte par refus au tour
     * suivant — `undo_changes` a vécu ainsi toute sa mise en service.
     *
     * **Optionnel, et l'absence du champ ne veut PAS dire « aucun verbe »** :
     * elle veut dire que les capacités n'ont pas pu être lues (surface absente,
     * lecture en échec). Traiter une lecture ratée comme une interdiction
     * couperait l'agent sur un défaut de diagnostic.
     */
    executableActions: CanvasAnnouncedActionNamesSchema.optional(),
    /**
     * Les révisions suivies du document, avec de quoi les DÉCIDER (#994).
     *
     * Publié quand `includeDomains` contient `trackedChanges`, et seulement
     * alors : sur un acte relu par un confrère, la liste peut compter des
     * centaines d'entrées portant chacune un extrait — les servir à chaque
     * inspection coûterait la fenêtre de contexte du modèle pour une donnée
     * dont il n'a besoin qu'au moment de la revue. `counts.trackedChanges`,
     * lui, est toujours là : c'est ce compte qui apprend au modèle qu'il y a
     * quelque chose à demander.
     *
     * ⚠️ **L'absence du champ ne veut pas dire « aucune révision »** : elle veut
     * dire que le domaine n'a pas été demandé. Le seul énoncé d'absence de
     * révision est `counts.trackedChanges === 0`.
     */
    trackedChanges: z.array(CanvasTrackedChangeSchema).optional(),
    /**
     * Les commentaires du document, quand le domaine `comments` est demandé
     * (#996) — **même réparation que `trackedChanges` en #994**, et pour la
     * même cause exactement.
     *
     * Avant ce ticket, l'inspection ne publiait qu'un `counts.comments`,
     * c'est-à-dire un NOMBRE. Or `resolve_comments` et `reply_to_comment`
     * exigent tous deux un `commentId` : sans cette surface, ils seraient
     * **inappelables** — le défaut que #994 venait de refermer pour les
     * révisions, refait quinze jours plus tard sur les commentaires.
     *
     * ⚠️ Même contrat d'absence : le champ absent veut dire « le domaine n'a
     * pas été demandé », jamais « il n'y a aucun commentaire ». Le seul énoncé
     * d'absence est `counts.comments === 0`.
     *
     * ⚠️ `status` est un enum FERMÉ à la lecture (`open` / `resolved`), et il ne
     * coïncide PAS avec le vocabulaire d'écriture du fournisseur, qui exige
     * `resolved` ou `active` — mesuré le 2026-08-16 : réécrire `open` tel qu'on
     * vient de le lire est refusé. La traduction vit dans l'exécuteur, jamais
     * dans le contrat exposé au modèle, à qui on n'apprend qu'un seul mot.
     */
    comments: z.array(CanvasCommentSchema).optional(),
    /**
     * Les notes de bas de page du document, quand le domaine `footnotes` est
     * demandé — **troisième occurrence de la même réparation**, après les
     * révisions (#994) et les commentaires (#996).
     *
     * ⚠️ **Sa DÉCLARATION ici est load-bearing indépendamment de son contenu** :
     * `CanvasInspectReceiptSchema` est `.strict()`, donc un front qui émettrait
     * `footnotes` sans que le champ soit déclaré ferait échouer le parse de
     * TOUTE l'inspection — pas seulement la perte des notes, mais la perte des
     * blocs, du revision token et du reste du receipt.
     *
     * ⚠️ Même contrat d'absence que ses deux aînés : le champ absent veut dire
     * « le domaine n'a pas été demandé », jamais « il n'y a aucune note ».
     *
     * ⚠️ Et une réserve propre aux notes (mesurée 2026-08-26) : sous régime
     * suivi, une note supprimée RESTE listée jusqu'à ce que l'avocat accepte la
     * révision. Cette liste dit ce que le document PORTE, pas ce que l'avocat
     * VERRA une fois la revue faite.
     */
    footnotes: z.array(CanvasFootnoteSchema).optional(),
    /**
     * Les en-têtes et pieds de page, quand le domaine `headersFooters` est
     * demandé — **quatrième occurrence de la même réparation**, après les
     * révisions (#994), les commentaires (#996) et les notes.
     *
     * ⚠️ **Ce domaine est LU SUR DEMANDE, et lui seul l'est** : les trois autres
     * sont déjà relus à chaque instantané pour alimenter `counts`, donc les
     * publier ne coûte que des jetons. Celui-ci coûte des APPELS au fournisseur
     * — une résolution et une lecture de blocs par zone, soit une douzaine
     * d'aller-retours par section. Les servir à chaque inspection ralentirait
     * toutes les autres.
     *
     * ⚠️ Même contrat d'absence que ses trois aînés, et il est ici plus piégeux
     * parce qu'aucun `counts` ne l'accompagne : le champ absent veut dire « le
     * domaine n'a pas été demandé », jamais « ce document n'a ni en-tête ni pied
     * de page ». Le seul énoncé d'absence est un `status:'none'` sur les six
     * zones d'une section.
     */
    headersFooters: CanvasHeadersFootersSchema.optional(),
    /**
     * La forme des paragraphes visés par `formatOf` (#1859), lue dans le
     * fichier Word que l'éditeur exporte en mémoire.
     *
     * ⚠️ Même contrat d'absence que les domaines à la demande : le champ absent
     * veut dire « aucune lecture de forme n'a été demandée », jamais « ces
     * paragraphes n'ont pas de forme ». Et même règle de déclaration : ce
     * schéma est `.strict()`, un front qui émettrait `format` sans que le champ
     * soit déclaré ferait échouer le parse de toute l'inspection.
     *
     * ⚠️ Ce que le modèle doit COMPRENDRE de ce champ est écrit dans
     * `INSPECT_DESCRIPTION` (backend, `llm/CanvasToolDefinitions.ts`) : un
     * reçu part au modèle en JSON brut, sans son contrat.
     */
    format: CanvasFormatReadSchema.optional(),
    /**
     * La lecture avec forme de l'acte ouvert, quand `formatReading` la demande.
     * Même contrat d'absence et même règle de déclaration que `format` : champ
     * absent, aucune lecture avec forme n'a été demandée.
     */
    formatReading: CanvasFormatReadingSchema.optional(),
  })
  .strict();

function verifierRevisionDeProjection(
  receipt: { revision: string; projection?: CanvasInspectionProjectionReceipt },
  context: z.RefinementCtx,
): void {
  if (receipt.projection !== undefined && receipt.projection.evaluatedRevision !== receipt.revision) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['projection', 'evaluatedRevision'],
      message: 'projection.evaluatedRevision must equal the canvas_inspect receipt revision.',
    });
  }
}

export const CanvasInspectReceiptSchema = CanvasInspectReceiptBaseSchema.superRefine(verifierRevisionDeProjection);

/**
 * Les identités de révisions suivies RELUES de part et d'autre d'une action.
 *
 * Les trois listes sont toutes requises dès que le navigateur publie cette
 * preuve : un tableau vide établit qu'une relecture a bien eu lieu et qu'elle
 * n'a observé aucune identité de cette catégorie. Leur absence, elle, conserve
 * le sens distinct de « ce runtime n'a pas pu produire cette preuve ».
 *
 * Les identifiants restent opaques au contrat. Leur format est celui du
 * fournisseur et ne doit pas être inventé ni durci ici ; le rôle de cette
 * surface est de transporter fidèlement la comparaison avant/après faite par
 * l'exécuteur frontend.
 */
export const CanvasRevisionIdentitiesSchema = z
  .object({
    nouvelles: z.array(z.string()),
    disparues: z.array(z.string()),
    disparuesInattendues: z.array(z.string()),
  })
  .strict();
export type CanvasRevisionIdentities = z.infer<typeof CanvasRevisionIdentitiesSchema>;

/**
 * Preuve détaillée d'un plan d'édition atomique exécuté par SuperDoc (#1610).
 *
 * Ce champ transporte des faits de mutation, pas un état de persistance :
 * `revision.before/after` vient du moteur vivant et ne dit rien de la future
 * sauvegarde automatique, des octets DOCX ni de leur validité.
 */
const CanvasEditPlanReceiptSchema = z
  .object({
    revision: z.object({ before: z.string().min(1), after: z.string().min(1) }).strict(),
    steps: z.array(
      z.object({
        id: CanvasEditPlanStepIdSchema,
        op: CanvasEditPlanOperationSchema,
        effect: z.enum(['changed', 'noop', 'error', 'assert_passed', 'assert_failed']),
        matchCount: z.number().int().nonnegative(),
        trackedChangeIds: z.array(z.string().min(1)).optional(),
        verified: z.boolean(),
        message: z.string().min(1).optional(),
      }).strict(),
    ).min(1).max(50),
    /**
     * La forme posée par les étapes `format_apply`, RELUE dans le fichier Word
     * exporté après le plan (#1947) : un reçu par réglage et par valeur
     * demandée — conforme, différent (avec la valeur lue et le passage) ou non
     * lisible (avec la raison). L'accusé de l'éditeur (`steps[].effect`) ne
     * prouve pas une forme. Absent : le plan ne porte aucune étape de forme.
     */
    format: z.object({ settings: z.array(CanvasFormatSettingReceiptSchema).min(1) }).strict().optional(),
  })
  .strict();

/** La forme relue des étapes `format_apply` d'un plan, telle que le reçu la porte (`editPlan.format`). */
export type CanvasEditPlanFormatReceipt = NonNullable<z.infer<typeof CanvasEditPlanReceiptSchema>['format']>;

const CanvasImagePixelSizeSchema = z
  .object({ width: z.number().int().nonnegative(), height: z.number().int().nonnegative() })
  .strict();

/**
 * Ce qu'`insert_image` ou `edit_image` a fait : l'image, le bloc qui la porte,
 * et la taille en pixels avant et après. `sizeBefore` est absent d'une pose,
 * `sizeAfter` d'un retrait ; les tailles sont lues dans la liste des images de
 * l'éditeur, jamais dans sa réponse. Pour une pose, `imageId` est l'identifiant
 * de l'image neuve et `nodeId` celui de son paragraphe neuf.
 */
export const CanvasImageReceiptSchema = z
  .object({
    imageId: z.string().min(1),
    nodeId: z.string().min(1),
    change: z.enum(['removed', 'resized', 'inserted', 'replaced']),
    sizeBefore: CanvasImagePixelSizeSchema.optional(),
    sizeAfter: CanvasImagePixelSizeSchema.optional(),
  })
  .strict();
export type CanvasImageReceipt = z.infer<typeof CanvasImageReceiptSchema>;

const CanvasActionReceiptCommonSchema = z
  .object({
    requestId: z.string().min(1),
    documentSessionId: z.string().min(1),
    intent: CanvasActionNameSchema,
    reviewability: z.enum(['tracked', 'direct_untracked', 'mixed', 'none']),
    preSnapshot: CanvasSnapshotSummarySchema,
    postSnapshot: CanvasSnapshotSummarySchema,
    resolvedTargets: z.array(ResolvedTargetSummarySchema),
    effects: z.array(CanvasEffectSchema),
    formattingMatched: z.boolean().optional(),
    /**
     * Preuve d'identité relue par le navigateur autour de l'action. Elle n'est
     * jamais déduite d'un compteur : un autre auteur peut modifier le document
     * au même instant sans que le count permette d'attribuer le changement.
     */
    revisionIdentities: CanvasRevisionIdentitiesSchema.optional(),
    /** Reçu natif par étape de `apply_edit_plan`, quand une application a été tentée. */
    editPlan: CanvasEditPlanReceiptSchema.optional(),
    /**
     * Ce qu'une écriture de forme (`format_text`, `format_paragraphs`) a fait, relu dans le fichier
     * Word exporté après l'écriture : par réglage, conforme, différent (avec la
     * valeur lue) ou non lisible ; ce que la cible couvrait sans être écrit.
     * Absent d'un reçu qui n'a rien écrit.
     */
    format: CanvasFormatWriteReceiptSchema.optional(),
    /**
     * Ce que `set_header_footer` a RELU du document, et lui seul.
     *
     * ⚠️ **Ces faits n'ont aucune autre porte de sortie.** Le receipt commun ne
     * transporte que des instantanés, des cibles et des vérifications ; or
     * l'agent doit apprendre trois choses qu'aucune d'elles ne dit, et qu'il ne
     * peut pas déduire de sa propre demande : si le drapeau de première page a
     * dû être levé (`first_page_flag`), ce que la zone portait AVANT
     * (`previous_text`), et surtout si un logo y est resté
     * (`previous_had_image`). Les enfouir dans une phrase de `description`
     * obligerait le modèle à lire de la prose pour établir un fait binaire.
     *
     * ⚠️ **`previous_had_image` est le champ qui empêche un mensonge par
     * omission.** `mode:'replace'` remplace le TEXTE et **conserve** l'image :
     * un agent qui annoncerait « en-tête remplacé » sans le dire ferait croire
     * à l'avocat que son papier à en-tête a disparu, ou l'inverse.
     */
    headerFooter: z
      .object({
        target: z.enum(['header', 'footer']),
        type: CanvasHeaderFooterTypeSchema,
        section_index: z.number().int().nonnegative(),
        mode: z.enum(['replace', 'append']),
        paragraphs_written: z.number().int().nonnegative(),
        /** Le texte que la zone portait AVANT l'écriture, borné. Vide si elle ne peignait rien. */
        previous_text: z.string().max(1_000),
        /** `true` quand une image était présente — elle a été CONSERVÉE, jamais supprimée. */
        previous_had_image: z.boolean(),
        /**
         * `raised` — le drapeau de première page était éteint et vient d'être levé,
         * sans quoi rien n'aurait été peint. `already_on` — il l'était déjà.
         * `not_applicable` — la zone visée n'est pas la première page.
         */
        first_page_flag: z.enum(['raised', 'already_on', 'not_applicable']),
        /** Les finitions qui n'ont pas abouti, dites en clair. Vide = rien à signaler. */
        warnings: z.array(z.string().min(1)).max(20),
        /**
         * Les quatre parties de la section (en-tête / pied × première page / autres pages), relues après l'écriture.
         * Les zones de pages paires n'y figurent pas : `canvas_inspect`, domaine `headersFooters`, les donne.
         *
         * Facultatif au contrat, comme `other_part` : l'exécuteur les envoie toujours, mais un onglet resté sur un
         * bundle d'avant le 2026-10-05 ne les connaît pas, et son reçu ne doit pas être refusé pour autant.
         */
        parts_after: CanvasHeaderFooterPartsAfterSchema.optional(),
        /** Le sort de la partie qui n'était pas demandée. */
        other_part: CanvasHeaderFooterOtherPartSchema.optional(),
        /**
         * Les numéros de page de la zone, relus dans le fichier exporté après l'appel. Présent quand l'appel en
         * posait ou en retirait ; l'éditeur ne les rend par aucune de ses lectures (`blocks.list` rend « Page  / »).
         */
        page_numbers: CanvasHeaderFooterPageNumbersSchema.optional(),
        /**
         * Les écritures que l'appel a faites, une entrée d'historique chacune : ce qu'`undo_changes` doit défaire
         * (`steps`). Un réglage déjà en place et une écriture refusée n'en sont pas. Même champ que `pageLayout.writes`.
         * Facultatif au contrat pour un onglet resté sur un bundle d'avant le 2026-10-07 ; l'exécuteur l'envoie toujours.
         */
        writes: z.number().int().nonnegative().optional(),
      })
      .strict()
      .optional(),
    /**
     * Ce que `set_page_layout` a fait, relu dans le fichier exporté après l'écriture : par réglage, conforme
     * (toutes les sections le portent), différent (avec les valeurs lues) ou non lisible. Même règle que `format`.
     */
    pageLayout: CanvasPageLayoutReceiptSchema.optional(),
    /**
     * Ce qu'un `replace_text` en mode partout (`everywhere:true`) a trouvé et écrit : sur un refus, le compte et les
     * emplacements de chaque texte à trancher. Absent du mode à un passage.
     */
    replaceEverywhere: CanvasReplaceEverywhereReceiptSchema.optional(),
    /** Ce que `rewrite_block` a fait : paragraphes écrits, relus, et ceux que l'éditeur a refusés. */
    rewrite: CanvasRewriteReceiptSchema.optional(),
    /** Ce qu'`insert_image` ou `edit_image` a fait d'une image du corps, d'après la liste des images relue après l'écriture. */
    image: CanvasImageReceiptSchema.optional(),
    recovery: RecoveryInstructionSchema.optional(),
  })
  .strict();

/**
 * Les CINQ issues d'une action canvas, et ce que chacune autorise à dire à
 * l'avocat (#939, #990).
 *
 * - `ok` — l'effet demandé est prouvé sur le document par une relecture.
 * - `partial` — **une partie de ce qui était demandé a été appliquée, l'autre
 *   non**. Deux formes, et elles se lisent pareil : un lot dont certaines
 *   opérations ont échoué (une suppression sur trois refusée par le provider),
 *   et une écriture obtenue **sans le régime demandé** — le texte est bien dans
 *   le document, mais aucune révision suivie n'a été produite
 *   (`TRACKING_NOT_APPLIED`). Dans les deux cas l'effet partiel est ACQUIS :
 *   rejouer l'action le dupliquerait.
 * - `failed` — l'effet demandé n'a pas eu lieu ; réinspecter puis retenter est
 *   la bonne conduite.
 * - `aborted` — l'action a été **refusée avant toute mutation**, donc rien n'a
 *   été écrit : capacité absente sur ce document, opération qui s'annonce non
 *   suivable alors que le document est en régime suivi, sélecteur irrésolu.
 *   Distinct de `failed`, qui laisse planer un doute sur ce qui a bougé : ici
 *   le document est intact, et la sortie est de changer de verbe ou de réglage,
 *   jamais de rejouer à l'identique.
 * - `non_verifiable` — le document a bougé pour une autre raison pendant
 *   l'action : son effet n'est **ni prouvé ni infirmé**, et il ne faut PAS
 *   retenter (retenter à l'aveugle une édition qui a peut-être eu lieu la
 *   duplique).
 */
const CanvasActionReceiptOkSchema = CanvasActionReceiptCommonSchema.extend({
  status: z.literal('ok'),
  verification: z
    .array(VerificationCheckSchema)
    .min(1)
    .refine((checks) => checks.every((check) => check.passed), 'ok receipts require every verification check to pass'),
  verificationPassed: z.literal(true),
  errors: z.array(CanvasActionErrorSchema).length(0),
}).strict();

const CanvasActionReceiptNotOkSchema = CanvasActionReceiptCommonSchema.extend({
  status: z.enum(['partial', 'failed', 'aborted', 'non_verifiable']),
  verification: z.array(VerificationCheckSchema),
  verificationPassed: z.boolean(),
  errors: z.array(CanvasActionErrorSchema),
}).strict();

export const CanvasActionReceiptSchema = z.discriminatedUnion('status', [
  CanvasActionReceiptOkSchema,
  CanvasActionReceiptNotOkSchema,
]);

export type CanvasInspectReceipt = z.infer<typeof CanvasInspectReceiptSchema>;
export type CanvasActionReceipt = z.infer<typeof CanvasActionReceiptSchema>;

// ── Forme conversation/époque des mêmes reçus (#1335) ──────────────────────
//
// Même construction que les enveloppes : `omit` + `extend` sur le schéma
// legacy, pour qu'aucun champ ne puisse diverger entre les deux formes.

export const CanvasInspectReceiptConversationSchema = CanvasInspectReceiptBaseSchema
  .omit({ documentSessionId: true })
  .extend(CanvasConversationMountIdentityShape)
  .superRefine(verifierRevisionDeProjection);
export type CanvasInspectReceiptConversation = z.infer<typeof CanvasInspectReceiptConversationSchema>;

/**
 * Reçu d'inspection dans les deux formes d'identité (#1335).
 *
 * Le courtier backend (`CanvasSessionBroker`) parse CE schéma ; le producteur
 * navigateur reste sur `CanvasInspectReceiptSchema` jusqu'à #1336.
 */
export const CanvasInspectReceiptTransitionSchema = z.union([
  CanvasInspectReceiptSchema,
  CanvasInspectReceiptConversationSchema,
]);
export type CanvasInspectReceiptTransition = z.infer<typeof CanvasInspectReceiptTransitionSchema>;

/**
 * Les deux issues du reçu d'action, en forme conversation (#1373).
 *
 * Nommées et exportées plutôt que laissées anonymes dans l'union ci-dessous :
 * un producteur qui vise la forme conversation doit pouvoir se valider contre
 * SA branche, et non contre une union qui accepterait aussi son legacy.
 * `.strict()` vient des schémas d'origine et survit à `omit` + `extend` : un
 * reçu qui porterait les deux identités est refusé.
 */
export const CanvasActionReceiptOkConversationSchema = CanvasActionReceiptOkSchema
  .omit({ documentSessionId: true })
  .extend(CanvasConversationMountIdentityShape);
export type CanvasActionReceiptOkConversation = z.infer<typeof CanvasActionReceiptOkConversationSchema>;

export const CanvasActionReceiptNotOkConversationSchema = CanvasActionReceiptNotOkSchema
  .omit({ documentSessionId: true })
  .extend(CanvasConversationMountIdentityShape);
export type CanvasActionReceiptNotOkConversation = z.infer<typeof CanvasActionReceiptNotOkConversationSchema>;

/**
 * Reçu d'action dans les deux formes d'identité (#1335).
 *
 * ⚠️ Une `z.union` et non une `z.discriminatedUnion('status')` : le
 * discriminant `status` ne départage pas les deux IDENTITÉS, qui se croisent
 * avec lui (4 branches). Zod n'accepte qu'un discriminant par union ; imbriquer
 * deux `discriminatedUnion` produirait des messages d'erreur qui ne nomment ni
 * la branche ni la clé — précisément le défaut corrigé sur
 * `CanvasActionInputSchema`. Les quatre branches restent `.strict()`, donc un
 * reçu aliasé est refusé quelle que soit son issue.
 */
export const CanvasActionReceiptTransitionSchema = z.union([
  CanvasActionReceiptOkSchema,
  CanvasActionReceiptNotOkSchema,
  CanvasActionReceiptOkConversationSchema,
  CanvasActionReceiptNotOkConversationSchema,
]);
export type CanvasActionReceiptTransition = z.infer<typeof CanvasActionReceiptTransitionSchema>;

type CanvasActionSchema = z.ZodType<{ action: string }, z.ZodTypeDef, unknown>;
type CanvasActionDefinition<TSchema extends CanvasActionSchema> = {
  name: z.infer<TSchema>['action'];
  inputSchema: TSchema;
  llmDescription: string;
  examples: readonly z.input<TSchema>[];
  capability: string;
  /**
   * Le régime d'écriture que ce verbe emprunte.
   *
   * - `tracked` — le verbe demande le suivi ; ce qu'il OBTIENT se lit dans le
   *   receipt, jamais ici (#990).
   * - `capability_dependent` — le suivi dépend de ce que la surface du
   *   fournisseur sait faire.
   * - `document_mode_dependent` — le verbe emprunte le régime de révision
   *   actuellement choisi dans le document par l'avocat. Le receipt décrit
   *   toujours le résultat réellement obtenu (`tracked` ou `direct_untracked`).
   * - `direct` — le verbe écrit en direct **quel que soit le régime affiché**,
   *   et c'est une décision de produit, pas une limite technique (#993).
   *   Employé quand le geste ne produit structurellement aucune révision :
   *   document complet, commentaires, déplacement ou restauration d'historique.
   *   Pour `write_markdown`, soumettre un acte de 200 paragraphes comme autant
   *   de révisions à accepter une par une n'aurait aucun sens pour l'avocat.
   *   Une troisième valeur plutôt qu'un `capability_dependent` par défaut :
   *   ranger sous « ça dépend » une exception qui ne dépend de rien la rendrait
   *   invisible au premier lecteur.
   */
  reviewPolicy: 'tracked' | 'capability_dependent' | 'document_mode_dependent' | 'direct';
  /**
   * Opérations du Document API SANS lesquelles ce verbe ne peut pas s'exécuter,
   * nommées comme `capabilities.get().operations` les indexe (#987).
   *
   * ⚠️ **C'est ce champ qui rend impossible l'état où un verbe est proposé au
   * modèle alors que le fournisseur ne sait pas l'exécuter.** `undo_changes` a
   * vécu ainsi depuis sa mise en service : il appelait `doc.undo`, qui n'existe
   * pas — l'undo vit sous `doc.history.undo` — et rendait donc
   * `CAPABILITY_UNAVAILABLE` à chaque appel, sans qu'aucun test ne rougisse (le
   * double de test, lui, définissait un `undo` à la racine).
   *
   * Deux règles pour maintenir cette liste :
   *  - n'y mettre que le REQUIS. Une opération de confort (`trackChanges.list`
   *    pour enrichir un receipt) n'a rien à y faire : la déclarer ferait
   *    disparaître le verbe pour une capacité dont son résultat ne dépend pas.
   *  - la lire depuis l'exécuteur, jamais depuis l'intention. Le nom du verbe
   *    ne dit pas quelle surface il emprunte : `replace_text` passe par
   *    `query.match` puis `doc.replace`, ce qu'aucune lecture de son nom ne
   *    donne.
   */
  requiredOperations: readonly string[];
  /**
   * Les opérations MUTANTES dont le suivi conditionne la relisibilité du verbe
   * (#990) — un sous-ensemble strict de `requiredOperations`.
   *
   * ## Pourquoi ce n'est pas `requiredOperations`
   *
   * `capabilities.get()` publie un `tracked` par opération, et il vaut `false`
   * sur tout ce qui ne mute pas : mesuré le 2026-08-16 sur `superdoc@2.6.0`,
   * `query.match`, `blocks.list` et `trackChanges.list` sont tous
   * `tracked:false`. Refuser un verbe dès qu'une de ses opérations requises
   * s'annonce non suivable couperait donc `replace_text` et
   * `delete_block_range` **en régime suivi** —
   * c'est-à-dire tout le produit, à cause d'une lecture.
   *
   * Le champ ne liste donc que l'opération par laquelle l'écriture passe :
   * `replace`, `insert`, `blocks.delete`, `styles.paragraph.setStyle`.
   *
   * ## Ce que `tracked` vaut, et ce qu'il ne vaut pas
   *
   * Mesuré le 2026-08-16 (`tests/ws-mocked/mesure-suivi-par-verbe.spec.ts`) :
   * ces opérations s'annoncent **toutes** `tracked:true`, et pourtant `insert`
   * ne produit **aucune** révision sous fenêtre `suggesting` alors que `replace`
   * et `blocks.delete` en produisent une. `tracked` n'est donc **pas** une
   * source de vérité sur le résultat : il sert à REFUSER en amont ce qui
   * s'annonce non suivable, jamais à promettre que le suivi a eu lieu — cette
   * dernière question se tranche à la relecture, dans le receipt.
   *
   * ⚠️ **C'est pour cette raison qu'`insert_paragraphs` ne passe plus par
   * `insert`** (mesuré le 2026-08-29,
   * `tests/ws-mocked/mesure-insertion-suivie-formes.spec.ts`). Onze formes
   * d'insertion exercées sous fenêtre `suggesting`, relevé des révisions avant
   * et après chacune : les deux formes de `doc.insert` structurel — avec et
   * sans cible — écrivent le texte et ne produisent aucune révision, tandis que
   * `create.paragraph` crée un bloc **et** produit une révision `structural /
   * paragraph-insertion` attribuée à ce bloc. C'est la seule surface mesurée à
   * faire les deux : les trois autres qui produisent une révision
   * (`doc.insert` texte par `ref`, `doc.insert` texte sans locator,
   * `mutations.apply(text.insert)`) n'écrivent que du texte INLINE dans un bloc
   * existant, ce qui ne peut pas servir un verbe qui ajoute des paragraphes.
   *
   * Vide pour les verbes qui ne produisent structurellement aucune révision
   * (accepter/refuser des révisions, annuler) : leur refuser le régime suivi
   * les rendrait inutilisables alors qu'ils sont précisément l'outil de revue.
   */
  reviewedOperations: readonly string[];
  /**
   * Libellé lisible par l'avocat — ce qu'il lit quand Hector agit sur son acte.
   *
   * Aucun verbe n'en avait avant #987 : l'interface s'arrêtait au nom de
   * l'outil, et le nom technique nu (`rewrite_block`) remontait jusqu'à l'écran.
   */
  libelle: string;
};

const CANVAS_ACTION_DEFINITION_TUPLE = [
  // Le texte part par le moteur de plans (une étape `text.rewrite` par paragraphe), dans le régime du document : comme
  // pour `apply_edit_plan`, ses entrées et ses étapes ne sont pas des opérations plates de `capabilities.get()` et se
  // contrôlent sur le document monté (`ETAPES_DE_PLAN_PAR_VERBE`, navigateur). D'où `document_mode_dependent` et
  // `reviewedOperations` vide : le suivi obtenu se lit dans le reçu, aux révisions relues.
  { name: 'rewrite_block', inputSchema: RewriteBlockInputSchema, llmDescription: 'Rewrite whole paragraphs in ONE call, paragraph formatting kept: text carries **bold**, *italic*, __underline__; the receipt names what is lost or skipped.', examples: [{ action: 'rewrite_block', blocks: [{ nodeId: '4f2a91c0', text: '**Objet :** loyers impayés' }] }], capability: 'tracked_text', reviewPolicy: 'document_mode_dependent', requiredOperations: ['format.apply', 'blocks.list'], reviewedOperations: [], libelle: 'Réécrire des paragraphes' },
  { name: 'replace_text', inputSchema: ReplaceTextInputSchema, llmDescription: 'Replace one isolated passage inside a block; with everywhere:true, each find across the whole document, written only if every expectedCount matches.', examples: [{ action: 'replace_text', edits: [{ find: 'ancienne denomination', replace: 'nouvelle denomination' }], caseSensitive: false }, { action: 'replace_text', everywhere: true, edits: [{ find: '1 500 €', replace: '2 300 €', expectedCount: 2 }, { find: '12 000 €', replace: '15 400 €', expectedCount: 2 }] }], capability: 'tracked_text', reviewPolicy: 'tracked', requiredOperations: ['query.match', 'replace'], reviewedOperations: ['replace'], libelle: 'Remplacer un texte' },
  { name: 'apply_edit_plan', inputSchema: ApplyEditPlanInputSchema, llmDescription: 'Apply one native atomic plan of 1–50 linked edits in the document review mode (more: several plans, one block\'s steps kept in the same plan). Each step has a stable id and targets ONE block: assert, rewrite, delete, one formatting mark (a passage of one given paragraph, or with rewrites; texts everywhere or whole paragraphs: format_text), or text_insert — text added INSIDE the block, at its start (before) or end (after), space included (a new paragraph is insert_paragraphs); at least one step must change the document. A text a step looks for (find, or the text of a delete or format step) must occur exactly once in that block, case-sensitive, as plain text (no ** or * from the read): one step per occurrence, find widened with its context when the word recurs. Targets come from the inspected revision, not from another step; mutations use distinct ranges and an assert_text may guard one. Rewrites keep source formatting.', examples: [{ action: 'apply_edit_plan', steps: [{ id: 'renum-3', op: 'text_rewrite', selector: { kind: 'nodeId', nodeId: '4f2a91c0' }, find: 'n°3', replace: 'n°4' }, { id: 'renum-4', op: 'text_rewrite', selector: { kind: 'nodeId', nodeId: '9b17e2d4' }, find: 'n°4', replace: 'n°5' }] }], capability: 'atomic_edit_plan', reviewPolicy: 'document_mode_dependent', requiredOperations: ['blocks.list'], reviewedOperations: [], libelle: 'Appliquer les retouches au document' },
  { name: 'insert_paragraphs', inputSchema: InsertParagraphsInputSchema, llmDescription: 'Insert plain-text paragraphs (not markdown). Anchor framed by "": each text gets a following "" if missing; leading "" dropped.', examples: [{ action: 'insert_paragraphs', placement: { at: 'after', selector: { kind: 'textSearch', terms: ['Piece n°9'], match: 'all' } }, texts: ['Piece n°10 : ...', 'Piece n°11 : ...'] }], capability: 'tracked_text', reviewPolicy: 'tracked', requiredOperations: ['create.paragraph', 'blocks.list'], reviewedOperations: ['create.paragraph'], libelle: 'Insérer des paragraphes' },
  { name: 'delete_block_range', inputSchema: DeleteBlockRangeInputSchema, llmDescription: 'Delete one block ("from") or a contiguous run ("from" to "to").', examples: [{ action: 'delete_block_range', from: { kind: 'nodeId', nodeId: 'p-12' } }], capability: 'tracked_structure', reviewPolicy: 'tracked', requiredOperations: ['blocks.delete', 'blocks.list'], reviewedOperations: ['blocks.delete'], libelle: 'Supprimer un passage' },
  { name: 'apply_style', inputSchema: ApplyStyleInputSchema, llmDescription: 'Instead of format_paragraphs when ONE block needs a named style without its hand-set text formatting — template: Hector-Normal, Hector-H1 … Hector-H6, Hector-PLAISE, Hector-BORDEREAU, Hector-PAR-CES-MOTIFS, Hector-TRES-IMPORTANT, Hector-Marqueur-BU, Hector-Marqueur-Bold, Hector-Pieces-Jointes, Hector-Piece-Before.', examples: [{ action: 'apply_style', selector: { kind: 'nodeId', nodeId: 'NEW-P10' }, styleId: 'Hector-Normal' }], capability: 'style', reviewPolicy: 'capability_dependent', requiredOperations: ['styles.paragraph.setStyle', 'blocks.list'], reviewedOperations: ['styles.paragraph.setStyle'], libelle: 'Appliquer un style' },
  // `requiredOperations` s'arrête à ce SANS QUOI rien ne peut être écrit :
  // `insert` (le canal HTML du point d'écriture) et `blocks.list` (les
  // instantanés avant/après du receipt). Les listes et les tables ont un repli
  // HTML, la pose de style est best-effort, et `blocks.delete` ne sert qu'au
  // mode `replace` — le déclarer ici ferait disparaître le verbe ENTIER, mode
  // `append` compris, sur un document qui ne sait pas supprimer. Ce mode-là se
  // refuse donc à l'exécution, par un message qui nomme `append` comme sortie.
  // `reviewedOperations` est vide : ce verbe écrit en direct par décret (voir
  // `reviewPolicy: 'direct'`), il ne produit structurellement aucune révision.
  { name: 'write_markdown', inputSchema: WriteMarkdownInputSchema, llmDescription: 'Pose a NEW document in ONE markdown call: headings, bold, italics, lists, tables and quotes become real Word formatting. A letter starts with its sender block. mode "replace" only initialises a truly empty document; "append" adds a section at the end. Existing text is changed in place by the other verbs. Never tracked.', examples: [{ action: 'write_markdown', mode: 'replace', markdown: '# Mise en demeure\n\n**Vos conclusions** appellent deux observations :\n\n- prescription acquise ;\n- préjudice non établi.\n' }], capability: 'document_write', reviewPolicy: 'direct', requiredOperations: ['insert', 'blocks.list'], reviewedOperations: [], libelle: 'Poser un document rédigé' },
  // #1863 — écriture DIRECTE par décret (spec #1858, règle commune n°1) : la
  // forme ne crée jamais de révision, même sous suivi. D'où `reviewPolicy:
  // 'direct'` et `reviewedOperations` vide — y déclarer une opération ferait
  // attendre une révision que le verbe est fait pour ne pas produire.
  // `requiredOperations` : `format.apply` (l'opération que chaque étape du plan
  // exécute) et `blocks.list` (les instantanés du reçu). L'écriture passe par
  // le moteur de plans, dont les entrées (`mutations.preview`/`apply`) et les
  // étapes ne sont pas des opérations plates de `capabilities.get()` : elles se
  // contrôlent sur le document monté, comme pour `apply_edit_plan`.
  {
    name: 'format_text',
    inputSchema: FormatTextInputSchema,
    llmDescription: 'Set the form of existing text: bold, italic, underline, highlight, color, fontFamily, fontSizePt. '
      + '"none" (false for the first three) removes a setting: font and size go back to the paragraph style\'s — tell '
      + 'the lawyer which, the receipt\'s "remaining" names it — and a style\'s highlight is covered with white. '
      + 'onlyWhere:{bold:true} writes only text that already shows that form, even from a style ("any": any highlight '
      + 'or colour; "none": none). target, by kind: "all" (the whole act), "ids", "range" (from and to are nodeIds, '
      + 'bounds included), or "text" — with nodeId, a text found once in that paragraph; without, an exact text or a list of them: each whole-word '
      + 'occurrence in the act.',
    examples: [{
      action: 'format_text',
      target: { kind: 'text', text: ['12 janvier 2022', '3 mars 2023'] },
      color: '#8B0000',
      bold: true,
    }],
    capability: 'formatting',
    reviewPolicy: 'direct',
    requiredOperations: ['format.apply', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Mettre en forme du texte',
  },
  // #1866 — même écriture directe par décret que `format_text`. L'alignement
  // part en plan atomique (`format.apply`) ; retraits, espacement et interligne
  // n'ont pas d'étape de plan (`mesure-forme-en-lot`) et demandent une
  // opération par paragraphe : les deux sont requises. #1867 — la bordure
  // (une opération par côté, une autre pour l'effacer) et la remise à zéro,
  // qui redonne ensuite le saut de page avant et le niveau de plan. Le style
  // nommé se pose par `setStyleRef`, comme pour `copy_format`. Les opérations
  // des fonds (`format.paragraph.setShading` / `clearShading`, `tables.setShading`
  // / `clearShading`, 2026-10-11) et celle du cadre d'une case (`tables.setBorders`,
  // 2026-10-12) restent dehors : les exiger ferait disparaître le verbe entier
  // d'un document qui ne sait pas poser un fond ou un cadre de case. L'exécuteur
  // les exige du seul appel qui en pose un.
  {
    name: 'format_paragraphs',
    inputSchema: FormatParagraphsInputSchema,
    llmDescription: 'Set the form of whole paragraphs: a style the document defines (styleId), alignment, indents (cm), '
      + 'spacing before and after (points), line spacing, border sides, shading, the border and shading of their table cell (cellBorder, cellShading), or reset to the named style. target: as '
      + 'format_text, without "text"; table cells only through "ids". Alignment is written in batches; a style, indents, spacing (line spacing included), a shading and each '
      + 'border side cost one write per paragraph each — a box four, reset one to three. Before a call of more than 100 writes, '
      + 'warn the lawyer: it can take minutes, and only the last 100 can be undone.',
    examples: [{
      action: 'format_paragraphs',
      target: { kind: 'range', from: '4f2a91c0', to: '9b17e2d4' },
      alignment: 'justify',
      indentFirstLineCm: 1,
      lineSpacing: 1.5,
    }],
    capability: 'formatting',
    reviewPolicy: 'direct',
    requiredOperations: [
      'format.apply',
      'styles.paragraph.setStyleRef',
      'format.paragraph.setIndentation',
      'format.paragraph.setSpacing',
      'format.paragraph.setBorder',
      'format.paragraph.clearBorder',
      'format.paragraph.resetDirectFormatting',
      'format.paragraph.setFlowOptions',
      'format.paragraph.setOutlineLevel',
      'blocks.list',
    ],
    reviewedOperations: [],
    libelle: 'Mettre en forme des paragraphes',
  },
  // #1869 — le pinceau. Même écriture directe par décret que les deux verbes
  // de forme. La forme de texte et l'alignement partent en plan atomique
  // (`format.apply`) ; le style nommé, les retraits, l'espacement et chaque
  // côté de bordure demandent une opération par paragraphe. Le style se pose
  // par `setStyleRef` : `setStyle` efface la forme directe du texte
  // (`mesure-copy-format`, S1 et S2, 2026-10-04).
  {
    name: 'copy_format',
    inputSchema: CopyFormatInputSchema,
    llmDescription: 'Give paragraphs the form a model paragraph ("from", its nodeId) shows — "same form as this one": named '
      + 'style, alignment, indents, spacing, line spacing, border, and one text form for all their text — that of the '
      + 'model\'s longest passage; their other hand-set character formatting is removed. Lists stay lists and keep '
      + 'their indents. target: "ids" or "range". '
      // Le seuil n'est pas répété : la référence livre toujours les deux descriptions ensemble
      // (`buildCanvasAgentPromptReference` rend tout le catalogue, sans filtre par verbe).
      + 'Up to seven writes per paragraph: same warning as format_paragraphs.',
    examples: [{
      action: 'copy_format',
      from: '4f2a91c0',
      target: { kind: 'ids', nodeIds: ['9b17e2d4', '5c3d8e1a'] },
    }],
    capability: 'formatting',
    reviewPolicy: 'direct',
    requiredOperations: [
      'format.apply',
      'styles.paragraph.setStyleRef',
      'format.paragraph.setIndentation',
      'format.paragraph.setSpacing',
      'format.paragraph.setBorder',
      'format.paragraph.clearBorder',
      'blocks.list',
    ],
    reviewedOperations: [],
    libelle: 'Copier la forme d’un paragraphe',
  },
  // Deux opérations d'écriture, et les deux sont requises : le paragraphe SANS
  // son style nommé n'est pas un titre, c'est du corps de texte. `create.heading`
  // existe et ferait le tout en un appel, mais avec le style built-in `Heading2`
  // — 18,67 px sans graisse, là où `Hector-H2` est gras et souligné (mesuré) :
  // le titre de l'agent serait visiblement différent des titres de l'acte.
  {
    name: 'insert_heading',
    inputSchema: InsertHeadingInputSchema,
    llmDescription: 'Insert a real Word heading (level 1-6) styled like the document\'s other headings, for the '
      + 'sections of an act — never a paragraph that merely looks like a title.',
    examples: [{
      action: 'insert_heading',
      text: 'II. SUR LA PRESCRIPTION DE L\'ACTION',
      level: 2,
      placement: { at: 'after', selector: { kind: 'nodeId', nodeId: '4f2a91c0' } },
    }],
    capability: 'document_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['create.paragraph', 'styles.paragraph.setStyle', 'blocks.list'],
    reviewedOperations: ['create.paragraph', 'styles.paragraph.setStyle'],
    libelle: 'Insérer un titre',
  },
  // Une seule opération d'écriture, mesurée `tracked:true` et produisant une
  // révision. `blocks.list` reste requis : c'est lui qui résout le sélecteur et
  // fournit les instantanés du receipt.
  {
    name: 'insert_page_break',
    inputSchema: InsertPageBreakInputSchema,
    llmDescription: 'Force a page break BEFORE the resolved block — the block starts a new page. Use it before the '
      + 'closing sections of an act (PLAISE AU TRIBUNAL, BORDEREAU, PIECES JOINTES). Two rules: never simulate a page '
      + 'break with empty paragraphs, they shift as soon as the text above changes; and add the page break AFTER you '
      + 'have written everything that follows it, because content inserted after a page-broken block is not painted on '
      + 'screen (known editor defect) even though the exported .docx is correct.',
    examples: [{
      action: 'insert_page_break',
      selector: { kind: 'textSearch', terms: ['BORDEREAU DE COMMUNICATION DE PIECES'], match: 'all' },
    }],
    capability: 'document_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['format.paragraph.setFlowOptions', 'blocks.list'],
    reviewedOperations: ['format.paragraph.setFlowOptions'],
    libelle: 'Insérer un saut de page',
  },
  // `hyperlinks.list` est déclarée REQUISE alors qu'elle ne fait que LIRE, et
  // c'est délibéré : c'est la seule surface qui prouve qu'un lien existe dans le
  // document. Sans elle, le verbe n'aurait pour preuve que l'accusé du
  // fournisseur — c'est-à-dire exactement le « verbe non mesuré » que ce
  // chantier interdit.
  // `reviewedOperations` est VIDE : `hyperlinks.wrap` s'annonce `tracked:false`
  // et produit pourtant une révision (mesuré). L'y déclarer ferait refuser le
  // verbe sous le régime suivi, au nom d'une déclaration que la
  // mesure dément.
  {
    name: 'add_hyperlink',
    inputSchema: AddHyperlinkInputSchema,
    llmDescription: 'Turn an EXISTING portion of text into a hyperlink. It links text that is already in the document — '
      + 'it never writes any: if the label does not exist yet, write it first with insert_paragraphs or rewrite_block, '
      + 'then link it. Use it for a Légifrance article, a Judilibre decision or a case-file link inside a note.',
    examples: [{
      action: 'add_hyperlink',
      selector: { kind: 'textSearch', terms: ['article 1240 du code civil'], match: 'all' },
      text: 'article 1240 du code civil',
      href: 'https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000032041571',
    }],
    capability: 'formatting',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['query.match', 'hyperlinks.wrap', 'hyperlinks.list', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Poser un lien',
  },
  // `tables.get` est requise pour la même raison qu'`hyperlinks.list` : c'est la
  // relecture qui prouve que la table existe ET qu'elle a la forme demandée.
  // `doc.insert` HTML rendait déjà `success:true` en posant un paragraphe.
  // L'habillage (style de grille, pleine largeur) reste dehors : l'exiger ferait
  // disparaître le verbe entier d'un document qui ne sait pas le poser. Le
  // préréglage de `borders:"none"` (#1861) est exigé par l'exécuteur, pour ce
  // seul appel.
  {
    name: 'create_table',
    inputSchema: CreateTableInputSchema,
    llmDescription: 'Create a real Word table from a matrix of cell texts (full width, bordered by default): a pièces '
      + 'schedule, a chronology. Pass borders:"none" for an invisible table that only lays text out side by side — the '
      + 'sender and the recipient at the top of a letter, two signature blocks. Cells are created PLAIN: no bold, '
      + 'italic or hyperlink, one paragraph each; format them afterwards.',
    examples: [{
      action: 'create_table',
      rows: [['Pièce n°', 'Intitulé', 'Date'], ['1', 'Contrat de prestation signé', '12/03/2024']],
      placement: { at: 'after', selector: { kind: 'textSearch', terms: ['BORDEREAU'], match: 'all' } },
    }],
    capability: 'document_structure',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['create.table', 'tables.setCellText', 'tables.get', 'blocks.list'],
    reviewedOperations: ['create.table'],
    libelle: 'Créer un tableau',
  },
  // ── Tranche 2 (#994) — listes et structure de table ────────────────────────
  //
  // ⚠️ `reviewedOperations` est VIDE pour `convert_list` et `attach_numbering`,
  // et ce n'est pas un oubli. `lists.create` s'annonce `tracked: false` tout en
  // produisant DEUX révisions sous fenêtre `suggesting` (mesuré) : l'y déclarer
  // ferait refuser le verbe sous le régime suivi.
  // Même arbitrage exact qu'`add_hyperlink` en #992, et pour la même cause — le
  // `tracked` déclaré ne promet rien, dans les deux sens. Le juge reste le
  // receipt, qui lit les identités de révision réellement apparues.
  {
    name: 'convert_list',
    inputSchema: ConvertListInputSchema,
    llmDescription: 'Turn paragraphs the lawyer already wrote into a real Word list — numbered or bulleted. Use it '
      + 'for "numérote ces moyens" or "mets ça en puces". Give "from" alone for one paragraph, or "from" and "to" '
      + 'for a run of consecutive paragraphs. This writes a real w:numPr, so Word renumbers by itself when a line '
      + 'is inserted later — unlike typing "1." by hand.',
    examples: [{
      action: 'convert_list',
      from: { kind: 'nodeId', nodeId: '4f2a91c0' },
      to: { kind: 'nodeId', nodeId: '9b17e2d4' },
      kind: 'ordered',
    }],
    capability: 'list_structure',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['lists.create', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Mettre en liste',
  },
  {
    name: 'add_list_items',
    inputSchema: AddListItemsInputSchema,
    llmDescription: 'Add one or more items to a list that already exists, next to the item you point at. The '
      + 'numbering keeps running — do not write the number yourself. Point the selector at an EXISTING list item, '
      + 'not at the paragraph above the list.',
    examples: [{
      action: 'add_list_items',
      selector: { kind: 'textSearch', terms: ['Sur le défaut de motivation'], match: 'all' },
      position: 'after',
      texts: ['Sur le détournement de pouvoir'],
    }],
    capability: 'list_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['lists.insert', 'blocks.list'],
    reviewedOperations: ['lists.insert'],
    libelle: 'Ajouter des items à une liste',
  },
  {
    name: 'attach_numbering',
    inputSchema: AttachNumberingInputSchema,
    llmDescription: 'Make paragraphs written after the fact JOIN an existing list, continuing its numbering instead '
      + 'of starting a new one at 1. "from"/"to" are the paragraphs to attach; "attachTo" points at any item of the '
      + 'list they must join. Prefer this over convert_list whenever a list already exists just above — convert_list '
      + 'would create a SECOND list restarting at 1.',
    examples: [{
      action: 'attach_numbering',
      from: { kind: 'textSearch', terms: ['Sur le détournement de pouvoir'], match: 'all' },
      attachTo: { kind: 'textSearch', terms: ['Sur la nullité externe'], match: 'all' },
    }],
    capability: 'list_structure',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['lists.attach', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Rattacher à une liste',
  },
  // `tables.get` est requise pour la même raison que dans `create_table` : c'est
  // la relecture de géométrie qui PROUVE le geste, un receipt en succès ne
  // prouvant rien. `tables.setCellText` y est aussi, parce que le verbe annonce
  // le remplissage de la ligne insérée : un document qui ne saurait pas écrire
  // en cellule tiendrait une promesse à moitié.
  {
    name: 'edit_table',
    inputSchema: EditTableInputSchema,
    llmDescription: 'Add or remove a row or a column of an existing table, and optionally fill the row you insert. '
      + 'Use it for "ajoute une ligne au tableau des préjudices". Point the selector at the table (its cell texts '
      + 'are searchable). Omit index and position to append at the end. ⚠️ Deleting removes exactly ONE row or '
      + 'column per call — count only works when inserting. ⚠️ "texts" fills the row being inserted; the cells of a '
      + 'row that already exists cannot be rewritten while change tracking is on.',
    examples: [{
      action: 'edit_table',
      selector: { kind: 'textSearch', terms: ['Chef de préjudice'], match: 'all' },
      operation: 'insert_row',
      texts: ['Préjudice moral', '5 000 €', 'Pièce n°7'],
    }],
    capability: 'document_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['tables.insertRow', 'tables.deleteRow', 'tables.insertColumn', 'tables.deleteColumn',
      'tables.setCellText', 'tables.get', 'blocks.list'],
    reviewedOperations: ['tables.insertRow', 'tables.deleteRow'],
    libelle: 'Modifier la structure d\'un tableau',
  },
  // ── Tranche 3 (#996) — commentaires, déplacement, rétablissement ──────────
  //
  // ⚠️ `reviewedOperations` est VIDE pour les trois verbes de commentaire, et ce
  // n'est pas le même arbitrage qu'en #994 : ici la mesure confirme la
  // déclaration. Un commentaire posé sous fenêtre `suggesting` est accepté et
  // produit ZÉRO révision — parce qu'un commentaire n'est pas une révision, il
  // ne s'accepte ni ne se refuse. Le receipt ne devra donc jamais conseiller à
  // l'avocat d'aller accepter quoi que ce soit après un commentaire.
  //
  // `query.match` est requise par `add_comments` : c'est la seule surface qui
  // rende la cible de texte que `comments.create` accepte. `comments.list` l'est
  // aussi — c'est elle qui PROUVE la pose, un receipt en succès ne prouvant rien
  // (doctrine constante depuis #980).
  {
    name: 'add_comments',
    inputSchema: AddCommentsInputSchema,
    llmDescription: 'Leave a comment anchored on a passage of the document, exactly like a lawyer reviewing a draft. '
      + 'Use it to raise a doubt, ask for a missing piece, or flag a risk WITHOUT touching the text. A comment is not '
      + 'a tracked change: there is nothing to accept or reject, so never tell the lawyer to go accept it.',
    examples: [{
      action: 'add_comments',
      selector: { kind: 'nodeId', nodeId: '4f2a91c0' },
      text: 'Le point de départ de la prescription mériterait d\'être précisé : la pièce n°4 le fixe au 12/03.',
    }],
    capability: 'comments',
    reviewPolicy: 'direct',
    requiredOperations: ['comments.create', 'query.match', 'comments.list'],
    reviewedOperations: [],
    libelle: 'Commenter un passage',
  },
  {
    name: 'reply_to_comment',
    inputSchema: ReplyToCommentInputSchema,
    llmDescription: 'Reply inside an existing comment thread. Take commentId from a canvas_inspect that included the '
      + '"comments" domain — never invent one. A reply has NO selector: it belongs to its thread, not to a passage.',
    examples: [{
      action: 'reply_to_comment',
      commentId: 'commentId lu dans un canvas_inspect frais — ne jamais inventer',
      text: 'La pièce n°4 a été ajoutée au bordereau, le point de départ est désormais explicite.',
    }],
    capability: 'comments',
    reviewPolicy: 'direct',
    requiredOperations: ['comments.create', 'comments.list'],
    reviewedOperations: [],
    libelle: 'Répondre à un commentaire',
  },
  {
    name: 'resolve_comments',
    inputSchema: ResolveCommentsInputSchema,
    llmDescription: 'Mark comment threads as resolved once they have been dealt with. ⚠️ Resolving the root of a '
      + 'thread resolves the WHOLE thread, replies included — this closes a conversation, not a single message. Take '
      + 'the ids from a fresh canvas_inspect including the "comments" domain. Only resolve what has actually been '
      + 'addressed in the document: a resolved comment stops being visible as an open point for the lawyer.',
    examples: [{
      action: 'resolve_comments',
      commentIds: ['commentId lu dans un canvas_inspect frais — ne jamais inventer'],
    }],
    capability: 'comments',
    reviewPolicy: 'direct',
    requiredOperations: ['comments.patch', 'comments.list'],
    reviewedOperations: [],
    libelle: 'Résoudre des commentaires',
  },
  {
    name: 'move_range',
    inputSchema: MoveRangeInputSchema,
    llmDescription: 'Move a block somewhere else in the document, before or after another block. Use it to reorder '
      + 'arguments or put a paragraph back in the right section, instead of deleting and rewriting it — moving keeps '
      + 'the text and its formatting intact. ⚠️ The editor CANNOT move a block while change tracking is on: the '
      + 'action is then refused before touching the document, and the way out is to ask the lawyer whether tracking '
      + 'may be turned off (set_review_mode, once he agrees) — or to delete and rewrite the passage, which IS tracked.',
    examples: [{
      action: 'move_range',
      selector: { kind: 'textSearch', terms: ['Sur le détournement de pouvoir'], match: 'all' },
      destination: { kind: 'textSearch', terms: ['Sur la nullité externe'], match: 'all' },
      placement: 'before',
    }],
    capability: 'document_structure',
    // ⚠️ `direct` et non `tracked`, et `reviewedOperations` VIDE — pour une
    // raison mesurée, pas par commodité. `blocks.move` se déclare
    // `tracked: true` et **refuse** en mode suivi :
    // `CAPABILITY_UNAVAILABLE — "paragraph-move-anchor-content-unsupported"`
    // (2026-08-16, avec témoin positif). Un déplacement n'est donc JAMAIS une
    // révision : il n'a lieu que le suivi coupé. Déclarer `blocks.move` en
    // `reviewedOperations` ne servirait à rien — le filtre par capacités lit le
    // `tracked` DÉCLARÉ, qui ment ici — et laisserait croire à une révision qui
    // ne viendra pas. Le refus préalable vit dans l'exécuteur, sur le régime du
    // document, et nomme la sortie (couper le suivi avec l'accord de l'avocat, ou supprimer/réécrire).
    reviewPolicy: 'direct',
    requiredOperations: ['blocks.move', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Déplacer un passage',
  },
  {
    name: 'redo_changes',
    inputSchema: RedoChangesInputSchema,
    llmDescription: 'Redo the last change that was undone. Only useful right after undo_changes — if nothing was '
      + 'undone, this is refused before touching the document.',
    examples: [{ action: 'redo_changes' }],
    capability: 'undo',
    reviewPolicy: 'direct',
    requiredOperations: ['history.redo', 'history.get', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Rétablir',
  },
  // ⚠️ **L'exemple de ces deux verbes ne doit JAMAIS porter un identifiant
  // plausible** (#994). Il portait `'change-1'` — une valeur courte, régulière,
  // directement imitable — alors qu'aucune surface ne publiait les vrais
  // identifiants au modèle : le seul geste que l'exemple lui enseignait était
  // précisément celui qui échoue à coup sûr. Le gabarit ci-dessous ne peut pas
  // être pris pour un identifiant : il NOMME sa source. (Les vrais sont des
  // chaînes opaques du fournisseur, du genre
  // `tc|main%3A%2Fword%2Fdocument.xml|replacement|00000005%7CwId%3A3|…`.)
  // `trackChanges.list` reste dans `requiredOperations` bien qu'elle ne fasse
  // que LIRE : sans elle, l'exécuteur ne peut pas vérifier que l'identifiant
  // existe avant de décider, et le refus ne pourrait plus dire combien de
  // révisions sont réellement ouvertes.
  {
    name: 'accept_tracked_changes',
    inputSchema: AcceptTrackedChangesInputSchema,
    llmDescription: 'Accept tracked changes in one action. ALL changes: target {kind:"all"}, no ids or listing needed. '
      + 'Partial selection: the same decision in one action with target {kind:"ids"} listing every selected id, copied verbatim from '
      + 'canvas_inspect includeDomains ["trackedChanges"] — ids are opaque, never invented.',
    examples: [
      { action: 'accept_tracked_changes', target: { kind: 'all' } },
      { action: 'accept_tracked_changes', target: { kind: 'ids', ids: ['<id copied verbatim from canvas_inspect trackedChanges[].id>'] } },
    ],
    capability: 'tracked_changes',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['trackChanges.decide', 'trackChanges.list'],
    reviewedOperations: [],
    libelle: 'Accepter des modifications',
  },
  {
    name: 'reject_tracked_changes',
    inputSchema: RejectTrackedChangesInputSchema,
    llmDescription: 'Reject tracked changes (the previous text comes back). Same targets as accept_tracked_changes: '
      + '{kind:"all"} for all; for a partial selection, the same decision in one action with {kind:"ids"}.',
    examples: [
      { action: 'reject_tracked_changes', target: { kind: 'all' } },
      { action: 'reject_tracked_changes', target: { kind: 'ids', ids: ['<id copied verbatim from canvas_inspect trackedChanges[].id>'] } },
    ],
    capability: 'tracked_changes',
    reviewPolicy: 'capability_dependent',
    requiredOperations: ['trackChanges.decide', 'trackChanges.list'],
    reviewedOperations: [],
    libelle: 'Refuser des modifications',
  },
  { name: 'undo_changes', inputSchema: UndoChangesInputSchema, llmDescription: 'Undo the last "steps" changes — up to 100, all the editor keeps. To undo one call: steps = the "writes" of its receipt. Needs the scope of a final canvas_inspect, not a formatOf read.', examples: [{ action: 'undo_changes', steps: 1 }], capability: 'undo', reviewPolicy: 'capability_dependent', requiredOperations: ['history.undo'], reviewedOperations: [], libelle: 'Annuler les dernières modifications' },
  // ── Notes de bas de page — relevé du 2026-08-26 (§9 du vault) ─────────────
  //
  // Les six opérations `footnotes.*` sont `available` sur `superdoc@2.6.0`, et
  // le `tracked` DÉCLARÉ y est démenti une quatrième fois — mais sur `update`
  // SEULEMENT : sous fenêtre `suggesting`, témoin `doc.replace` à 1 révision,
  // `insert` et `remove` en produisent 1 chacun, `update` ZÉRO.
  //
  // `footnotes.list` est déclarée requise sur les deux verbes qui prennent un
  // `note_id` bien qu'elle ne fasse que LIRE : c'est elle qui prouve que
  // l'identifiant existe avant d'écrire, et c'est elle qui PROUVE l'effet à la
  // relecture — un receipt en succès ne prouve rien (doctrine constante depuis
  // #980). Elle est en revanche ABSENTE d'`insert_footnote`, dont l'effet se
  // prouve par le renvoi posé dans le bloc et dont l'identifiant est rendu par
  // l'insertion elle-même : l'y déclarer ferait disparaître le verbe pour une
  // capacité dont son résultat ne dépend pas.
  {
    name: 'insert_footnote',
    inputSchema: InsertFootnoteInputSchema,
    llmDescription: 'Insert a real Word footnote: a superscript reference is placed right after anchor_text inside the '
      + 'resolved block, and the note text goes to the bottom of the page. Use it for a source, a case citation or an '
      + 'aside that would break the argument if kept inline. Several notes may share one block. ⚠️ A block whose text '
      + 'carries a PENDING tracked revision is refused before anything is written — including a block you just '
      + 'rewrote in this same turn. Accept that revision first (accept_tracked_changes, ids from canvas_inspect '
      + 'trackedChanges), then retry the SAME insertion.',
    examples: [{
      action: 'insert_footnote',
      selector: { kind: 'textSearch', terms: ['la prescription est acquise'], match: 'all' },
      anchor_text: 'la prescription est acquise',
      text: 'Cass. civ. 1re, 12 mars 2024, n° 22-19.876.',
    }],
    capability: 'document_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['query.match', 'footnotes.insert', 'blocks.list'],
    reviewedOperations: ['footnotes.insert'],
    libelle: 'Insérer une note de bas de page',
  },
  // ⚠️ `direct`, et `reviewedOperations` VIDE, PARCE QUE LA MESURE LE COMMANDE :
  // `footnotes.update` se déclare `tracked: true` et ne produit AUCUNE révision
  // (contrôlé sur une note commitée, témoin toujours à 1). Le déclarer revu
  // ferait rendre `partial` / `TRACKING_NOT_APPLIED` par `suiviDemandeNonObtenu`
  // à CHAQUE appel réussi — un succès annoncé en demi-échec.
  {
    name: 'edit_footnote',
    inputSchema: EditFootnoteInputSchema,
    llmDescription: 'Rewrite the text of an existing footnote. Take note_id from a canvas_inspect that included the '
      + '"footnotes" domain — never invent one. ⚠️ Rewriting a note produces NO tracked revision even when the lawyer '
      + 'has change tracking on: he gets no readable trace of what you changed. When the wording matters, use '
      + 'delete_footnote then insert_footnote — that pair IS reviewable, at the cost of a renumbered reference.',
    examples: [{
      action: 'edit_footnote',
      note_id: 'noteId lu dans un canvas_inspect frais incluant le domaine footnotes — ne jamais inventer',
      text: 'Cass. civ. 1re, 12 mars 2024, n° 22-19.876, publié au Bulletin.',
    }],
    capability: 'document_structure',
    reviewPolicy: 'direct',
    requiredOperations: ['footnotes.update', 'footnotes.list'],
    reviewedOperations: [],
    libelle: 'Réécrire une note de bas de page',
  },
  {
    name: 'delete_footnote',
    inputSchema: DeleteFootnoteInputSchema,
    llmDescription: 'Remove a footnote and its reference. Take note_id from a canvas_inspect that included the '
      + '"footnotes" domain — never invent one. ⚠️ Under change tracking the removal is a tracked revision, so the '
      + 'note STAYS listed by canvas_inspect until the lawyer accepts it: do not read that as a failure and do not '
      + 'call again.',
    examples: [{
      action: 'delete_footnote',
      note_id: 'noteId lu dans un canvas_inspect frais incluant le domaine footnotes — ne jamais inventer',
    }],
    capability: 'document_structure',
    reviewPolicy: 'tracked',
    requiredOperations: ['footnotes.remove', 'footnotes.list'],
    reviewedOperations: ['footnotes.remove'],
    libelle: 'Supprimer une note de bas de page',
  },
  /**
   * ⚠️ `reviewPolicy: 'direct'` et `reviewedOperations: []` ne sont pas un oubli.
   *
   * Un en-tête ne vit pas dans le corps du document : il est une PARTIE séparée
   * (`word/headerN.xml`) référencée par la section. Le suivi des modifications
   * ne le couvre pas — les dix opérations d'en-tête sont mesurées
   * `tracked: false`. Déclarer `reviewedOperations` non vide ferait rendre
   * `partial` / `TRACKING_NOT_APPLIED` à CHAQUE pose réussie, et le modèle
   * conclurait à un demi-échec sur un geste parfait.
   */
  {
    name: 'set_letterhead',
    inputSchema: SetLetterheadInputSchema,
    llmDescription: 'Place the firm\'s letterhead on page 1, or remove it. Give "name" exactly as list_letterheads '
      + 'spells it (no id exists); omit "name" for the firm\'s default letterhead. ⚠️ The first call is ADDITIVE: if '
      + 'the page already carries a letterhead you get a refusal, NOT an overwrite — the editor cannot tell a logo '
      + 'Hector placed from one that came with a Word file the lawyer imported. Tell the lawyer it would be lost, and '
      + 'only resend with replace_existing once they agree. Use "remove" alone to strip the letterhead. Headers are '
      + 'outside change tracking, so nothing here produces a revision to accept.',
    examples: [
      { action: 'set_letterhead' },
      { action: 'set_letterhead', name: 'nom rendu par list_letterheads — jamais un identifiant' },
      { action: 'set_letterhead', remove: true },
    ],
    capability: 'document_structure',
    reviewPolicy: 'direct',
    requiredOperations: [
      'sections.list',
      'sections.setTitlePage',
      'headerFooters.list',
      'headerFooters.parts.create',
      'headerFooters.refs.set',
      'headerFooters.refs.clear',
      'create.image',
    ],
    reviewedOperations: [],
    libelle: 'Poser ou retirer l\'en-tête du cabinet',
  },
  // ⚠️ `requiredOperations` s'arrête à QUATRE opérations, et c'est une MESURE,
  // ⚠️ `requiredOperations` s'arrête à QUATRE opérations, et c'est une MESURE,
  // pas une économie (2026-09-05, `tests/ws-mocked/mesure-en-tetes-et-pieds.spec.ts`) :
  // écrire par le locator de story `headerFooterSlot` **matérialise lui-même**
  // la partie manquante et pose la référence de section. Vérifié de bout en
  // bout sur une section sans aucun en-tête : `resolve` rendait `none` avant,
  // `explicit` avec un `refId` neuf après le seul `create.paragraph`, et le
  // texte est PEINT. `headerFooters.parts.create` et `headerFooters.refs.set`
  // seraient donc déclarées pour un chemin que l'exécuteur n'emprunte pas.
  //
  // Les trois opérations dépendantes du CAS sont refusées à l'exécution, avec
  // leur sortie nommée, plutôt que déclarées ici — les déclarer ferait
  // disparaître le verbe ENTIER sur un document qui sait pourtant faire
  // l'essentiel : `sections.setTitlePage` (seulement pour `first_page`),
  // `sections.setOddEvenHeadersFooters` (seulement pour `even_pages`) et
  // `blocks.delete` (seulement pour `mode:'replace'`). Même doctrine que
  // `blocks.delete` absent de `write_markdown`.
  //
  // ⚠️ `direct` et `reviewedOperations` VIDE parce que la mesure le commande :
  // sous fenêtre `suggesting`, `create.paragraph` dans une story d'en-tête rend
  // un `trackedChangeRefs` dans sa réponse et produit **ZÉRO** entrée dans
  // `trackChanges.list()`, pendant que le témoin posé dans le CORPS en produit
  // une. Cinquième démenti du `tracked` annoncé. Le déclarer revu ferait rendre
  // `partial` / `TRACKING_NOT_APPLIED` à chaque appel réussi.
  {
    name: 'set_header_footer',
    inputSchema: SetHeaderFooterInputSchema,
    llmDescription: 'Write text into the header or footer of a section — cabinet line, case reference, court name. The three zones are SEPARATE containers, not display filters: type: "primary" '
      + '= pages with no zone of their own (so every page ONLY when the first page is not detached), "first_page" = '
      + 'the first page only (the first-page flag is raised for you), "even_pages" = even pages only. '
      + 'mode "replace" rewrites the zone, page number included; "append" adds after it. '
      + 'Page number: in a paragraph text, {page} is the page number and {pages} the page count ("Page {page} / {pages}"); '
      + 'remove_page_number:true, instead of paragraphs, removes each number\'s WHOLE line; to keep text of that line '
      + '(the firm name), rewrite the zone with paragraphs and no token. '
      + '⚠️ An existing IMAGE (the cabinet letterhead) is NEVER deleted, not even by mode "replace"; the receipt says so in '
      + 'previous_had_image. ⚠️ Writing here produces NO tracked revision even when change tracking is on: tell the lawyer '
      + 'what you put in the header. Read the current state first with canvas_inspect and includeDomains containing "headersFooters".',
    examples: [{
      action: 'set_header_footer',
      target: 'header',
      type: 'primary',
      section_index: 0,
      mode: 'replace',
      paragraphs: [
        { text: 'CONCLUSIONS RÉCAPITULATIVES', alignment: 'center' },
        { text: 'RG n° 24/01234', alignment: 'center' },
      ],
    }],
    capability: 'header_footer',
    reviewPolicy: 'direct',
    requiredOperations: ['sections.list', 'headerFooters.resolve', 'create.paragraph', 'blocks.list'],
    reviewedOperations: [],
    libelle: 'Écrire un en-tête ou un pied de page',
  },
  // `requiredOperations` s'arrête à `sections.list` : `sections.setPageMargins` ne sert qu'aux marges,
  // `sections.setPageSetup` qu'à l'orientation et au format. Les déclarer ferait disparaître le verbe entier sur un
  // document qui sait faire l'un des deux ; chacune est refusée à l'exécution, nommée, quand l'appel en a besoin
  // (même doctrine que `set_header_footer`).
  // `direct` et `reviewedOperations` vide : un réglage de section n'est pas du contenu, il ne laisse aucune révision
  // (mesuré le 2026-10-06, `mesure-socle-page-et-defauts` ; sous suivi activé : `canvas-mise-en-page.spec.ts`).
  {
    name: 'set_page_layout',
    inputSchema: SetPageLayoutInputSchema,
    llmDescription: 'Set the page of the whole document: marginsCm (any of top, bottom, left, right), orientation, paperSize. '
      + 'Written directly, nothing to accept. The receipt re-reads the file: one outcome per setting in pageLayout.settings; '
      + 'to undo, undo_changes with steps = pageLayout.writes.',
    examples: [{ action: 'set_page_layout', marginsCm: { top: 2.5, left: 3 }, orientation: 'landscape' }],
    capability: 'page_layout',
    reviewPolicy: 'direct',
    requiredOperations: ['sections.list'],
    reviewedOperations: [],
    libelle: 'Régler la page',
  },
  // `create.image`, `images.delete` et `images.replaceSource` se déclarent indisponibles et marchent, en direct comme
  // en suivi (mesure : `mesure-images-du-corps.spec.ts`, exceptions dans `OPERATIONS_DECLAREES_A_TORT_INDISPONIBLES`).
  // Elles se déclarent aussi `tracked:false` alors qu'elles laissent chacune une révision : les inscrire dans
  // `reviewedOperations` ferait refuser tout le verbe sous suivi activé (`operationsNonSuivables`, navigateur).
  // `images.setSize`, déclarée suivable, y représente donc les deux verbes d'image : sa présence suffit pour que le
  // reçu rende `partial` quand une écriture sous suivi ne laisse aucune révision. `insert_image` ne l'appelle pas ;
  // il la requiert à ce seul titre, et un éditeur qui sait poser une image sait la redimensionner.
  //
  // `insert_image` précède `edit_image` : l'exemple de la famille, pris au premier verbe, montre `source` et `at`,
  // les deux formes qu'aucun autre verbe n'emploie.
  {
    name: 'insert_image',
    inputSchema: InsertImageInputSchema,
    llmDescription: 'Insert an image in a new paragraph, before or after block at.nodeId. source has one key: '
      + 'documentId (a case file) or attachmentId (a file the lawyer attached), PNG or JPEG, or imageId (copies a body '
      + 'image). width, in pixels, keeps the proportions; default: its own size, at most the text width. To '
      + 'move an image, insert a copy (source imageId), then remove the original with edit_image: the copy is '
      + 'inline, even if the original floated.',
    examples: [{ action: 'insert_image', source: { attachmentId: 'id du fichier joint' }, at: { nodeId: '4f2a91c0', placement: 'after' } }],
    capability: 'images',
    reviewPolicy: 'tracked',
    requiredOperations: ['images.list', 'create.image', 'images.setSize'],
    reviewedOperations: ['images.setSize'],
    libelle: 'Insérer une image',
  },
  {
    name: 'edit_image',
    inputSchema: EditImageInputSchema,
    llmDescription: 'Change a body image, one change per call: remove:true, size in pixels (one side alone keeps the '
      + 'proportions), or source (as in insert_image) to replace its picture: width kept, height follows the new '
      + 'picture. imageId comes from canvas_inspect. After remove, the receipt says whether the emptied paragraph '
      + 'can be deleted.',
    examples: [{ action: 'edit_image', imageId: 'img:301:word_document.xml:rId6', remove: true }],
    capability: 'images',
    reviewPolicy: 'tracked',
    requiredOperations: ['images.list', 'images.delete', 'images.setSize', 'images.replaceSource'],
    reviewedOperations: ['images.setSize'],
    libelle: 'Retirer, redimensionner ou remplacer une image',
  },
  // Le régime de suivi n'est pas une opération du Document API : le verbe règle
  // le scope documentaire d'Hector, qui pose le mode sur l'éditeur. La seule
  // opération qu'il emprunte est `blocks.list`, pour les instantanés d'avant et
  // d'après que porte son reçu.
  // `direct` et `reviewedOperations` vide : régler le suivi n'écrit rien dans
  // l'acte, donc ne produit aucune révision à relire.
  //
  // L'accord de l'avocat est demandé sans bouton de confirmation (décision de
  // la spec #1858), par UNE phrase de la consigne : « tu ne le changes
  // (`set_review_mode`) qu'à sa demande ou sur son accord explicite »
  // (`redactionMode.ts`, paragraphe « Suivi des modifications », garde C9).
  // Cette description ne la redit pas (#1870) ; elle dit ce que le courtier
  // exige avant d'envoyer (`CanvasSessionBroker.buildActionEnvelope` : la portée
  // d'une lecture finale, que seul un document non vide arme).
  {
    name: 'set_review_mode',
    inputSchema: SetReviewModeInputSchema,
    llmDescription: 'Turn change tracking on or off: the same switch as the toolbar button. It has no target but '
      + 'still needs the scope armed by a final canvas_inspect, so an empty document refuses it.',
    examples: [{ action: 'set_review_mode', enabled: true }],
    capability: 'tracked_changes',
    reviewPolicy: 'direct',
    requiredOperations: ['blocks.list'],
    reviewedOperations: [],
    libelle: 'Activer ou désactiver le suivi des modifications',
  },
] as const satisfies readonly CanvasActionDefinition<CanvasActionSchema>[];

export const CANVAS_ACTION_DEFINITIONS = CANVAS_ACTION_DEFINITION_TUPLE;
export const CANVAS_ACTION_NAMES = CANVAS_ACTION_DEFINITION_TUPLE.map((definition) => definition.name) as [
  CanvasActionName,
  ...CanvasActionName[],
];
export const CANVAS_ACTION_CATALOG: Readonly<Record<CanvasActionName, (typeof CANVAS_ACTION_DEFINITION_TUPLE)[number]>> = Object.fromEntries(
  CANVAS_ACTION_DEFINITION_TUPLE.map((definition) => [definition.name, definition]),
) as Readonly<Record<CanvasActionName, (typeof CANVAS_ACTION_DEFINITION_TUPLE)[number]>>;
